import type { Translations } from './ar';

export const en: Translations = {
  fulfillment: {
    deliveryOnly: 'This action is available for delivery orders only.',
    pickupOnly: 'This action is available for pickup orders only.',
    busy: 'Order data is changing. Try again, then review the details.',
    amountOutOfRange: 'The amount exceeds the supported range',
    clientUpdateRequired: 'Refresh the page to continue confirming your order.',
    pickupStockNotice: 'Stock is deducted when you confirm the order.',
    pickupNext: 'Collect from the place shown. Contact the store for details.',
    collected: 'Collected',
    legacySubtotal: 'Merchandise subtotal — delivery fee was not recorded',
    quoteFailed: 'Could not calculate the total. Try again.',
    quoteLoading: 'Calculating total',
    loadFailed: 'Could not load fulfillment options. Try again.',
    optionsChanged: 'Options changed. Review the details and total, then confirm again.',
    unavailable: 'Delivery and pickup are currently unavailable',
    chooseMethod: 'Choose delivery or self-pickup',
    pickupPlace: 'Pickup place',
    freePickup: 'Free self-pickup',
    freeDelivery: 'Free delivery',
    deliveryFee: 'Delivery fee',
    pickup: 'Self-pickup',
    delivery: 'Delivery',
    title: 'Fulfillment method',
  },
  common: {
    save: 'Save',
    cancel: 'Cancel',
    delete: 'Delete',
    loading: 'Loading\u2026',
    error: 'An error occurred',
    retry: 'Retry',
    search: 'Search',
    call: 'Call',
    actions: {
      close: 'Close',
    },
    phoneCountry: {
      IL: 'Israel',
      PS: 'Palestine',
      JO: 'Jordan',
      SY: 'Syria',
      LB: 'Lebanon',
      EG: 'Egypt',
      TR: 'Turkey',
      US: 'United States',
      international: 'International number',
    },
    languageNames: {
      ar: 'Arabic',
      he: 'Hebrew',
      en: 'English',
    },
    errors: {
      permissionDenied: 'You do not have permission to do that.',
      networkFailed: 'Connection failed. Check your network and try again.',
      storeNotFound: 'This store no longer exists. Refresh the page.',
      unknown: 'Something went wrong. Try again.',
    },
  },
  auth: {
    signup: {
      title: 'Create your account',
      subtitle: 'Start your store today',
      emailLabel: 'Email',
      passwordLabel: 'Password',
      passwordConfirmLabel: 'Confirm password',
      submit: 'Create account',
      submitting: 'Creating account\u2026',
      haveAccount: 'Already have an account?',
      signInLink: 'Sign in',
      showPassword: 'Show password',
      hidePassword: 'Hide password',
      errors: {
        emailRequired: 'Email is required',
        emailInvalid: 'Invalid email address',
        passwordTooShort: 'Password must be at least 8 characters',
        passwordConfirmRequired: 'Password confirmation is required',
        passwordMismatch: 'Passwords do not match',
        emailAlreadyExists: 'This email is already registered',
        rateLimited: 'Too many attempts. Please try again in a moment.',
        networkFailed: 'Connection failed. Check your network.',
        accountCreatedSignInFailed: 'Your account was created. Please try again in a moment.',
        unknown: 'Something went wrong. Try again.',
        noNegativeInventory: 'admin.barcode.errors.noNegativeInventory',
      },
    },
    callback: {
      title: 'Verifying\u2026',
    },
    phoneOtp: {
      phoneLabel: 'Phone number',
      phoneHint: 'Enter the number with country code or choose a country.',
      codeLabel: 'Verification code',
      codeHint: 'Enter the 6-digit code.',
      requestCode: 'Send code',
      requestingCode: 'Sending...',
      verifyCode: 'Verify code',
      verifyingCode: 'Verifying...',
      retryHandoff: 'Try again',
      resendCode: 'Send a new code',
      changePhone: 'Change phone',
      sentMessage: 'We sent a verification code to your phone.',
      smsOnly: 'We will send the code by SMS.',
      existingEmailFallback: "Don't have a verified phone?",
      useEmailFallback: 'Use email and password',
      usePhoneOtp: 'Use phone code',
      errors: {
        phoneRequired: 'Phone number is required',
        phoneInvalid: 'Phone number is not valid',
        codeRequired: 'Verification code is required',
        codeInvalid: 'Enter a 6-digit code',
        invalidCode: 'The code is incorrect or expired',
        customerAccountMissing: 'No customer account exists for this phone. Create one first.',
        adminAccountMissing: 'No merchant account exists for this phone.',
        rateLimited: 'Too many attempts. Try again in a moment.',
        networkFailed: 'Connection failed. Check your network.',
        cartHandoffFailed: 'You are signed in, but we could not move your cart. Try again.',
        profileEnsureFailed:
          'Your phone was verified, but we could not prepare your customer account. Try again.',
        unknown: 'Something went wrong. Try again.',
      },
    },
  },
  catalog: {
    products: 'Products',
    outOfStock: 'Out of stock',
  },
  checkout: {
    cart: 'Cart',
    total: 'Total',
  },
  customer: {
    phoneCountryLabel: 'Country code',
    phoneCountry: {
      IL: 'Israel',
      PS: 'Palestine',
      JO: 'Jordan',
      SY: 'Syria',
      LB: 'Lebanon',
      EG: 'Egypt',
      TR: 'Turkey',
      US: 'United States',
    },
    signup: {
      title: 'Continue with phone',
      subtitle: 'Use one phone verification code to continue shopping.',
      fullNameLabel: 'Full name',
      fullNameHint: 'How you\u2019d like the seller to address you',
      emailLabel: 'Email',
      emailHint: 'This email will be used to sign in',
      passwordLabel: 'Password',
      passwordHint: 'At least 8 characters',
      passwordConfirmLabel: 'Confirm password',
      phoneLabel: 'Phone number (optional)',
      phoneHint: 'Helps the seller reach you about your order',
      phoneCountryLabel: 'Country code',
      phoneCountry: {
        IL: 'Israel',
        PS: 'Palestine',
        JO: 'Jordan',
        SY: 'Syria',
        LB: 'Lebanon',
        EG: 'Egypt',
        TR: 'Turkey',
        US: 'United States',
      },
      showPassword: 'Show password',
      hidePassword: 'Hide password',
      submit: 'Create account',
      submitting: 'Creating account\u2026',
      haveAccount: 'Already have an account?',
      signInLink: 'Sign in',
      successToast: 'Account created successfully!',
      errors: {
        fullNameRequired: 'Full name is required',
        fullNameTooShort: 'Name is too short',
        fullNameTooLong: 'Name is too long',
        emailRequired: 'Email is required',
        emailInvalid: 'Invalid email address',
        passwordTooShort: 'Password must be at least 8 characters',
        passwordConfirmRequired: 'Password confirmation is required',
        passwordMismatch: 'Passwords do not match',
        phoneInvalid: 'Phone number is not valid for the selected country',
        emailAlreadyExists:
          "This email is already registered. If you're a merchant, sign in with the same account to shop.",
        rateLimited: 'Too many attempts. Please try again in a moment.',
        networkFailed: 'Connection failed. Check your network.',
        accountCreatedSignInFailed: 'Your account was created. Please try to sign in shortly.',
        unknown: 'Something went wrong. Try again.',
      },
    },
    header: {
      signUpLink: 'Continue with phone',
      signInLink: 'Sign in',
      avatarMenuLabel: 'Account menu',
      signOut: 'Sign out',
      myProfile: 'My profile',
      myAddresses: 'My addresses',
      myOrders: 'My orders',
    },
    login: {
      title: 'Continue with phone',
      subtitle: 'New and returning shoppers use the same phone code.',
      emailLabel: 'Email',
      emailHint: 'The address you used when creating your account',
      passwordLabel: 'Password',
      showPassword: 'Show password',
      hidePassword: 'Hide password',
      submit: 'Sign in',
      submitting: 'Signing in\u2026',
      forgotPasswordLink: 'Forgot password?',
      noAccountYet: 'Don\u2019t have an account?',
      signUpLink: 'Sign up',
      phoneOnlyHint: 'No email or password is needed.',
      errors: {
        emailRequired: 'Email is required',
        emailInvalid: 'Invalid email address',
        passwordRequired: 'Password is required',
        invalidCredentials: 'Invalid credentials, please try again',
        rateLimited: 'Too many attempts. Please try again in a few minutes.',
        networkFailed: 'Connection failed. Check your network.',
        unknown: 'Something went wrong. Try again.',
      },
    },
    passwordReset: {
      request: {
        title: 'Reset your password',
        subtitle: 'We\u2019ll send a reset link to your email.',
        emailLabel: 'Email',
        submit: 'Send reset link',
        submitting: 'Sending\u2026',
        successTitle: 'Check your email',
        successMessage:
          'If an account with this email exists, you\u2019ll receive a message with a reset link within a few minutes.',
        backToSignIn: 'Back to sign in',
        errors: {
          emailRequired: 'Email is required',
          emailInvalid: 'Invalid email address',
          rateLimited: 'Too many attempts. Please try again in a few minutes.',
          networkFailed: 'Connection failed. Check your network.',
          unknown: 'Something went wrong. Try again.',
        },
      },
      confirm: {
        title: 'Choose a new password',
        subtitle: 'Enter a new password to complete the reset.',
        passwordLabel: 'New password',
        passwordHint: 'At least 8 characters',
        passwordConfirmLabel: 'Confirm password',
        showPassword: 'Show password',
        hidePassword: 'Hide password',
        submit: 'Save new password',
        submitting: 'Saving\u2026',
        sessionMissingTitle: 'Reset link expired',
        sessionMissingMessage: 'Please request a new link.',
        sessionMissingCta: 'Request a new link',
        successToast: 'Password changed. Welcome back!',
        errors: {
          passwordTooShort: 'Password must be at least 8 characters',
          passwordMismatch: 'Passwords do not match',
          rateLimited: 'Too many attempts. Please try again in a few minutes.',
          networkFailed: 'Connection failed. Check your network.',
          unknown: 'Something went wrong. Try again.',
        },
      },
    },
    profilePlaceholder: {
      title: 'Profile page coming soon',
      subtitle: 'We\u2019re still building this page. Check back soon.',
      cta: 'Back to the store',
      ariaLabel: 'Profile placeholder page',
    },
    account: {
      pageTitle: 'Account',
      ariaLabel: 'Account page',
      subtitle: 'Manage your profile, saved delivery addresses, and orders.',
      navLabel: 'Account sections',
      items: {
        profile: {
          title: 'Profile',
          description: 'Update your name and preferred language.',
        },
        addresses: {
          title: 'Saved addresses',
          description: 'Add, edit, or choose your default delivery address.',
        },
        orders: {
          title: 'Orders',
          description: 'Review your recent orders and their status.',
        },
      },
    },
    profile: {
      pageTitle: 'Profile',
      ariaLabel: 'Profile page',
      editButton: 'Edit profile',
      addressesButton: 'Manage saved addresses',
      saveButton: 'Save changes',
      saving: 'Saving...',
      cancelButton: 'Cancel',
      nameLabel: 'Name',
      emailLabel: 'Email',
      emailReadOnly: 'Email cannot be changed at this time',
      phoneLabel: 'Phone number',
      phoneOptional: 'Optional',
      phoneReadOnly: 'Phone is tied to sign-in and cannot be changed here.',
      phonePlaceholder: '+972501234567',
      localeLabel: 'Preferred language',
      localeAr: 'Arabic',
      localeHe: 'Hebrew',
      localeEn: 'English',
      successToast: 'Changes saved successfully',
      signOut: 'Sign out',
      signingOut: 'Signing out...',
      deleteAccount: {
        title: 'Delete account',
        warning:
          'This action cannot be undone. Your account will be permanently deleted. Any active orders will remain visible to the merchant with anonymized info.',
        confirmPrompt: 'Type "{{word}}" to confirm',
        confirmWord: 'DELETE',
        confirmButton: 'Delete account permanently',
        deleting: 'Deleting account...',
      },
      errors: {
        nameRequired: 'Name is required',
        nameTooLong: 'Name is too long (max 100 characters)',
        phoneInvalid: 'Invalid phone number',
        networkFailed: 'Connection failed. Check your network.',
        rateLimited: 'Too many attempts. Try again in a few minutes.',
        unknown: 'Something went wrong. Try again.',
        deleteFailed: 'Account deletion failed. Try again.',
      },
    },
    addresses: {
      pageTitle: 'My addresses',
      ariaLabel: 'Addresses page',
      addNewCta: 'Add new address',
      limitReached: "You've reached the limit (20 addresses). Delete an old one to add a new one.",
      defaultBadge: 'Default',
      makeDefault: 'Make default',
      edit: 'Edit',
      delete: 'Delete',
      actionsMenuLabel: 'Address actions',
      empty: {
        title: 'No saved addresses yet',
        body: 'Add one to speed up checkout.',
        cta: 'Add your first address',
      },
      form: {
        addTitle: 'Add new address',
        editTitle: 'Edit address',
        labelLabel: 'Label',
        labelPlaceholder: 'e.g. Home, Work',
        labelOptional: 'Optional',
        recipientNameLabel: 'Recipient name',
        addressLine1Label: 'Address',
        addressLine2Label: 'Address line 2',
        cityLabel: 'City',
        localitySearchPlaceholder: 'Search city or village',
        localitySearchPrompt: 'Start typing to find your city or village.',
        localityNoResults: 'No matching city or village',
        regionLabel: 'Region',
        regionFallbackLabel: 'State / Province',
        postalCodeLabel: 'Postal code',
        countryLabel: 'Country',
        phoneLabel: 'Phone number',
        phoneOptional: 'Optional \u2014 the merchant may use this to coordinate delivery.',
        isDefaultLabel: 'Make this my default address',
        saveNew: 'Save address',
        saveEdit: 'Save changes',
        cancel: 'Cancel',
        saving: 'Saving\u2026',
      },
      districtIL: {
        northern: 'Northern District',
        haifa: 'Haifa District',
        central: 'Central District',
        tel_aviv: 'Tel Aviv District',
        jerusalem: 'Jerusalem District',
        southern: 'Southern District',
      },
      successToast: {
        created: 'Address saved',
        updated: 'Address updated',
        deleted: 'Address deleted',
        defaultSet: 'Default address updated',
      },
      deleteConfirm: {
        title: 'Delete this address?',
        body: 'This address will no longer appear in checkout options. Past orders are not affected.',
        confirm: 'Delete',
        confirmAlias: 'admin.products.barcode.confirm',
        deleting: 'Deleting\u2026',
      },
      errors: {
        labelTooLong: 'Label is too long',
        recipientNameRequired: 'Recipient name is required',
        recipientNameTooLong: 'Recipient name is too long',
        addressLine1Required: 'Address is required',
        addressLine1TooLong: 'Address is too long',
        addressLine2TooLong: 'Address line 2 is too long',
        cityRequired: 'City is required',
        cityTooLong: 'City name is too long',
        regionRequired: 'Region is required',
        regionTooLong: 'Region is too long',
        postalCodeRequired: 'Postal code is required',
        postalCodeInvalidIL: 'Israeli postal code must be 5 or 7 digits',
        postalCodeInvalid: 'Invalid postal code',
        countryRequired: 'Country is required',
        phoneInvalid: 'Invalid phone number',
        networkFailed: 'Connection failed. Check your network.',
        rateLimited: 'Too many attempts. Try again in a few minutes.',
        notFound: 'Address not found.',
        deleteFailed: 'Could not delete the address.',
        setDefaultFailed: 'Could not set the default address.',
        unknown: 'Something went wrong. Try again.',
      },
    },
    // DEPRECATED: F-019 replaced the orders placeholder with the real order
    // history page. These keys are retained for translator reference only.
    // No runtime code references them. Safe to remove in a future cleanup.
    ordersPlaceholder: {
      title: 'Orders coming soon',
      subtitle: 'We\u2019re still building this page. Check back soon.',
      cta: 'Back to the store',
      ariaLabel: 'Orders placeholder page',
    },
    callback: {
      title: 'Verifying\u2026',
      loading: 'One moment while we verify your email.',
    },
    authGate: {
      title: 'Verify your phone to contact the seller',
      subtitle: 'To contact {{storeName}} about {{productName}}, continue with phone verification.',
      signInCta: 'Continue with phone',
      signUpCta: 'Continue with phone',
      closeLabel: 'Close',
      primedBanner: {
        message:
          'You\u2019re signed in \u2014 tap \u201cContact seller\u201d to message {{storeName}}',
        dismissLabel: 'Dismiss',
      },
    },
  },
  cart: {
    errors: {
      insufficientStock:
        'Sorry, someone else just grabbed the last ones \u2014 only {{available}} left now',
      productNotFound: 'Product not found',
      notAuthenticated: 'Please sign in to continue',
      permissionDenied: 'You don\u2019t have permission to make this change',
      networkFailed: 'Network request failed. Please try again.',
      unknown: 'Something went wrong. Please try again.',
      storeUnpublished: 'This store is no longer accepting orders',
      cartItemNotFound: 'That item is no longer in your cart. We\u2019re refreshing it now.',
      invalidQuantity: 'Couldn\u2019t update the quantity. Please try again.',
      variantUnavailable: 'This option is no longer available',
      insufficientVariantStock: 'Not enough stock for this option',
    },
  },
  reviews: {
    verifiedCustomer: 'Verified customer',
    verifiedPurchase: 'Verified purchase',
    merchantResponse: 'Merchant response',
    ratingLabel: '{{rating}} out of 5',
    reviewCount: '{{count}} reviews',
    noReviewsYet: 'No reviews yet',
    errors: {
      notEligible: 'This product is not eligible for review',
      alreadyReviewed: 'This product was already reviewed',
      permissionDenied: 'You do not have permission to submit this review',
      productUnavailable: 'This product cannot be reviewed right now',
      ratingInvalid: 'Choose a rating from 1 to 5',
      textTooLong: 'Review text must be 1000 characters or fewer',
      whitespaceOnly: 'Write clear text or leave the field empty',
    },
  },
  admin: {
    newsletterSubscribers: {
      consentVersion: 'Consent version',
      source: 'Subscription source',
      exportError: 'Displayed subscribers could not be exported. Try again.',
      recentLimit: 'Most recent, up to {{count}} subscribers',
      title: 'Subscribers',
      description: 'View recent contacts who consented to your store updates.',
      limit: 'Recent contacts to display',
      limitOption: 'Latest {{count}} contacts',
      export: 'Export displayed subscribers',
      empty:
        'No subscribers yet. Enable collection and publish your newsletter content so visitors can subscribe.',
      loadError: 'Subscribers could not be loaded.',
      forbidden: 'This page is available only to store owners and admins.',
      contact: 'Contact',
      method: 'Contact method',
      status: 'Status',
      subscribedAt: 'Subscribed at',
      lastSubmittedAt: 'Last submitted at',
      active: 'Active',
      suppressed: 'Suppressed',
      suppress: 'Suppress subscription',
      suppressTitle: 'Suppress this contact?',
      suppressBody: 'The contact remains suppressed even if another subscription is submitted.',
      suppressSuccess: 'Subscription suppressed.',
      suppressError: 'Subscription could not be suppressed. Try again.',
      email: 'Email',
      phone: 'Phone',
    },
    websiteTemplates: {
      newsletter: {
        enabled: 'Enable subscriptions',
        help: 'Subscriptions are disabled by default. Publish changes to enable them on your store.',
        contactMethods: 'Accepted contact methods',
        email: 'Email',
        phone: 'Phone',
        consentVersion: 'The current newsletter consent version applies.',
        invalid: 'Choose at least one contact method.',
      },
      runtime: {
        reload: 'Reload editor',
        unsavedWarning: 'Your unsaved changes will be lost if you reload.',
        reloadFailed: 'The editor still needs newer code. Try again later.',
      },
      fashion: {
        campaignPositionHint:
          'This campaign stays linked to its original category. It is hidden when that category is unavailable or empty.',
      },
      richText: {
        hint: 'Leave a blank line between paragraphs. Start a line with - for a bullet list.',
        lossy: 'This text has formatting that will be simplified if you edit it.',
      },
      gallery: {
        credits: 'Template photo credits',
        title: 'Storefront',
        heading: 'Choose a template to create your online store',
        subheading:
          'Pick a design that fits your business. You can easily customize the content after selecting.',
        filterLabel: 'Filter templates by category',
        filter: {
          all: 'All templates',
        },
        badge: {
          live: 'Published',
          draftSaved: 'Draft saved',
          custom: 'Made for your store',
        },
        swatchesLabel: 'Template colors',
        action: {
          previewDemo: 'Preview demo',
          use: 'Use template',
          edit: 'Edit',
          opening: 'Opening…',
          editNamed: 'Edit: {{name}}',
          previewDemoNamed: 'Preview demo: {{name}}',
          useTemplateNamed: 'Use template: {{name}}',
        },
        loadMore: 'Load more templates',
        empty: 'No templates in this category yet.',
        loadError: "We couldn't load templates.",
        designUnavailable: 'This design is not available. Choose a template.',
        notStarted: 'Choose "Use template" to start this design.',
      },
      category: {
        jewelry: 'Jewelry',
        fashion: 'Fashion',
        beauty: 'Beauty',
        electronics: 'Electronics',
        homeLiving: 'Home & Living',
        food: 'Food',
        other: 'Other',
      },
      common: {
        retry: 'Try again',
        cancel: 'Cancel',
        close: 'Close',
      },
      catalog: {
        'luxury-jewelry-v1': {
          title: 'Luxury Jewelry',
          description:
            'An editorial navy and gold jewelry design with a consistent shopping experience.',
        },
        'modern-beauty-v1': {
          title: 'Modern Beauty',
          description: 'A spacious neutral design for beauty products.',
        },
        'editorial-fashion-v1': {
          title: 'Editorial Fashion',
          description: 'Image-led collections with a clean monochrome style.',
        },
        'jewelry-atelier-v1': {
          title: 'Jewelry',
          description: 'Elegant and premium design for jewelry and accessories.',
        },
        'apparel-stride-v1': {
          title: 'Fashion',
          description: 'A bold design for clothing and shoes that puts sizes first.',
        },
        legacy: {
          title: 'Your original design',
          description: 'The design your store had before templates.',
        },
      },
      preview: {
        title: 'Preview: {{name}}',
        readOnly: 'Preview only. Your live store does not change.',
        useThis: 'Use this template',
      },
      stage: {
        applying: 'Applying…',
        title: 'Use {{name}}?',
        carriedOver: 'Items carried over: {{count}}',
        notCarriedBody:
          "These items have no matching place in the new template, so they won't appear in it.",
        confirm: 'Use template',
        success: 'Template added to your draft.',
        originalDesignNote:
          'Your original design stays in your template list, so you can switch back to it.',
      },
      useTemplate: {
        noPublish: 'This creates a draft. Nothing is published until you click Publish.',
      },
      editor: {
        back: 'Back',
        status: {
          live: 'Published',
          draft: 'Draft',
          unpublishedChanges: 'Unpublished changes',
        },
        changeTemplate: 'Change template',
        editMode: 'Edit mode',
        on: 'On',
        off: 'Off',
        publish: 'Publish',
        publishChanges: 'Publish changes',
        allPublished: 'All changes published',
        publishing: 'Publishing…',
        published: 'Your changes are live.',
        noChanges: 'No unpublished changes.',
        publishedStoreHint: 'Design published. Your site goes live after you publish your store.',
        unpublished: 'You have unpublished changes.',
        device: {
          desktop: 'Desktop view',
          mobile: 'Mobile view',
        },
        reload: 'Refresh',
        openLive: 'View live store',
        page: {
          home: 'Home',
          catalog: 'Products list',
          product: 'Product page',
          cart: 'Cart',
          checkout: 'Checkout',
          order: 'Order confirmation',
          storeInfo: 'Store info',
        },
        editHint: 'Select a pencil to edit that item.',
        slotMarker: 'Edit {{label}}',
        storeLanguage: 'Store language',
        legacyNotice: 'Your store was converted to a fixed design. Some parts are now locked.',
        legacyReport: 'View conversion details',
        unmappable:
          "Your current design couldn't be converted automatically. Your live store is unchanged. Choose a template to continue editing.",
        templateThumbnailAlt: '{{name}} template',
        more: 'More options',
        updateTemplate: 'Update template',
        updateTemplateHint:
          'A newer version of this design is available. Your content is kept where it fits.',
        updating: 'Updating…',
        updateTemplateDone: 'Template updated',
        updateTemplateAllKept: 'All your content was kept.',
        save: {
          notSaved: 'Not saved — fix the highlighted field',
          saving: 'Saving…',
          saved: 'All changes saved',
          savedShort: 'Saved',
          failed: "Couldn't save changes",
          retry: 'Retry',
          conflict: 'This was also changed in another window.',
          conflictShort: 'Changed in another window',
          conflictHelp:
            "Keeping your version loads the latest draft and puts your text from this window on top, replacing the other window's changes to the same items.",
          reload: 'Keep my version',
          rejected: "Couldn't save one item",
        },
      },
      report: {
        title: 'Conversion details',
        mapped: 'Editable items: {{count}}',
        frozen: 'Locked items: {{count}}',
        sourceLanguage: 'Text kept from: {{language}}',
      },
      leave: {
        title: "Your latest changes aren't saved",
        body: 'If you leave now, they will be lost.',
        saving: 'Saving your changes before you leave…',
        leave: 'Leave without saving',
        retry: 'Try again and leave',
        keepMine: 'Keep my version and leave',
        stay: 'Stay',
      },
      dialog: {
        titleEdit: 'Edit {{label}}',
        useDefault: 'Use template text',
        leaveEmpty: 'Leave empty',
        textHelp: 'Type exactly what shoppers should see, in any language.',
        charCount: '{{count}} / {{max}}',
        required: 'Required before publishing.',
        conflictTitle: 'This draft changed elsewhere',
        conflictBody:
          'A newer version was saved in another window. Your choice here is kept: reload to apply it to the newer version.',
        reloadDraft: 'Reload draft',
        saveFailed: "Couldn't save changes. Try again.",
        refreshFailed:
          'Your changes were saved, but the updated draft could not be loaded. Reload the draft to continue.',
        discardTitle: 'Discard unsaved changes?',
        discardBody: "Your changes in this window haven't been saved.",
        keepEditing: 'Keep editing',
        discard: 'Discard changes',
        done: 'Done',
      },
      link: {
        shop: 'Shop all',
        sales: 'Sales',
        choose: 'Choose…',
        method: {
          whatsapp: 'WhatsApp',
          phone: 'Phone',
          email: 'Email',
        },
        label: 'Link to',
        none: 'No link',
        category: 'A category',
        product: 'A product',
        collection: 'A collection',
        storePage: 'A store page',
        contact: 'Contact',
      },
      ref: {
        noOptions: 'No items to choose yet. Add them in your catalog first.',
        chooseCategory: 'Choose category',
        chooseProduct: 'Choose product',
        chooseCollection: 'Choose collection',
        auto: 'Choose automatically',
      },
      media: {
        posterFailed:
          "We couldn't make a cover image from this video. Upload a cover image to continue.",
        emptyPreview: 'No media yet',
        upload: 'Upload file',
        limits: {
          image: 'Images: JPG, PNG or WebP, up to 5 MB.',
          gif: 'GIF: up to 8 MB.',
          video: 'Video: MP4 or WebM, up to 15 seconds and 20 MB.',
        },
        poster: 'Cover image',
        posterHelp: 'Shown before the video plays and when motion is reduced.',
        altRequired: 'Add a short description before this image is saved',
        alt: 'Description for screen readers',
        remove: 'Remove current media',
        uploading: 'Uploading…',
        checking: 'Checking file…',
        error: {
          type: "This file type isn't supported here.",
          mov: "MOV files aren't supported. Export as MP4 (H.264) and upload again.",
          size: 'The file is too large.',
          dimensions: 'The image or video size is outside the allowed range.',
          duration: 'The video is longer than 15 seconds.',
          codec: "This video format isn't supported. Use MP4 (H.264) or WebM.",
          metadata: "This video contains metadata we can't remove. Export it again as MP4.",
          posterRequired: 'Add a cover image for this animation or video.',
          generic: 'The upload failed. Try again.',
          corrupt: "This file is damaged or can't be opened.",
        },
        current: 'Current media',
        uploadImageOrVideo: 'Upload image or video',
        uploadImage: 'Upload image',
        dropHint: 'or drag a file here',
        help: {
          imageOrVideo: 'Upload a high-quality image or a short looping video.',
          image: 'Upload a high-quality image.',
        },
        progress: 'Uploading… {{percent}}%',
        posterAuto: 'We made a cover image from your video. You can replace it.',
        replacePoster: 'Replace cover image',
        useTemplateImage: 'Use template image',
        framing: {
          title: 'Position and zoom',
          desktop: 'Desktop',
          mobile: 'Mobile',
          zoom: 'Zoom',
          zoomIn: 'Zoom in',
          zoomOut: 'Zoom out',
          reset: 'Reset',
          apply: 'Apply',
          dragHint: 'Drag to reposition. Use arrow keys to nudge.',
          position: 'Position in the frame',
          positionStatus: 'Horizontal {{x}}%, vertical {{y}}%, zoom {{zoom}}×',
          previewNote: 'On your store this frame can change a little with screen size and text.',
          defaultHint: 'Upload your own image to adjust its position',
          unappliedTitle: "Your new position isn't applied",
          unappliedBody:
            'Select Apply to keep the position and zoom you changed, or leave without them.',
          leaveUnapplied: 'Leave without applying',
        },
      },
      locale: {
        title: 'Store language',
        help: 'Buttons, cart, checkout and messages on your store use this language. Your own texts stay exactly as you wrote them.',
        independent: "This doesn't change your dashboard language.",
        ar: 'العربية',
        he: 'עברית',
        en: 'English',
        save: 'Save',
        saving: 'Saving…',
      },
      readiness: {
        missingMerchantValue: '{{label}} needs your text.',
        missingRequired: '{{label}} is required.',
        invalidMedia: '{{label}}: the file is no longer available.',
        invalidReference: '{{label}}: the linked item is no longer available.',
        missingLogo: 'Upload a store logo before publishing.',
        goTo: 'Go to item',
      },
      publish: {
        titleDesign: 'Publish {{name}}?',
        titleChanges: 'Publish changes to {{name}}?',
        titleOriginal: 'Publish this design?',
        bodyChanges: 'Shoppers will see the latest saved changes to {{name}}.',
        bodyFirst: 'Your store has no live design yet. Shoppers will see {{name}}.',
        bodyFirstStoreOffline:
          'Your store has no live design yet. Shoppers will see {{name}} after you publish your store.',
        bodyReplace:
          'Shoppers will see {{name}} instead of {{live}}. Your {{live}} draft stays saved.',
        bodyReplaceUnknown: 'Shoppers will see {{name}} instead of the design live now.',
        bodyReplaceOriginal:
          'This replaces the design your store shows now. Shoppers will see this design.',
        stale: 'Your storefront changed in another session. Refresh and try again.',
        title: 'Publish storefront changes?',
        body: 'Your saved storefront changes will become visible to customers.',
        confirm: 'Publish',
        failed: 'Publishing failed. Nothing changed on your live store.',
        checking: 'Checking your storefront…',
        fixFirst: 'Fix these items before publishing:',
        successTitle: 'Storefront published',
        viewLive: 'View live store',
        goToSettings: 'Go to store settings',
        goToStorePublish: 'Go to store publishing',
        warningsTitle: 'Things you may want to fix',
        warningsIntro: "You can publish now. These are worth fixing but don't block publishing.",
        warningsToggle: 'Show details',
        warningsToggleHide: 'Hide details',
        publishAnyway: 'Publish anyway',
        flushFirst: "Your latest changes couldn't be saved yet. Try again.",
        adsNotice:
          "Your active ads only appear on your current store page. This design doesn't show them. Nothing is deleted.",
      },
      issue: {
        missingStoreLogo: {
          warning: 'Add a store logo in Settings',
        },
        invalidReference: {
          warning: 'A link points to an item that is no longer available',
          warningNamed: '{{label}}: the link points to an item that is no longer available',
        },
        missingMerchantValue: {
          warning: '{{label}} has no text yet',
        },
      },
      errors: {
        builderRetired: 'The old design editor is no longer available. Use the website editor.',
        slotRetired: 'This item is managed from its own page.',
        templateUnavailable: 'This template is no longer available.',
        invalidValue: "This value can't be saved. Check it and try again.",
        unsafeText: 'Remove hidden direction or control characters and try again.',
        tooLong: 'The text is too long.',
        rateLimited: 'Too many saves. Wait a moment and try again.',
        generic: 'Something went wrong. Try again.',
      },
      slot: {
        mission: {
          body: 'Store mission text',
          ctaLabel: 'Mission link text',
          ctaLink: 'Mission link destination',
        },
        community: {
          heading: 'Community heading',
          media: 'Community image or video',
          ctaLabel: 'Community link text',
          ctaLink: 'Community link destination',
        },
        newsletter: {
          heading: 'Newsletter heading',
          body: 'Newsletter text',
          collection: 'Subscription settings',
        },

        announcement: {
          text: 'Announcement',
        },
        brand: {
          tagline: 'Tagline',
        },
        hero: {
          heading: 'Main heading',
          subheading: 'Subheading',
          ctaLabel: 'Button text',
          ctaLink: 'Button link',
          media: 'Main image or video',
        },
        category: {
          tile: 'Category tile',
          tileImage: 'Category tile image',
        },
        section: {
          heading: 'Section heading',
          body: 'Section text',
        },
        feature: {
          title: 'Highlight title',
          body: 'Highlight text',
        },
        promo: {
          heading: 'Banner heading',
          body: 'Banner text',
          media: 'Banner image or video',
          ctaLabel: 'Banner button text',
          ctaLink: 'Banner button link',
        },
        about: {
          heading: 'About heading',
          body: 'About your store',
          media: 'About image',
        },
        policy: {
          delivery: 'Delivery policy',
          returns: 'Returns policy',
        },
        contact: {
          note: 'Contact note',
        },
        product: {
          careNote: 'Care instructions',
          sizeGuide: 'Size guide',
        },
        cart: {
          note: 'Cart note',
        },
        checkout: {
          note: 'Checkout note',
        },
        confirmation: {
          message: 'Thank-you message',
        },
        footer: {
          note: 'Footer text',
        },
        legacy: {
          text: 'Text',
          media: 'Image',
        },
        ordinal: '{{label}} {{n}}',
      },
      frame: {
        viewport: 'Preview size',
        toolbar: 'Storefront controls',
        back: 'Previous page',
        forward: 'Next page',
        address: 'Current page address',
        loading: 'Loading your storefront…',
        loadError: "We couldn't load your storefront.",
        openLiveUnavailable: 'Publish your storefront first to view it live.',
        resetNotice: 'We reloaded your storefront view. Your saved changes are kept.',
        draftCartNote:
          'Items you add to the cart here are only for trying your store. No orders are created.',
      },
      overlay: {
        moreItems: 'More editable items',
        regionListTitle: 'Editable items on this page',
      },
      region: {
        newsletterCollection: {
          label: 'Subscription settings',
          edit: 'Edit subscription settings',
        },
        heroText: {
          label: 'Hero text',
          edit: 'Edit hero text',
        },
        heroMedia: {
          label: 'Hero image or video',
          edit: 'Edit hero media',
        },
        heroMediaMobile: {
          label: 'Hero media (mobile)',
          edit: 'Edit hero media (mobile)',
        },
        heroMediaDesktop: {
          label: 'Hero media (desktop)',
          edit: 'Edit hero media (desktop)',
        },
        promoText: {
          label: 'Banner text',
          edit: 'Edit banner text',
        },
        promoMedia: {
          label: 'Banner image or video',
          edit: 'Edit banner media',
        },
        aboutText: {
          label: 'About text',
          edit: 'Edit about text',
        },
        aboutMedia: {
          label: 'About image',
          edit: 'Edit about image',
        },
        categoryImage: {
          label: 'Category image',
          edit: 'Edit category image',
        },
        categoryTile: {
          label: 'Category shown',
          edit: 'Choose the category shown',
        },
        categoriesHeading: {
          label: 'Categories heading',
          edit: 'Edit categories heading',
        },
        sectionHeading: {
          label: 'Section heading',
          edit: 'Edit section heading',
        },
        sectionText: {
          label: 'Section text',
          edit: 'Edit section text',
        },
        featureText: {
          label: 'Highlight text',
          edit: 'Edit highlight text',
        },
        announcement: {
          label: 'Announcement bar',
          edit: 'Edit announcement bar',
        },
        tagline: {
          label: 'Store tagline',
          edit: 'Edit store tagline',
        },
        footerText: {
          label: 'Footer text',
          edit: 'Edit footer text',
        },
        storeDetails: {
          label: 'Store details',
          edit: 'Edit store details',
        },
        storeAbout: {
          label: 'About your store',
          edit: 'Edit about your store',
        },
        deliveryPolicy: {
          label: 'Delivery policy',
          edit: 'Edit delivery policy',
        },
        returnsPolicy: {
          label: 'Returns policy',
          edit: 'Edit returns policy',
        },
        productCare: {
          label: 'Care instructions',
          edit: 'Edit care instructions',
        },
        productSizeGuide: {
          label: 'Size guide',
          edit: 'Edit size guide',
        },
        cartNote: {
          label: 'Cart note',
          edit: 'Edit cart note',
        },
        checkoutNote: {
          label: 'Checkout note',
          edit: 'Edit checkout note',
        },
        thankYouMessage: {
          label: 'Thank-you message',
          edit: 'Edit thank-you message',
        },
        legacyText: {
          label: 'Text',
          edit: 'Edit text',
        },
        legacyMedia: {
          label: 'Image',
          edit: 'Edit image',
        },
      },
    },
    storefrontEngine: {
      sections: {
        hero: {
          name: 'Hero section',
          description: 'Shows the first storefront view shoppers see.',
        },
        header: {
          name: 'Store header',
          description: 'Shows the store logo and primary navigation.',
        },
        footer: {
          name: 'Store footer',
          description: 'Shows the Dukanti mark and available contact links.',
        },
        mobileNav: {
          name: 'Mobile navigation',
          description: 'Shows quick navigation links at the bottom of the screen.',
        },
        announcementBar: {
          name: 'Announcement bar',
          description: 'Shows a short message above the store header with an optional link.',
        },
        trustBadge: {
          name: 'Trust badge',
          description: 'Shows a compact review summary near the page header.',
        },
        promotionalAds: {
          name: 'Store ads',
          description: 'Shows active store promotional ads.',
        },
        categories: {
          name: 'Categories',
          description: 'Shows store categories that contain published products.',
        },
        benefits: {
          name: 'Benefits',
          description: 'Shows merchant-authored store benefits with approved icon tokens.',
        },
        faq: {
          name: 'FAQ',
          description: 'Shows merchant-authored questions and answers.',
        },
        about: {
          name: 'About store',
          description: 'Shows merchant-authored localized store paragraphs.',
        },
        testimonials: {
          name: 'Testimonials',
          description: 'Shows merchant-authored quotes without customer or order data.',
        },
        newsletterCta: {
          name: 'Newsletter CTA',
          description: 'Shows a safe call-to-action and optional consented contact collection.',
        },
        liveOffers: {
          name: 'Live offers',
          description: 'Shows active offer campaigns.',
        },
        productRail: {
          bestSellers: {
            name: 'Best sellers',
            description: 'Shows the most ordered products.',
          },
          trendingNow: {
            name: 'Trending now',
            description: 'Shows recently trending products.',
          },
          newArrivals: {
            name: 'New arrivals',
            description: 'Shows the newest published products.',
          },
          specialOffers: {
            name: 'Special offers',
            description: 'Shows discounted products.',
          },
          customCollection: {
            name: 'Custom collection',
            description: 'Shows an ordered product collection chosen by the merchant.',
          },
        },
        trustSummary: {
          name: 'Trust summary',
          description: 'Shows a larger review summary.',
        },
        editorialBanner: {
          name: 'Editorial banner',
          description:
            'Shows a merchant-authored image, heading, text and call-to-action to break up the page rhythm.',
        },
      },
      lifecycle: {
        duplicate: 'Duplicate {{section}}',
        restore: 'Restore {{section}}',
        add: {
          open: 'Add section',
          title: 'Add section',
          description: 'Choose one supported home section to stage for this draft.',
          empty: 'No sections can be added right now.',
          insertion: {
            afterSelected: 'Adds after {{section}}.',
            atPlacement: 'Adds to {{placement}}.',
          },
        },
        delete: {
          open: 'Delete {{section}}',
          title: 'Delete section',
          description:
            'Delete {{section}} from this draft. The section is kept for restore until you publish another change.',
          confirm: 'Delete section',
        },
        pending: {
          ready:
            'Lifecycle change staged. Apply to update the live canvas, or cancel to keep the current page.',
          apply: 'Apply',
          cancel: 'Cancel',
          applied: 'Lifecycle change applied.',
          cancelled: 'Lifecycle change cancelled.',
          blocking: 'Apply or cancel the staged lifecycle change before continuing.',
        },
      },
      inlineEditor: {
        launch: {
          open: 'Open inline editor',
          description: 'Use a full-page storefront canvas for direct section editing.',
        },
        toolbar: {
          label: 'Storefront inline editor toolbar',
          backToBuilder: 'Back to Builder',
          navigationControls: 'Editor navigation',
          statusControls: 'Editor status',
          deviceControls: 'Preview device',
          zoomControls: 'Zoom controls',
          sectionControls: 'Section controls',
          historyControls: 'History controls',
          releaseControls: 'Draft and publish controls',
          openSettings: 'Edit settings',
          openQuickPresentation: 'Quick presentation',
          addSection: 'Add section',
        },
        inspector: {
          title: 'Section inspector',
          description: 'Editing {{section}}.',
          emptyTitle: 'No section selected',
          emptyBody: 'Select a storefront section to edit it.',
          readOnlyTitle: 'Read-only section',
        },
      },
      v3: {
        shell: {
          ariaLabel: 'Storefront Builder V3 editor',
          panelDrawer: 'Builder panel',
          panelDrawerDescription: 'Edit the selected Builder area in a compact drawer.',
          inspectorDrawer: 'Section inspector',
          inspectorDrawerDescription: 'Edit the selected section in a compact inspector drawer.',
        },
        rail: {
          ariaLabel: 'Builder areas',
          templates: 'Templates',
          design: 'Design',
          sections: 'Sections',
          settings: 'Settings',
        },
        toolbar: {
          dukantiIdentity: 'Dukanti',
          storefrontIdentity: 'Storefront',
          back: 'Back to storefront',
          publish: 'Publish',
          undo: 'Undo',
          redo: 'Redo',
          deviceGroup: 'Canvas device',
          status: {
            draft: 'Draft',
            published: 'Published',
          },
        },
        autosave: {
          idle: 'Ready',
          dirty: 'Unsaved changes',
          debouncing: 'Saving soon',
          saving: 'Saving...',
          saved: 'All changes saved',
          failed: "Couldn't save",
          conflict: 'Refresh needed',
          blocked: 'Publish blocked',
          details: {
            idle: 'Draft is ready.',
            dirty: 'Changes are waiting to save as a draft.',
            debouncing: 'Draft will save in a moment.',
            saving: 'Saving this draft before any publish.',
            saved: 'Draft saved. Live storefront unchanged.',
            failed: 'Your local edits are still here.',
            conflict: 'Load the latest draft before publishing.',
            blocked: 'Fix highlighted fields before publishing.',
          },
        },
        templates: {
          title: 'Templates',
          description:
            'Start from a storefront shaped for your business category, then tune design and sections.',
          categoriesTitle: 'Business types',
          allCategories: 'All',
          results: {
            all: 'Available templates: {{count}}',
            category: '{{category}} templates: {{count}}',
            allDescription: 'Choose a business type, preview the fit, then apply it to this draft.',
          },
          metadata: {
            intent: 'Goal: {{intent}}',
            themePreset: 'Style: {{theme}}',
          },
          actions: {
            apply: 'Apply',
            applied: 'Applied',
            save: 'Save current template',
            delete: 'Delete {{name}}',
          },
          intent: {
            quickStart: 'Quick start',
            campaign: 'Campaign led',
            trust: 'Trust led',
            catalog: 'Catalog led',
            story: 'Story led',
            education: 'Education led',
            comparison: 'Comparison led',
            inspiration: 'Inspiration led',
            repeatPurchase: 'Repeat purchase',
            freshness: 'Fresh today',
            occasion: 'Occasion led',
            consultation: 'Consultation led',
          },
          categories: {
            starter: {
              name: 'Starter',
              description: 'General storefronts for merchants who want a balanced beginning.',
            },
            seasonal: {
              name: 'Seasonal',
              description: 'Offer-led layouts for summer, winter, and campaign moments.',
            },
            story: {
              name: 'Story',
              description: 'Editorial storefronts for craft, identity, and trust building.',
            },
            'jewellery-accessories': {
              name: 'Jewellery and accessories',
              description: 'Trust-first templates for rings, gifts, watches, and accessories.',
            },
            fashion: {
              name: 'Fashion',
              description: 'Fast browsing for apparel, shoes, modest wear, and boutiques.',
            },
            'beauty-wellness': {
              name: 'Beauty and wellness',
              description: 'Routine-led layouts for skincare, cosmetics, and wellness products.',
            },
            electronics: {
              name: 'Electronics',
              description: 'Comparison-friendly layouts for devices, accessories, and specs.',
            },
            'home-decor': {
              name: 'Home and decor',
              description: 'Inspiration-led layouts for decor, furniture, and home goods.',
            },
            'food-grocery': {
              name: 'Food and grocery',
              description: 'Dense repeat-purchase layouts for daily essentials and offers.',
            },
            'bakery-prepared-food': {
              name: 'Bakery and prepared food',
              description: 'Freshness-first layouts for bakeries, kitchens, and daily menus.',
            },
            gifts: {
              name: 'Gifts',
              description: 'Occasion-led templates for gift shops and curated bundles.',
            },
            services: {
              name: 'Services',
              description:
                'Trust and consultation layouts for services, appointments, and studios.',
            },
          },
          saved: {
            title: 'Saved templates',
            description: 'Save the current draft and reuse it for seasons, branches, or campaigns.',
            label: 'Save your own',
            placeholder: 'Example: Winter 2027',
            savedTitle: 'Your saved templates',
            loading: 'Loading saved templates...',
            empty: 'No saved templates yet.',
            updatedAt: 'Updated {{value}}',
          },
          messages: {
            applied: 'Template applied to the draft.',
            saved: 'Template saved.',
            deleted: 'Saved template deleted.',
            notFound: 'That saved template is no longer available.',
            applyFailed: 'This template could not be applied.',
            saveFailed: 'This template could not be saved.',
            deleteFailed: 'This template could not be deleted.',
          },
        },
        design: {
          title: 'Design',
          description: 'Shape the storefront look and feel.',
          themeSummary: 'Current theme',
          chooseTheme: 'Choose theme',
          colorOption: '{{role}}: {{color}}',
          baseSize: 'Base size',
          baseSizeOptions: {
            compact: 'Compact',
            comfortable: 'Comfortable',
            large: 'Large',
          },
          groups: {
            theme: 'Theme',
            colors: 'Colors',
            typography: 'Typography',
            buttons: 'Buttons',
            cards: 'Cards',
            spacing: 'Spacing',
          },
          defaults: {
            title: 'Design defaults',
            description: 'Start from a full storefront design or save this draft for reuse.',
            actions: {
              apply: 'Apply',
              save: 'Save current design',
              delete: 'Delete {{name}}',
            },
            tags: {
              balanced: 'Balanced',
              quickStart: 'Quick start',
              seasonal: 'Seasonal',
              offers: 'Offers',
              trust: 'Trust',
              catalog: 'Catalog',
              compact: 'Compact',
              story: 'Story',
              spacious: 'Spacious',
              editorial: 'Editorial',
              newArrivals: 'New arrivals',
              specs: 'Specs',
            },
            items: {
              'dukanti-starter': {
                name: 'Dukanti starter',
                description: 'A balanced storefront for everyday selling with familiar sections.',
              },
              'summer-market': {
                name: 'Summer market',
                description: 'Bright offer-first layout for seasonal campaigns and active deals.',
              },
              'winter-warmth': {
                name: 'Winter warmth',
                description: 'Warmer story and trust sections for slower seasonal browsing.',
              },
              'boutique-catalog': {
                name: 'Boutique catalog',
                description: 'Compact product grids for stores that want shoppers browsing fast.',
              },
              'galilee-story': {
                name: 'Galilee story',
                description:
                  'Spacious editorial flow for stores that lead with craft and identity.',
              },
              'jewellery-atelier': {
                name: 'Jewellery atelier',
                description: 'Trust-first storefront for rings, watches, gifts, and accessories.',
              },
              'fashion-boutique': {
                name: 'Fashion boutique',
                description:
                  'Fast seasonal browsing for apparel, shoes, and accessory collections.',
              },
              'beauty-care': {
                name: 'Beauty care',
                description: 'Routine-led flow for skincare, cosmetics, and wellness products.',
              },
              'electronics-hub': {
                name: 'Electronics hub',
                description: 'Comparison-friendly storefront for devices, accessories, and offers.',
              },
              'home-decor': {
                name: 'Home decor',
                description: 'Editorial inspiration flow for decor, furniture, and home goods.',
              },
              'food-grocery': {
                name: 'Food and grocery',
                description: 'Dense offer-led layout for daily essentials and repeat purchases.',
              },
              'bakery-kitchen': {
                name: 'Bakery kitchen',
                description: 'Freshness-first storefront for bakeries, kitchens, and daily menus.',
              },
              'gift-shop': {
                name: 'Gift shop',
                description: 'Occasion-led browsing for gifts, bundles, and curated collections.',
              },
              'service-studio': {
                name: 'Service studio',
                description:
                  'Trust and FAQ-led storefront for services, appointments, and consultations.',
              },
            },
            custom: {
              label: 'Save your own',
              placeholder: 'Example: Winter 2027',
              savedTitle: 'Saved defaults',
              loading: 'Loading saved defaults...',
              empty: 'No saved defaults yet.',
              updatedAt: 'Updated {{value}}',
            },
            messages: {
              applied: 'Design default applied to the draft.',
              saved: 'Design default saved.',
              deleted: 'Saved design default deleted.',
              notFound: 'That saved design default is no longer available.',
              applyFailed: 'This design default could not be applied.',
              saveFailed: 'This design default could not be saved.',
              deleteFailed: 'This design default could not be deleted.',
            },
          },
        },
        sections: {
          title: 'Sections',
          description: 'Control page structure, visibility, and insertion points.',
          add: 'Add section',
          empty: 'No sections are available for this page.',
          alwaysVisible: 'Always visible',
          more: 'More actions for {{section}}',
          reorder: 'Reorder {{section}}. Drag, or use Arrow Up and Arrow Down.',
        },
        settings: {
          title: 'Settings',
          description: 'Edit storefront navigation, search, and sharing.',
          navigation: 'Navigation',
          seo: 'Search and sharing',
          autosaveReady: 'Changes autosave after each valid edit.',
          invalidLocalBuffers: 'Fix the highlighted field before autosave can continue.',
        },
        gallery: {
          title: 'Add section',
          description: 'Choose a section for this storefront page.',
          searchPlaceholder: 'Search sections',
          categoryAriaLabel: 'Section categories',
          empty: 'No sections match this category.',
          summary: {
            all: 'Section types available: {{count}}',
            category: '{{category}} sections: {{count}}',
            search: 'Search results: {{count}}',
          },
          status: {
            available: 'Available to add',
            blocked: 'Unavailable here',
          },
          categoryDescriptions: {
            all: 'Browse every supported section type for this draft.',
            featured: 'Start with the main storefront identity sections.',
            products: 'Add product rails that use public product sources.',
            categories: 'Add category discovery sections backed by public categories.',
            marketing: 'Add offer and promotion sections that follow existing campaign contracts.',
            content: 'Add story, trust, FAQ, newsletter, and editorial content sections.',
            trust: 'Add proof sections that summarize visible customer confidence signals.',
            global: 'Review global chrome sections that are controlled by Builder contracts.',
            layout: 'Add structural sections only when the current page supports them.',
            advanced: 'Advanced section types stay bounded by the same storefront contracts.',
          },
          categories: {
            all: 'All sections',
            featured: 'Featured',
            products: 'Products',
            categories: 'Categories',
            marketing: 'Marketing',
            content: 'Content',
            trust: 'Trust',
            global: 'Global',
            layout: 'Layout',
            advanced: 'Advanced',
          },
        },
        inspector: {
          close: 'Close inspector',
          tabList: 'Inspector setting groups',
          tabs: {
            content: 'Content',
            design: 'Design',
            settings: 'Settings',
          },
          empty: {
            title: 'Select a section to edit',
            body: 'Choose a section in the real storefront canvas or the structure list.',
          },
          hero: {
            kicker: 'Hero section',
            mediaTitle: 'Hero media',
            mediaHelp:
              'Choose the desktop image first, then add a mobile crop when it helps the composition.',
            desktopRecommendation: 'Recommended desktop image: 1440 x 720 px, wide crop.',
            mobileRecommendation: 'Recommended mobile image: 900 x 1200 px, centered crop.',
            mobileCrop: 'Optional mobile crop',
            mediaSelected: 'Image selected',
            mediaAvailable: 'Available for this hero image',
            mediaUnavailable: 'Choose another image for this hero section.',
            mediaCurrent: 'Current hero image',
            mediaMissing: 'No image selected yet.',
            noImage: 'No image selected',
            browseLibrary: 'Browse image library',
            changeImage: 'Change image',
            mediaUploadHint: 'JPEG, PNG, or WebP. Max 5 MB and 4096 px.',
            copyTitle: 'Localized copy',
            copyHelp:
              'Edit one language at a time. Arabic, Hebrew, and English stay together for this slide.',
            localeTabs: 'Hero content languages',
            characterCount: '{{count}} / {{max}} characters',
            previewLabel: 'Layout preview',
            scheduleTimezone: 'Times are shown in Israel local time and saved as UTC.',
            scheduleInvalid: 'Enter a complete date and time before saving this schedule.',
            specificCategoryHelp:
              'This button links to the selected category. Change the category from the category picker when that control is available.',
            selectedCategory: 'Selected category: {{category}}',
            categorySearch: 'Search categories',
            title: 'Hero content',
            description: 'Edit hero media, text, and actions.',
          },
        },
        overlay: {
          editShort: 'Edit',
          edit: 'Edit {{section}}',
          drag: 'Drag {{section}}',
          more: 'More actions for {{section}}',
          addAfter: 'Add section after {{section}}',
        },
        publish: {
          readyTitle: 'Ready to publish',
          readyDescription: 'Publish the latest saved draft to the live storefront.',
          blockedTitle: 'Fix {{count}} issues before publishing',
          blockedDescription: 'Review each issue, then publish when the draft is ready.',
          failedTitle: 'Publish failed',
          failedRetry: 'Publishing failed. Nothing changed on the live storefront.',
          publishedTitle: 'Store published',
          publishedDescription: 'The live storefront now reflects this draft.',
          confirm: 'Publish store',
          copyLink: 'Copy store link',
          viewLiveStore: 'View live store',
          noBlockingIssues: 'No blocking issues found.',
          summary: {
            store: 'Store',
            link: 'Store link',
            sections: 'Sections',
            sectionCount: '{{count}} sections',
            devices: 'Available views',
            deviceViews: 'Desktop, Tablet, Mobile',
            impact: 'Live impact',
          },
          impact: {
            ready: 'Nothing changes for shoppers until you confirm.',
            blocked: 'The live storefront stays unchanged while issues are fixed.',
            savingDraft: 'Saving this draft first; shoppers still see the current live storefront.',
            validating: 'Checking public-safe content before anything goes live.',
            publishing: 'Publishing is replacing the live storefront now.',
            published: 'Shoppers can now see this published draft.',
            failed: 'The saved draft remains available; the live storefront was not changed.',
          },
          issue: {
            hero: 'Hero section',
            fixHero: 'Add hero content before publishing.',
          },
        },
        errors: {
          validationFailed: 'Fix the highlighted field before autosave can continue.',
        },
        conflict: {
          replaceLocalChanges:
            'Load the latest saved storefront? Your local changes will be discarded.',
          staleDraft: 'This draft changed elsewhere. Refresh before publishing.',
        },
      },
      builder: {
        title: 'Storefront',
        subtitle: 'Arrange the home page sections and choose what shoppers see.',
        sectionListTitle: 'Home page sections',
        source: {
          draft: 'Editing saved draft',
          published: 'Editing published version',
          default: 'Editing default storefront',
        },
        sourceDescriptors: {
          cardTitle: 'Source',
          workspaceTitle: 'Source guidance',
          metrics: {
            selectedCount: '{{count}} selected',
            visibleLimit: 'Shows up to {{count}}',
          },
          badges: {
            automatic: 'Automatic',
            manual: 'Manual',
            customCollection: 'Collection-backed',
            publishedEligibility: 'Published eligibility',
            hidden: 'Hidden',
            disabled: 'Disabled',
            draftDeleted: 'Deleted in draft',
            nonRendered: 'Not rendering',
            invalid: 'Needs attention',
          },
          warnings: {
            emptyManualSelection: 'Choose at least one item before preview or publish.',
            invalidSourceShape:
              'This source setting is not valid. Reapply the source before preview or publish.',
            publishedEligibilityRisk:
              'Preview and publish use only items that remain public and eligible.',
            customCollectionReadinessRisk:
              'Preview and publish check that this collection still has published products.',
            hiddenSection: 'This section is hidden in the Builder and is not shown to shoppers.',
            disabledSection:
              'This section is disabled and will not render until the section contract is repaired.',
            draftDeletedSection:
              'This section is deleted in the draft. Restore it before editing the source.',
            nonRenderedSection: 'This section is not currently renderable in the preview.',
          },
          modes: {
            productAutoBestSellers: {
              label: 'Best sellers',
              summary: 'Automatic product rail from best-selling public products.',
              guidance: 'Uses the existing public merchandising order and the configured limit.',
            },
            productAutoTrendingNow: {
              label: 'Trending now',
              summary: 'Automatic product rail from trending public products.',
              guidance: 'Uses the existing public merchandising order and the configured limit.',
            },
            productAutoNewArrivals: {
              label: 'New arrivals',
              summary: 'Automatic product rail from newest public products.',
              guidance: 'Uses the existing public merchandising order and the configured limit.',
            },
            productAutoSpecialOffers: {
              label: 'Special offers',
              summary: 'Automatic product rail from public products with active offers.',
              guidance: 'Uses the existing public merchandising order and the configured limit.',
            },
            productManualSelection: {
              label: 'Manual products',
              summary: 'Manual product rail from selected public products.',
              guidance: 'Only selected products that remain published and eligible can render.',
            },
            productCustomCollection: {
              label: 'Custom collection',
              summary: 'Product rail backed by a configured custom collection.',
              guidance:
                'Only published products from the configured collection can appear to shoppers.',
            },
            categoryAutoPublic: {
              label: 'Public categories',
              summary: 'Automatic category rail from published public categories.',
              guidance: 'Uses the existing public category order and the configured limit.',
            },
            categoryManualSelection: {
              label: 'Manual categories',
              summary: 'Manual category rail from selected public categories.',
              guidance: 'Only selected categories that remain published and eligible can render.',
            },
            adsAutoActive: {
              label: 'Active ads',
              summary: 'Automatic ads slot from active promotional ads.',
              guidance: 'Uses the existing ads order and the configured limit.',
            },
            adsManualSelection: {
              label: 'Manual ads',
              summary: 'Manual ads slot from selected promotional ads.',
              guidance: 'Only selected ads that remain active and eligible can render.',
            },
            offersAutoLiveCampaigns: {
              label: 'Live campaigns',
              summary: 'Automatic offers rail from live offer campaigns.',
              guidance: 'Uses the existing campaign resolver and the configured limit.',
            },
            invalidSource: {
              label: 'Invalid source',
              summary: 'This commerce section has a source setting the Builder cannot read.',
              guidance: 'Reapply the source settings before preview or publish.',
            },
          },
        },
        states: {
          clean: 'No unsaved changes',
          dirty: 'You have unsaved changes',
          saving: 'Saving changes',
          saved: 'Draft saved',
          failed: 'Could not save changes',
          stale: 'This storefront changed elsewhere. Refresh or reset before publishing.',
          validationBlocked: 'Fix section issues before publishing',
        },
        actions: {
          moveUp: 'Move up',
          moveDown: 'Move down',
          moveUpSection: 'Move {{section}} up',
          moveDownSection: 'Move {{section}} down',
          enableSection: 'Show section',
          hideSection: 'Hide section',
          enableNamedSection: 'Show {{section}}',
          hideNamedSection: 'Hide {{section}}',
          reset: 'Reset',
          refresh: 'Refresh',
          openPreview: 'Open preview',
          openSectionSettings: 'Open settings for {{section}}',
        },
        reorder: {
          position: 'Position {{position}} of {{count}}',
          movedUp: '{{section}} moved up.',
          movedDown: '{{section}} moved down.',
          movedBefore: '{{section}} moved before {{target}}.',
          movedAfter: '{{section}} moved after {{target}}.',
          blockedBoundary: 'This section is already at the edge of its placement.',
          blockedCrossPlacement: 'Sections can only be reordered inside their current placement.',
          blockedProtected: 'This protected section cannot be reordered.',
          blockedDisabled: 'This disabled section cannot be reordered.',
          blockedDeleted: 'Restore this draft-deleted section before reordering it.',
          blockedInvalid: 'This section order change is not valid.',
          blockedBusy: 'Wait for the current builder action to finish before reordering.',
          blockedStale: 'Refresh or reset this stale storefront before reordering.',
          blockedWorkspace: 'Apply or cancel workspace changes before reordering sections.',
          blockedLifecycle: 'Apply or cancel the staged lifecycle change before reordering.',
          invalidRowLabel: 'Invalid section',
          invalidRowDescription:
            'This row is locked because the section contract could not be validated.',
          aria: {
            moveUp: 'Move {{section}} up',
            moveDown: 'Move {{section}} down',
            locked: 'Section reorder is locked',
          },
        },
        liveCanvas: {
          title: 'Live canvas',
          description: 'Renders the storefront from the applied builder config.',
          iframeTitle: 'Live storefront canvas',
          status: {
            idle: 'Canvas idle',
            loadingFrame: 'Loading frame',
            waitingReady: 'Waiting for canvas',
            initializing: 'Initializing canvas',
            loading: 'Loading canvas',
            ready: 'Canvas ready',
            syncing: 'Syncing changes',
            synced: 'Changes synced',
            timeout: 'Canvas did not respond',
            timeoutWithRetry: 'Canvas did not respond. Retry the canvas.',
            invalidPayload: 'Canvas rejected this configuration',
            renderFailed: 'Canvas could not render',
            lostFrame: 'Canvas reloaded',
            permissionDenied: 'Live canvas unavailable',
          },
          actions: {
            retry: 'Retry canvas',
            reloadFrame: 'Reload frame',
            openPreview: 'Open preview',
          },
          controls: {
            toolbarLabel: 'Canvas preview controls',
            previewModesLabel: 'Preview sizes',
            device: {
              desktop: 'Desktop',
              tablet: 'Tablet',
              mobile: 'Mobile',
            },
            deviceAction: {
              desktop: 'Switch to desktop preview',
              tablet: 'Switch to tablet preview',
              mobile: 'Switch to mobile preview',
            },
            zoomControlsLabel: 'Zoom controls',
            zoomIn: 'Zoom in',
            zoomOut: 'Zoom out',
            zoomReset: 'Reset zoom',
            zoomFit: 'Fit',
            zoomFitLabel: 'Fit canvas to the visible width',
            zoomValue: '{{value}}%',
            zoomLabel: '{{value}}% zoom',
            currentState: '{{device}} preview · {{width}} px wide · {{zoom}}% zoom',
          },
          selection: {
            selected: 'Selected section: {{section}}',
            none: 'No section selected',
            clickToEdit: 'Select a canvas section to edit its settings here.',
            unavailable: 'This section cannot be selected from the canvas.',
            invalid: 'Invalid canvas selection was ignored.',
            applied: 'Changes were applied to the live canvas.',
            unappliedPending: 'Apply or cancel workspace changes before selecting another section.',
            chromeLabel: 'Selected in canvas',
            editUnavailable: 'This section cannot be edited from the canvas.',
          },
          errors: {
            invalidOrigin: 'The configured canvas origin is not trusted.',
            invalidNonce: 'The canvas session could not be verified.',
            invalidMessage: 'The canvas message was invalid.',
            invalidConfig: 'The canvas configuration was invalid.',
            invalidSection: 'That section cannot be selected in the canvas.',
          },
        },
        inlineText: {
          title: 'Inline text editor',
          localeTabs: 'Text locales',
          characterCount: '{{count}} / {{max}}',
          locales: {
            ar: 'Arabic',
            he: 'Hebrew',
            en: 'English',
          },
          actions: {
            apply: 'Apply',
            cancel: 'Cancel',
          },
          fields: {
            heroHeadline: 'Hero headline',
            heroEyebrow: 'Hero eyebrow',
            heroDescription: 'Hero description',
            heroPrimaryActionLabel: 'Hero primary button',
            contentBenefitsHeading: 'Benefits heading',
            contentBenefitsBody: 'Benefits body',
            contentBenefitTitle: 'Benefit title',
            contentBenefitBody: 'Benefit body',
            contentFaqHeading: 'FAQ heading',
            contentFaqBody: 'FAQ intro',
            contentFaqQuestion: 'FAQ question',
            contentFaqAnswer: 'FAQ answer',
            contentAboutHeading: 'About heading',
            contentAboutParagraph: 'About paragraph',
            contentTestimonialsHeading: 'Testimonials heading',
            contentTestimonialsBody: 'Testimonials intro',
            contentTestimonialQuote: 'Testimonial quote',
            contentTestimonialName: 'Testimonial name',
            contentTestimonialContext: 'Testimonial context',
            contentNewsletterHeading: 'Newsletter CTA heading',
            contentNewsletterBody: 'Newsletter CTA body',
            contentNewsletterButtonLabel: 'Newsletter CTA button',
            contentEditorialBannerHeading: 'Banner heading',
            contentEditorialBannerBody: 'Banner body',
            contentEditorialBannerButtonLabel: 'Banner button',
          },
          errors: {
            unknownField: 'This text field is not editable.',
            wrongSectionType: 'This text field does not belong to the selected section.',
            staleSection: 'Refresh the builder before editing this text.',
            nonEditableField: 'This text is not editable from the canvas.',
            missingRequiredLocale: 'Complete Arabic, Hebrew, and English before applying.',
            unsafeText:
              'Use plain text only. HTML, scripts, markdown links, and raw URLs are blocked.',
            oversizedText: 'This text is longer than the allowed limit.',
            invalidValue: 'This text value is invalid.',
            invalidConfig: 'This text change does not pass section validation.',
          },
        },
        inlineMedia: {
          title: 'Inline media picker',
          actions: {
            apply: 'Apply',
            cancel: 'Cancel',
          },
          fields: {
            heroDesktop: 'Hero slide desktop image',
            heroMobile: 'Hero slide mobile image',
            trustBadgeImage: 'Trust badge image',
            editorialBannerDesktop: 'Banner desktop image',
            editorialBannerMobile: 'Banner mobile image',
          },
          errors: {
            unknownField: 'This media field is not editable.',
            wrongSectionType: 'This media field does not belong to the selected section.',
            staleSection: 'Refresh the builder before replacing this media.',
            nonEditableField: 'This media is not editable from the canvas.',
            invalidReference: 'This media selection is not available for the storefront.',
            invalidConfig: 'This media change does not pass section validation.',
          },
        },
        inlineRichContent: {
          targetOpened: 'Opened rich content field: {{field}}',
          fields: {
            contentBenefitsBody: 'Benefits body',
            contentBenefitBody: 'Benefit body',
            contentFaqBody: 'FAQ intro',
            contentFaqAnswer: 'FAQ answer',
            contentAboutParagraph: 'About paragraph',
            contentTestimonialsBody: 'Testimonials intro',
            contentTestimonialQuote: 'Testimonial quote',
            contentNewsletterBody: 'Newsletter CTA body',
          },
          errors: {
            unknownField: 'This rich content field is not editable.',
            wrongSectionType: 'This rich content field does not belong to the selected section.',
            staleSection: 'Refresh the builder before editing this rich content.',
            nonEditableField: 'This rich content is not editable from the canvas.',
            invalidConfig: 'This rich content entry point does not pass section validation.',
          },
        },
        inlineQuick: {
          title: 'Quick presentation',
          description: 'Quick presentation controls for {{section}}.',
          actions: {
            open: 'Open quick presentation controls for {{section}}',
            close: 'Close quick presentation controls',
          },
          errors: {
            unavailable: 'Quick presentation controls are unavailable for this section.',
          },
        },
        visibility: {
          enabled: 'Visible to shoppers',
          hidden: 'Hidden from shoppers',
          disabled: 'Disabled',
        },
        placement: {
          globalHeader: 'Page header',
          homeMain: 'Page content',
          globalFooter: 'Page footer',
          mobileNav: 'Mobile navigation',
          globalAnnouncementBar: 'Above page header',
        },
        empty: 'No editable sections for this page.',
        blocked: {
          invalidMove: 'This section cannot be moved to another placement.',
          disabledSection: 'This section is disabled and cannot be changed here.',
          unsupportedSection: 'This section is unsupported and cannot be published.',
          unsupportedEngineVersion: 'This storefront engine version is not editable here.',
          unsupportedSectionType: 'This section type is not supported.',
          invalidPlacement: 'This section cannot be placed there.',
          invalidConfig: 'This lifecycle change is not valid for the current page.',
          protectedSection: 'This protected section cannot be changed here.',
          duplicateProtectedSection: 'This protected section already exists.',
          notDraftDeleted: 'Only draft-deleted sections can be restored.',
        },
        validation: {
          issueCount: '{{count}} section issues',
        },
        history: {
          actions: {
            undo: '',
            redo: '',
          },
          tooltips: {
            undoCommand: '',
            redoCommand: '',
          },
          disabled: {
            noUndo: '',
            noRedo: '',
            busy: '',
            stale: '',
            workspaceDirty: '',
            pendingLifecycle: '',
            unavailable: '',
            invalidConfig: '',
            invalidHistory: '',
          },
          commands: {
            reorderSection: '',
            canvasReorderSection: '',
            applyLifecycle: '',
            applySettings: '',
            applyDesignDefault: '',
            applyInlineText: '',
            applyInlineMedia: '',
            applyTheme: '',
            resetTheme: '',
            applySeo: '',
            resetSeo: '',
            applyChrome: '',
            resetChrome: '',
          },
          messages: {
            undone: '',
            redone: '',
            blocked: '',
            noChange: '',
          },
        },
        shell: {
          regions: {
            toolbar: 'Toolbar',
            sections: 'Sections',
            canvas: 'Preview area',
            workspace: 'Workspace',
            status: 'Status',
          },
          canvas: {
            title: 'Live canvas is coming later',
            body: 'Use preview to inspect the real storefront. This area will host live editing in a later release.',
            selectedSection: 'Selected section: {{section}}',
            noSelection: 'Select a section to see its editing workspace.',
          },
          workspace: {
            emptyTitle: 'No section selected',
            emptyBody: 'Select a section from the panel to see settings.',
            readOnlyTitle: 'Read-only section',
            readOnlyBody:
              'You can review this section here, but it cannot be edited in this release.',
            editableTitle: 'Editable settings',
            footerUnsaved: 'There are unapplied workspace changes',
            applyBeforeSwitch:
              'Apply or cancel workspace changes before selecting another section.',
            sectionSelectedFromCanvas: '{{section}} selected from the live canvas.',
            applyBeforeCanvasUpdate:
              'There are unapplied workspace changes. Changes will not appear in the canvas until you choose Apply.',
            metadata: 'Section metadata',
            metadataLabels: {
              placement: 'Placement',
              status: 'Status',
              visibility: 'Device visibility',
              dataSource: 'Data source',
              viewAllTarget: 'View-all target',
              lifecycle: 'Section stage',
            },
            metadataValues: {
              none: 'Not set',
              dataSourceWithLimit: '{{source}} · {{count}}',
              dataSourceWithSection: '{{source}} · {{section}}',
              dataSources: {
                none: 'No data source',
                storeIdentity: 'Store identity',
                homeMerchandising: 'Home products',
                publicCategories: 'Public categories',
                merchandisingSection: 'Product section',
                promotionalAds: 'Store ads',
                liveOfferCampaigns: 'Live offers',
                storeReviewSummary: 'Store review summary',
              },
              viewAll: {
                collection: 'Collection page',
                sales: 'Sales page',
              },
              lifecycle: {
                active: 'Active',
                draftAdded: 'Added in draft',
                draftDeleted: 'Deleted in draft',
                restored: 'Restored',
              },
            },
            revisions: 'Revisions',
          },
          deferred: {
            addSection: 'Add Section is coming later',
            liveCanvas: 'Live canvas editing is coming later',
            dragDrop: 'Drag and drop is coming later',
            inlineEditing: 'Inline preview editing is coming later',
            undoRedo: '',
            zoom: 'Zoom controls are coming later',
          },
          device: {
            desktop: 'Desktop',
            tablet: 'Tablet',
            mobile: 'Phone',
          },
          zoom: {
            fit: 'Fit',
            value: '{{value}}%',
          },
          status: {
            selectedSection: 'Editing {{section}}',
            noSelection: 'No section selected',
            validationOk: 'No blocking issues',
            loading: 'Loading builder',
            failedLoad: 'Builder failed to load',
            empty: 'No editable sections',
            dirty: 'Unsaved changes',
            workspaceDirty: 'Apply or cancel workspace changes',
            saving: 'Saving draft',
            saved: 'Draft saved',
            saveFailed: 'Draft save failed',
            validationBlocked: 'Fix issues before continuing',
            staleConflict: 'This storefront changed elsewhere',
            previewing: 'Creating preview',
            previewFailed: 'Preview failed',
            publishing: 'Publishing',
            validatingPublish: 'Checking publish readiness',
            savingPublishDraft: 'Saving draft before publish',
            publishBlockedReadiness: 'Publish blocked by readiness',
            publishBlockedStale: 'Refresh required before publish',
            rollingBack: 'Rolling back',
            rollbackFailed: 'Rollback failed',
            restoringDraft: 'Restoring draft',
            restoreDraftFailed: 'Draft restore failed',
            publishSuccess: 'Published',
            publishFailed: 'Publish failed',
            permissionDenied: 'No permission',
          },
          actions: {
            applyWorkspace: 'Apply',
            cancelWorkspace: 'Cancel',
            discardWorkspace: 'Discard workspace changes',
            retryOperation: 'Retry',
            selectSection: 'Select {{section}}',
            disabledControl: '{{control}} is not available in this release',
          },
        },
      },
      fieldGroups: {
        content: 'Content',
        dataSource: 'Data source',
        layout: 'Layout',
        appearance: 'Appearance',
        visibility: 'Visibility',
      },
      settings: {
        title: 'Section settings',
        description: 'Adjust settings for {{section}}.',
        actions: {
          apply: 'Apply',
          cancel: 'Cancel',
        },
        fields: {
          status: 'Section status',
          deviceVisibility: 'Device visibility',
          categoryLimit: 'Visible categories',
          categoryOrderMode: 'Category order',
          categoryLayoutMode: 'Category layout',
          categoryImageShape: 'Category image shape',
          categoryDensity: 'Category spacing',
          adsLimit: 'Visible ads',
          adsOrderMode: 'Ad order',
          adsLayoutPreset: 'Ad layout',
          adsOrientation: 'Ad orientation',
          adsImageFit: 'Ad image fit',
          adsDensity: 'Ad spacing',
          adsShowTitle: 'Show ad title',
          adsShowDestinationCue: 'Show destination cue',
          categoryHeading: 'Section heading',
          categoryShowProductCount: 'Show product count',
          categoryShowDescription: 'Show descriptions',
          liveOfferLimit: 'Visible live offers',
          productSourceMode: 'Product source',
          productPicker: 'Products',
          productLimit: 'Visible products',
          productLayoutMode: 'Product layout',
          productImageShape: 'Product image shape',
          productDensity: 'Product spacing',
          productImageFit: 'Product image fit',
          productShowPrice: 'Show prices',
          productShowSaleBadge: 'Show sale badges',
          productShowAvailabilityBadge: 'Show availability badges',
          categoryPicker: 'Categories',
          promotionalAdPicker: 'Store ads',
          trustBadgeImage: 'Trust badge image',
          heroSlides: 'Hero slides',
          contentSection: 'Content section',
          collectionPicker: 'Collection',
          newsletterCollection: 'Newsletter collection',
          mediaPickerUnsupported: 'Media picker is not available for this section.',
        },
        pickers: {
          searchPlaceholder: 'Search public content',
          mediaSearchPlaceholder: 'Search public images',
          loading: 'Loading options...',
          loadingMedia: 'Loading images...',
          retry: 'Retry',
          emptyProducts: 'No published products match this search.',
          emptySelectedProducts: 'Select at least one product before applying this manual rail.',
          customCollectionSearchPlaceholder: 'Search custom collections',
          createCustomCollection: 'Create collection',
          loadingCustomCollections: 'Loading custom collections...',
          customCollectionsUnavailable: 'Could not load custom collections. Try again.',
          emptyCustomCollections: 'No custom collections yet.',
          emptyCustomCollectionProducts: 'Add at least one published product to this collection.',
          customCollectionProductCount: '{{count}} products',
          editCustomCollection: 'Edit collection',
          customCollectionNameAr: 'Collection name in Arabic',
          customCollectionNameHe: 'Collection name in Hebrew',
          customCollectionNameEn: 'Collection name in English',
          customCollectionDescriptionAr: 'Collection description in Arabic',
          customCollectionDescriptionHe: 'Collection description in Hebrew',
          customCollectionDescriptionEn: 'Collection description in English',
          customCollectionSaveFailed: 'Could not save the collection.',
          saveCustomCollection: 'Save collection',
          emptyCategories: 'No public categories match this search.',
          emptyCollections: 'No collections are available.',
          emptyMedia: 'No public images match this search.',
          emptyPromotionalAds: 'No eligible active ads match this search.',
          emptyMediaSelection: 'No image selected yet.',
          selectedMedia: 'Selected image',
          safeMediaReference: 'Safe image reference',
          mediaError: 'Could not load public images.',
          mediaDefaultLabel: 'Untitled image',
          uploadMedia: 'Upload image',
          replaceMedia: 'Replace image',
          removeMedia: 'Remove image',
          refreshMedia: 'Refresh images',
          mediaUploadHint: 'JPEG, PNG, or WebP. Max 5 MB and 4096 px.',
          mediaUploading: 'Uploading image...',
          mediaRemoving: 'Removing image...',
          mediaUnavailable: 'Image unavailable',
          mediaUnavailableDescription: 'Choose another image before applying this section.',
          mediaInUse:
            'This image is used in the saved storefront. Clear or replace it, then save before removing.',
          mediaRemoveConfirm: 'Remove this image from the media library?',
          mediaRemoveConfirmDescription:
            'This does not clear saved storefront sections that already use it.',
          mediaSelect: 'Select image',
          mediaSelected: 'Selected',
          mediaUploadFailed: 'Could not upload the image.',
          mediaRemoveFailed: 'Could not remove the image.',
          mediaInvalidType: 'Use JPEG, PNG, or WebP only.',
          mediaTooLarge: 'Image is larger than 5 MB.',
          mediaDimensionsInvalid: 'Image dimensions must be between 1 and 4096 px.',
          mediaRequired: 'Choose an image to upload.',
          selectedCount: '{{count}} of {{max}} selected',
          unavailableSelectedProduct: 'Unavailable product',
          promotionalAdsSearchPlaceholder: 'Search active ads',
          selectedPromotionalAdsCount: '{{count}} of {{max}} ads selected',
          emptySelectedPromotionalAds: 'No ads selected yet.',
          loadingPromotionalAds: 'Loading active ads...',
          promotionalAdsUnavailable: 'Could not load active ads. Try again.',
          removeSelection: 'Remove selection',
          clearSelection: 'Clear selection',
          moveSelectionUp: 'Move selection up',
          moveSelectionDown: 'Move selection down',
          categoryFilterAll: 'All categories',
          categoryFilterButton: 'Filter by category',
          categoryProductCount: '{{count}} products',
          availability: {
            inStock: 'In stock',
            outOfStock: 'Out of stock',
          },
          source: {
            collection: 'Collection',
            manualProducts: 'Manual products',
            customCollection: 'Custom collection',
            autoCategories: 'Automatic categories',
            manualCategories: 'Manual categories',
            autoPromotionalAds: 'Automatic active ads',
            autoPromotionalAdsHelp:
              'Uses active ads from Promotions in their business display order. Ad creation, media, destinations, activation, and ordering stay managed in Promotions.',
            manualPromotionalAds: 'Selected ads',
          },
        },
        boundaries: {
          adsPresentationOnlyTitle: 'Store Ads stay managed in Promotions',
          adsPresentationOnly:
            'Builder controls how this section presents ads. Create ads, set destinations, upload ad media, activate ads, and change business order in Promotions.',
          manageAdsLink: 'Manage ads',
        },
        visibility: {
          all: 'All devices',
          desktop: 'Desktop only',
          tablet: 'Tablet only',
          mobile: 'Mobile only',
          none: 'No device selected',
          devices: {
            desktop: 'Desktop',
            tablet: 'Tablet',
            mobile: 'Mobile',
          },
          deviceActions: {
            desktop: 'Show on desktop',
            tablet: 'Show on tablet',
            mobile: 'Show on mobile',
          },
        },
        help: {
          deviceVisibility: 'Choose which screen sizes should show this section.',
          trustBadgeImage: 'Choose an approved public image for this section.',
          categoryLimit: 'Choose how many categories appear in this rail.',
          categoryOrderMode:
            'Automatic categories follow the category manager order; selected categories follow this rail list.',
          categoryLayoutMode: 'Choose whether categories render as a rail or a responsive grid.',
          categoryImageShape: 'Choose the image frame used by category cards.',
          categoryDensity: 'Choose the amount of spacing inside the category rail.',
          adsLimit: 'Choose how many store ads appear in this section.',
          adsOrderMode:
            'Automatic ads follow the Promotions manager order; selected ads follow this section list.',
          adsLayoutPreset: 'Choose how store ads are arranged.',
          adsOrientation:
            'Horizontal ads span the storefront width; vertical ads use a centered portrait frame.',
          adsImageFit: 'Choose how ad images fit inside their frame.',
          adsDensity: 'Choose the amount of spacing inside the Store Ads section.',
          adsShowTitle: 'Show or hide the public ad title.',
          adsShowDestinationCue: 'Show or hide a generic destination cue.',
          categoryHeading:
            'Optional. Shown above the categories instead of the default title. Leave all three empty to use the default title.',
          categoryShowProductCount: 'Show or hide public product counts for each category.',
          categoryShowDescription:
            'Show the safe public descriptions you added in category management.',
          liveOfferLimit: 'Choose how many live offers appear in this rail.',
          productLimit: 'Choose how many products appear in this rail.',
          productLayoutMode: 'Choose whether products render as a rail or a responsive grid.',
          productImageShape: 'Choose the image frame used by product cards.',
          productDensity: 'Choose the amount of spacing inside the product rail.',
          productImageFit: 'Choose how product images fit inside their frame.',
          productShowPrice: 'Show or hide the public product price.',
          productShowSaleBadge: 'Show or hide public sale badges when a product is on sale.',
          productShowAvailabilityBadge: 'Show or hide public stock availability badges.',
          heroSlides:
            'Add up to three localized slides with safe destinations and approved public images.',
          contentSection:
            'Write localized merchant content with approved tokens only. HTML, custom URLs, scripts, styles, and customer data are rejected.',
        },
        hero: {
          emptyTitle: 'Store identity fallback is active',
          emptyBody:
            'Add a slide when you want to replace the default storefront hero. Until then, shoppers see the store name and description.',
          addSlide: 'Add slide',
          removeSlide: 'Remove slide',
          slideLabel: 'Slide {{index}}',
          enabled: 'Visible',
          headline: 'Headline',
          eyebrow: 'Eyebrow',
          description: 'Description',
          primaryAction: 'Primary button',
          secondaryAction: 'Secondary button',
          addSecondaryAction: 'Add secondary button',
          removeSecondaryAction: 'Remove secondary button',
          buttonLabel: 'Button label',
          desktopMedia: 'Desktop image',
          mobileMedia: 'Mobile image',
          destination: 'Destination',
          categoryId: 'Category ID',
          overlay: 'Overlay',
          contentPosition: 'Content position',
          textAlignment: 'Text alignment',
          layoutPreset: 'Layout preset',
          scheduleTitle: 'Schedule window',
          scheduleHelp:
            'Optional Israel-time window. It is saved as UTC and shown to shoppers only while active.',
          startsAt: 'Starts at',
          endsAt: 'Ends before',
          clearSchedule: 'Clear schedule',
          scheduleInvalidRange: 'End time must be after the start time.',
          scheduleStates: {
            always: 'Always active',
            active: 'Active now',
            upcoming: 'Scheduled for later',
            expired: 'Expired',
          },
          locales: {
            ar: 'Arabic',
            he: 'Hebrew',
            en: 'English',
          },
          targets: {
            storeHome: 'Store home',
            storeCategories: 'Categories',
            storeSales: 'Sales',
            bestSellers: 'Best sellers',
            trendingNow: 'Trending now',
            newArrivals: 'New arrivals',
            storeCategory: 'Specific category',
            cart: 'Cart',
            customerAccount: 'Customer account',
            customerOrders: 'Customer orders',
            whatsapp: 'WhatsApp',
            phone: 'Phone',
          },
          overlays: {
            none: 'None',
            soft: 'Soft',
            medium: 'Medium',
            strong: 'Strong',
          },
          positions: {
            start: 'Start',
            center: 'Center',
            end: 'End',
          },
          alignments: {
            start: 'Start',
            center: 'Center',
            end: 'End',
          },
          layoutPresets: {
            immersive: 'Immersive',
            balanced: 'Balanced',
            compact: 'Compact',
          },
        },
        content: {
          emptyBody: 'This optional text is empty for shoppers until you add localized copy.',
          addItem: 'Add item',
          removeItem: 'Remove item',
          moveItemUp: 'Move item up',
          moveItemDown: 'Move item down',
          itemLabel: 'Item {{index}}',
          heading: 'Heading',
          body: 'Body',
          paragraph: 'Paragraph',
          title: 'Title',
          question: 'Question',
          answer: 'Answer',
          quote: 'Quote',
          name: 'Display name',
          context: 'Context',
          buttonLabel: 'Button label',
          icon: 'Icon',
          layout: 'Layout',
          tone: 'Tone',
          cta: 'Call to action',
          ctaEnabled: 'Show button',
          collectionEnabled: 'Collect subscribers',
          collectionContactMethods: 'Accepted contacts',
          collectionEmail: 'Email',
          collectionPhone: 'Israeli phone',
          collectionConsentVersion: 'Consent version {{version}}',
          newsletterNote:
            'Collection is off until enabled here, saved, and published. Shoppers must consent before submitting.',
          testimonialNote:
            'Use merchant-authored display text only. Customer, order, review, phone, or email data is not connected here.',
          unsafeNote:
            'Safe formatting only. HTML, Markdown, raw links, scripts, styles, and hidden controls are rejected.',
          editorialBannerNote:
            'Choose a desktop image and a mobile image from the public media library. Either can be left empty.',
          media: 'Media',
          mediaDesktop: 'Desktop image',
          mediaMobile: 'Mobile image',
          richTextControls: 'Formatting',
          richTextPlain: 'Plain text',
          richTextBold: 'Bold',
          richTextItalic: 'Italic',
          richTextBulleted: 'Bulleted list',
          richTextNumbered: 'Numbered list',
          richTextApplyLink: 'Apply link',
          richTextRemoveLink: 'Remove link',
          icons: {
            'badge-check': 'Badge check',
            'heart-handshake': 'Heart handshake',
            leaf: 'Leaf',
            'package-check': 'Package check',
            'shield-check': 'Shield check',
            sparkles: 'Sparkles',
            truck: 'Truck',
          },
          layouts: {
            cards: 'Cards',
            list: 'List',
            accordion: 'Accordion',
            story: 'Story',
            quotes: 'Quotes',
            banner: 'Banner',
            split: 'Image beside text',
            overlay: 'Text over image',
          },
          tones: {
            neutral: 'Neutral',
            warm: 'Warm',
            accent: 'Accent',
          },
        },
        categoryOrderMode: {
          categoryDisplayOrder: 'Category manager order',
          selectedListOrder: 'Selected list order',
        },
        categoryLayoutMode: {
          scrollRail: 'Scroll rail',
          responsiveGrid: 'Responsive grid',
        },
        categoryImageShape: {
          circle: 'Circle',
          roundedSquare: 'Soft square',
        },
        categoryDensity: {
          comfortable: 'Comfortable',
          compact: 'Compact',
        },
        productLayoutMode: {
          scrollRail: 'Scroll rail',
          responsiveGrid: 'Responsive grid',
        },
        productImageShape: {
          square: 'Square',
          roundedSquare: 'Soft square',
        },
        productDensity: {
          comfortable: 'Comfortable',
          compact: 'Compact',
        },
        productImageFit: {
          cover: 'Cover',
          contain: 'Contain',
        },
        adsOrderMode: {
          businessDisplayOrder: 'Promotions manager order',
          selectedListOrder: 'Selected list order',
        },
        adsLayoutPreset: {
          carousel: 'Carousel',
          stackedCards: 'Stacked cards',
        },
        adsOrientation: {
          horizontal: 'Horizontal',
          vertical: 'Vertical',
        },
        adsImageFit: {
          cover: 'Cover',
          contain: 'Contain',
        },
        adsDensity: {
          comfortable: 'Comfortable',
          compact: 'Compact',
        },
        readOnly: {
          dataSource: 'Data source',
          placement: 'Placement',
        },
        messages: {
          applied: 'Section settings applied.',
          cancelled: 'Section setting changes cancelled.',
        },
        aria: {
          sectionSettingsSurface: 'Section settings surface',
          applySettingChanges: 'Apply section setting changes',
          cancelSettingChanges: 'Cancel section setting changes',
        },
        errors: {
          sectionNotEditable: 'This section cannot be edited here.',
          deletedSectionReadOnly:
            'This section is deleted from the draft. Restore it before editing settings.',
          duplicateInvalidReadOnly:
            'This section is an invalid duplicate. Fix the duplicate before editing settings.',
          unsupportedField: 'This field cannot be edited here.',
          staleBuilder: 'This storefront changed elsewhere. Refresh before editing settings.',
          busyBuilder: 'Wait for the current operation to finish before editing settings.',
          invalidPatchSafe: 'This change was not applied. The previous settings stayed intact.',
          lastVisibleDevice:
            'Keep at least one device visible. Use section status to hide the section everywhere.',
          invalidInteger: 'Enter a whole number.',
          outOfRange: 'Choose a number within the allowed range.',
          invalidReference: 'Invalid reference: this selection is no longer available.',
          customCollectionNameRequired: 'Enter at least an Arabic collection name.',
          emptyCustomCollection:
            'Add at least one published product before choosing this collection.',
          emptyProductSelection: 'Select at least one product before applying this manual rail.',
          invalidHeroConfig:
            'Complete every enabled hero slide in Arabic, Hebrew, and English before applying.',
          invalidContentConfig:
            'Complete required Arabic, Hebrew, and English content and remove unsafe text before applying.',
          crossStoreReference: 'This selection is not available for this store.',
          unpublishedReference: 'This selection is not published.',
          deletedReference: 'This selection was removed.',
          disabledReference: 'This selection is disabled.',
          emptyCategoryReference: 'This category has no public products.',
          emptyCollectionReference: 'This collection has no public products.',
          unsafeAdsDestination: 'This ad destination is not available.',
          missingAdsMedia: 'This ad media is not available.',
          duplicateReference: 'This selection is already included.',
          tooManyReferences: 'Remove a selection before adding another one.',
          unsupportedContentKind: 'This content type is not supported here.',
          categoryHeadingLocales:
            'Enter the heading in Arabic, Hebrew and English (up to 120 characters each), or leave all three empty.',
        },
      },
      publicContent: {
        errors: {
          invalidReference: 'Invalid reference: this selection is no longer available.',
          crossStoreReference: 'This selection is not available for this store.',
          unpublishedReference: 'This selection is not published.',
          deletedReference: 'This selection was removed.',
          disabledReference: 'This selection is disabled.',
          emptyCategoryReference: 'This category has no public products.',
          emptyCollectionReference: 'This collection has no public products.',
          unsafeAdsDestination: 'This ad destination is not available.',
          missingAdsMedia: 'This ad media is not available.',
          duplicateReference: 'This selection is already included.',
          tooManyReferences: 'Remove a selection before adding another one.',
          unsupportedContentKind: 'This content type is not supported here.',
        },
      },
      newsletterSubscribers: {
        title: 'Newsletter contacts',
        description: 'Recent consented contacts collected from published newsletter CTA forms.',
        loading: 'Loading contacts...',
        empty: 'No contacts collected yet.',
        loadError: 'Could not load contacts.',
        exportEmpty: 'No contacts to export.',
        exportReady: 'CSV export ready.',
        suppressFailed: 'Could not suppress this contact.',
        columns: {
          contact: 'Contact',
          status: 'Status',
          locale: 'Locale',
          source: 'Source section',
          created: 'Created',
        },
        actions: {
          refresh: 'Refresh contacts',
          export: 'Export CSV',
          suppress: 'Suppress contact',
        },
        contactTypes: {
          email: 'Email',
          phone: 'Phone',
        },
        statuses: {
          active: 'Active',
          suppressed: 'Suppressed',
        },
      },
      status: {
        draft: 'Draft',
        published: 'Published',
        archived: 'Archived',
      },
      readiness: {
        title: 'Publish readiness',
        ready: 'Ready to preview and publish',
        blocked: 'Readiness blocked',
        checking: 'Checking readiness...',
        warningCount: '{{count}} warnings',
        blockerCount: '{{count}} blockers',
        publishBlocked: 'Publish is blocked until readiness issues are fixed.',
        previewBlocked: 'Preview is blocked until readiness issues are fixed.',
        focusSection: 'Focus section',
        focusPage: 'Focus page settings',
        pageIssue: 'Page issue',
        severity: {
          blocker: 'Blocker',
          warning: 'Warning',
        },
        errors: {
          freshnessUnknown: 'Readiness could not be confirmed. Check again before continuing.',
          publishBlocked: 'Publish readiness failed. Fix the blockers and try again.',
          previewBlocked: 'Preview readiness failed. Fix the blockers and try again.',
        },
        issues: {
          invalidConfiguration: 'The storefront configuration is invalid.',
          invalidThemeSettings: 'Theme settings contain unsupported style choices.',
          invalidNavigationTarget: 'A navigation item points somewhere unsupported.',
          unsafeNavigationUrl: 'A social profile link is unsafe.',
          missingNavigationContact: 'A navigation contact link is missing its public channel.',
          missingStoreLogo: 'Upload a store logo before publishing your storefront.',
          malformedConfig: 'The storefront configuration could not be read.',
          unsupportedEngineVersion: 'This storefront engine version is not supported.',
          unsupportedSchemaVersion: 'A section version is not supported.',
          unsupportedSection: 'A section is not supported.',
          invalidPlacement: 'A section is in an invalid placement.',
          duplicateSectionId: 'Two sections share the same identifier.',
          duplicateSingleton: 'A section that can appear only once was duplicated.',
          disabledSection: 'A disabled section blocks preview and publish.',
          hiddenSection: 'A hidden section will not render for shoppers.',
          draftDeletedSection: 'A draft-deleted section will be removed when published.',
          restoredSectionInvalid: 'A restored section needs valid public content.',
          invalidLifecycle: 'A section lifecycle state is invalid.',
          invalidOrder: 'The section order is invalid.',
          invalidProductReference: 'A product selection is no longer publicly available.',
          invalidCategoryReference: 'A category selection is no longer publicly available.',
          invalidMediaReference: 'A media selection is no longer publicly available.',
          invalidAdsConfig: 'Store Ads settings are invalid.',
          invalidAdsReference: 'An ad selection is no longer publicly available.',
          unsafeAdsDestination: 'An ad destination is not available.',
          missingAdsMedia: 'An ad image is not available.',
          invalidHeroConfig: 'Hero section settings are invalid.',
          invalidHeroDestination: 'A hero button destination is no longer available.',
          invalidHeroMediaReference: 'A hero image is no longer publicly available.',
          tooManyReferences: 'A section contains too many selections.',
          unsafePublicConfig: 'Unsafe public fields must be removed before preview or publish.',
        },
      },
      actions: {
        saveDraft: 'Save draft',
        savingDraft: 'Saving draft...',
        publish: 'Publish',
        publishing: 'Publishing...',
        preview: 'Preview',
        createPreviewLink: 'Create preview link',
        publishDisabledReadiness: 'Publish is disabled because readiness has blockers.',
        previewDisabledReadiness: 'Preview is disabled because readiness has blockers.',
        rollback: 'Roll back',
        rollingBack: 'Rolling back...',
        restoreToDraft: 'Restore to draft',
      },
      messages: {
        draftSaved: 'Draft saved.',
        published: 'Storefront published.',
        rollbackPublished: 'Rollback published.',
        draftRestored: 'Revision restored to draft. Publish when it is ready for shoppers.',
        previewLinkCreated: 'Preview link created.',
        defaultFallbackActive: 'Using the default storefront until a config is published.',
        noDraft: 'No draft is saved yet.',
        noRevisions: 'No revisions yet.',
      },
      preview: {
        banner: 'Preview mode',
        expired: 'This preview link expired.',
        notAvailable: 'Preview is not available.',
        noindexLabel: 'Hidden from search engines',
      },
      revisions: {
        title: 'Revision history',
        revisionNumber: 'Revision {{number}}',
        publishedAt: 'Published {{date}}',
        publishedBy: 'Published by {{name}}',
        rolledBackFrom: 'Rolled back from revision {{number}}',
        restoredFrom: 'Copied from revision {{number}}',
        sectionCount: '{{count}} sections',
        actions: {
          draft_save: 'Draft save',
          publish: 'Publish',
          rollback: 'Rollback',
          restore_to_draft: 'Restore to draft',
        },
      },
      publishSummary: {
        title: '',
        description: '',
        pendingTitle: '',
        changedSurfacesTitle: '',
        changedSectionsTitle: '',
        changedSectionLine: '',
        noChangedSurfaces: '',
        noChangedSections: '',
        additionalChangedSections: '',
        publicExposureReminder: '',
        confirm: '',
        labels: {
          draft: '',
          localChanges: '',
          workspaceEdits: '',
          readiness: '',
        },
        values: {
          draftExists: '',
          noDraft: '',
          localChanges: '',
          noLocalChanges: '',
          workspaceEdits: '',
          noWorkspaceEdits: '',
        },
        surfaces: {
          seo: '',
          theme: '',
          navigation: '',
          hero: '',
          categories: '',
          ads: '',
          productRails: '',
          contentSections: '',
          sectionOrder: '',
          sectionLifecycle: '',
        },
        sectionTypes: {
          productRails: '',
        },
        changeKinds: {
          added: '',
          removed: '',
          changed: '',
          moved: '',
          statusChanged: '',
        },
      },
      publishOperation: {
        states: {
          idle: 'Ready to publish',
          validating: 'Checking readiness',
          savingDraft: 'Saving draft',
          publishing: 'Publishing',
          published: 'Published',
          failedRecoverable: 'Publish failed',
          blockedReadiness: 'Readiness blocked',
          blockedStale: 'Refresh required',
          blockedPermissions: 'No permission',
        },
        helpers: {
          idle: 'Nothing is public until you confirm.',
          validating: 'Checking the current draft and public references.',
          savingDraft: 'Saving local changes before publish.',
          publishing: 'Replacing the public storefront with the approved draft.',
          published: 'The public storefront now uses this revision.',
          failedRecoverable:
            'The draft was kept. Network errors do not mean the public storefront changed.',
          blockedReadiness: 'Fix readiness blockers before publishing.',
          blockedStale: 'Refresh state before publishing so newer edits are not overwritten.',
          blockedPermissions: 'This account cannot publish this storefront.',
        },
        actions: {
          retry: 'Retry publish',
          reviewIssues: 'Review issues',
          refreshState: 'Refresh state',
          continueEditing: 'Continue editing',
        },
      },
      restoreDialog: {
        title: 'Restore revision to draft',
        description: 'Review this safe revision summary before replacing the current draft.',
        confirm: 'Restore draft',
        restoring: 'Restoring...',
        publicUnaffected:
          'This creates a draft only. The public storefront will not change until you publish.',
        labels: {
          status: 'Status',
          action: 'Action',
          sections: 'Sections',
          date: 'Date',
        },
      },
      errors: {
        invalidConfiguration: 'This storefront configuration is invalid.',
        invalidThemeSettings: 'Theme settings contain unsupported style choices.',
        invalidNavigationTarget: 'A navigation item points somewhere unsupported.',
        unsafeNavigationUrl: 'A social profile link is unsafe.',
        missingNavigationContact: 'A navigation contact link is missing its public channel.',
        unsupportedEngineVersion: 'This storefront engine version is not supported.',
        unsupportedSchemaVersion: 'This storefront section version is not supported.',
        invalidPlacement: 'A section is in an invalid placement.',
        duplicateSingleton: 'A section that can appear only once was duplicated.',
        unsafeRichText: 'Remove unsafe formatted text before saving.',
        unsafeMediaReference: 'Remove unsafe media references before saving.',
        unsafeUrl: 'Remove unsafe links before saving.',
        crossStoreReference: 'A section references content from another store.',
        draftNotFound: 'The draft could not be found.',
        revisionNotFound: 'The revision could not be found.',
        previewExpired: 'This preview link expired.',
        publishConflict: 'Another publish changed this storefront. Refresh and try again.',
        permissionDenied: 'You do not have permission to manage this storefront.',
        networkFailed: 'Connection failed. Check your network and try again.',
      },
      validation: {
        enabledSection: 'This section is enabled and will be shown to shoppers.',
        hiddenSection: 'This section is hidden and will not be shown to shoppers.',
        disabledSection: 'This section is disabled and will not be shown to shoppers.',
        unsupportedSection: 'This section is not supported.',
        invalidConfiguration: 'This section configuration is invalid.',
        invalidHeroConfig: 'Hero section content is invalid.',
        invalidThemeSettings: 'Theme settings contain unsupported style choices.',
        invalidNavigationTarget: 'A navigation item points somewhere unsupported.',
        unsafeNavigationUrl: 'A social profile link is unsafe.',
        missingNavigationContact: 'A navigation contact link is missing its public channel.',
        invalidPlacement: 'This section cannot be shown in this placement.',
        duplicateSection: 'This section cannot be duplicated.',
        unsupportedEngineVersion: 'This storefront engine version is not supported.',
        invalidLifecycleAction: 'This section lifecycle action is not allowed.',
        invalidResponsiveVisibility: 'Choose at least one valid device visibility.',
        invalidWorkspaceTemplate: 'This workspace template is not supported.',
        invalidLibraryEntry: 'This section library entry is not supported.',
        unsafePublicConfig: 'This section contains fields that cannot be public.',
        unsafeMediaReference: 'This section contains an unsafe media reference.',
        unsafeRichText: 'This section contains unsafe formatted text.',
        unsafeUrl: 'This section contains an unsafe link.',
        crossStoreReference: 'This section references another store.',
      },
      fallback: {
        sectionUnavailable: 'This section cannot be shown right now.',
      },
      seo: {
        title: 'Search and sharing',
        description:
          'Control the title, description, and image shown to shoppers and search engines.',
        fields: {
          title: 'Search title',
          description: 'Search description',
          socialImage: 'Sharing image',
        },
        locales: {
          ar: 'Arabic',
          he: 'Hebrew',
          en: 'English',
        },
        placeholders: {
          title: 'Write a short store title',
          description: 'Write a concise safe store description',
        },
        counters: {
          text: '{{count}} of {{max}}',
        },
        actions: {
          apply: 'Apply search metadata',
          cancel: 'Cancel search changes',
          reset: 'Reset search metadata',
        },
        status: {
          clean: 'No unapplied changes.',
          dirty: 'Apply or cancel search changes before saving or publishing.',
        },
        messages: {
          applied: 'Search metadata applied to the draft.',
          reset: 'Search metadata reset to the default.',
        },
        errors: {
          invalidMetadata:
            'This search metadata was not applied. The previous draft stayed intact.',
          unsafeText: 'Use plain text only, without links or tags.',
          titleTooLong: 'Search title is over the allowed limit.',
          descriptionTooLong: 'Search description is over the allowed limit.',
          staleBuilder:
            'This storefront changed elsewhere. Refresh before changing search metadata.',
        },
      },
      theme: {
        title: 'Theme style',
        description: 'Control the storefront-wide visual style with approved Dukanti presets.',
        actions: {
          reset: 'Reset theme to default',
        },
        presets: {
          title: 'Theme presets',
          custom: 'Custom token mix',
          selected: 'Selected',
          apply: 'Apply',
          items: {
            'dukanti-default': {
              name: 'Dukanti neutral',
              description: 'A quiet starting point with neutral canvas and soft edges.',
            },
            'olive-grove': {
              name: 'Olive grove',
              description: 'Warm olive tones for local food and craft storefronts.',
            },
            'saffron-market': {
              name: 'Saffron market',
              description: 'Clear amber emphasis for offers and active storefronts.',
            },
            'stone-boutique': {
              name: 'Stone boutique',
              description: 'Clean surfaces and compact spacing for product-heavy shops.',
            },
            'galilee-mist': {
              name: 'Galilee mist',
              description: 'Calm blue-green accents and roomy spacing for a gentle storefront.',
            },
            'heritage-paper': {
              name: 'Heritage paper',
              description: 'Editorial type and paper tones for a warmer store story.',
            },
          },
        },
        fields: {
          palette: 'Palette',
          primary: 'Primary color',
          accent: 'Accent color',
          background: 'Background',
          surface: 'Surface',
          text: 'Text',
          typography: 'Typography',
          buttonRadius: 'Button radius',
          cardRadius: 'Card radius',
          spacing: 'Section spacing',
        },
        palettes: {
          'dukanti-default': 'Dukanti default',
          'olive-grove': 'Olive grove',
          'saffron-market': 'Saffron market',
        },
        colorTokens: {
          'neutral-ink': 'Neutral ink',
          'semantic-status': 'Status accents',
          'neutral-canvas': 'Neutral canvas',
          'neutral-surface': 'Neutral surface',
          'zaatar-olive': "Za'atar olive",
          'zaatar-deep': "Za'atar deep",
          'saffron-amber': 'Saffron amber',
          'zaatar-soft': "Za'atar soft",
          'olive-mist': 'Olive mist',
          paper: 'Paper',
          'stone-100': 'Warm stone',
          'zaatar-wash': "Za'atar wash",
          'stone-700': 'Deep stone',
          'stone-600': 'Soft stone',
        },
        typography: {
          'language-default': 'Language default',
          'heritage-editorial': 'Heritage editorial',
          'modern-market': 'Modern market',
          'clean-commerce': 'Clean commerce',
        },
        radius: {
          crisp: 'Crisp',
          soft: 'Soft',
          rounded: 'Rounded',
        },
        spacing: {
          compact: 'Compact',
          comfortable: 'Comfortable',
          spacious: 'Spacious',
        },
        messages: {
          applied: 'Theme style applied to the draft.',
          reset: 'Theme style reset to the default.',
        },
        errors: {
          invalidThemeSettings:
            'This theme style was not applied. The previous draft stayed intact.',
          staleBuilder: 'This storefront changed elsewhere. Refresh before changing theme style.',
        },
      },
      chrome: {
        title: 'Header and navigation',
        description:
          'Configure the announcement bar, storefront header, footer, and shopper navigation.',
        fields: {
          announcementBar: 'Announcement bar',
          announcementBarEnabled: 'Show announcement bar',
          announcementBarText: 'Announcement text',
          announcementBarLink: 'Link (optional)',
          headerBrand: 'Header brand',
          headerLinks: 'Header links',
          footerBrand: 'Footer brand',
          footerLinks: 'Footer links',
          footerContact: 'Footer contact',
          socialLinks: 'Social links',
          mobileItems: 'Mobile navigation',
          target: 'Target',
          collection: 'Collection',
          categoryId: 'Category ID',
          socialProvider: 'Social profile',
          socialUrl: 'Profile URL',
        },
        placeholders: {
          categoryId: '00000000-0000-0000-0000-000000000000',
        },
        announcementBarLink: {
          none: 'No link',
        },
        hints: {
          announcementBarText:
            'Up to 140 characters per language. Leave every language empty to show nothing.',
        },
        actions: {
          reset: 'Reset header and navigation',
          apply: 'Apply navigation',
          add: 'Add',
          remove: 'Remove',
          moveUp: 'Move up',
          moveDown: 'Move down',
        },
        headerBrand: {
          logo: 'Logo',
          name: 'Name',
          'logo-and-name': 'Logo and name',
        },
        footerBrand: {
          'dukanti-and-store': 'Dukanti and store',
          'store-only': 'Store only',
          'dukanti-only': 'Dukanti only',
        },
        messages: {
          applied: 'Header and navigation applied to the draft.',
          reset: 'Header and navigation reset to the default.',
        },
        errors: {
          invalidNavigation:
            'These header and navigation settings were not applied. The previous draft stayed intact.',
          unsafeSocialUrl: 'Use a direct HTTPS Instagram or Facebook profile link.',
          missingNavigationContact:
            'This contact link needs a public store phone or WhatsApp number.',
          staleBuilder:
            'This storefront changed elsewhere. Refresh before changing header and navigation.',
          invalidAnnouncementText:
            'Announcement text must be plain text of at most 140 characters, without links.',
        },
      },
    },
    dashboard: {
      title: 'Dashboard',
      subtitle: 'Track store status and important tasks from one place',
      storeCreatedBanner: 'Your store was created successfully!',
      nextStep: 'Next step: Add your first product',
      addProduct: 'Add product',
      viewProducts: 'View products',
      storeUrl: 'Store URL',
      logoUploadWarning:
        'Your store was created successfully, but the logo upload failed. You can add it later in store settings.',
      metrics: {
        loadingValue: 'Loading',
        errorValue: 'Unavailable',
        loadingHelper: 'This summary is loading now',
        errorHelper: 'This summary could not be loaded',
        products: {
          title: 'Products',
          helper: '{{published}} published of {{count}} shown',
          ariaLabel: 'Open products',
        },
        orders: {
          title: 'New orders',
          helper: 'First page of incoming new orders',
          ariaLabel: 'Open new orders',
        },
        stock: {
          title: 'Active variants needing attention',
          helper: 'Active variants: {{out}} out of stock · {{low}} low stock',
          ariaLabel: 'Review active variant stock',
        },
        storefront: {
          title: 'Storefront',
          published: 'Live',
          draft: 'Draft',
          helper: 'Public storefront status',
          ariaLabel: 'Open storefront',
        },
      },
      recentOrders: {
        title: 'Latest new orders',
        viewAll: 'View all orders',
        loading: 'Loading orders',
        empty: 'No new orders right now',
        error: 'Could not load new orders',
      },
      stockAttention: {
        title: 'Active variants needing attention',
        viewStock: 'Review active stock',
        loading: 'Loading stock',
        empty: 'No active variants are out of stock or low on stock.',
        error: 'Could not load stock alerts',
      },
      nextStepCard: {
        title: 'Next step',
        description: 'Add a new product or review your current product list.',
      },
      snapshotNotice: 'These numbers are an operational snapshot from currently available data.',
    },
    shell: {
      tabTitle: '({{count}}) Dukanti Admin',
      tabTitleDefault: 'Dukanti Admin',
    },
    authBoundary: {
      pendingTitle: 'Waiting for admin access',
      pendingBody:
        'You are signed in, but this account does not have an active store membership yet.',
      unauthorizedTitle: 'No admin access',
      unauthorizedBody: 'This account does not have a Dukanti store membership.',
      missingTitle: 'This is not a merchant account',
      missingBody:
        'To manage a Dukanti store, create a merchant account and wait for team approval before creating a store.',
      signOut: 'Sign out',
    },
    barcode: {
      entry: {
        scanReceiving: 'Scan receiving',
        searchByBarcode: 'Search by barcode',
        scanBarcode: 'Scan barcode',
        productsAction: 'Barcode search',
        stockAction: 'Scan receiving',
      },
      scan: {
        title: 'Barcode product intake',
        subtitle: 'Scan, paste, or type a barcode to find the exact variant.',
        inputLabel: 'Barcode',
        inputPlaceholder: 'Scan or type barcode',
        submit: 'Search',
        search: { submit: 'admin.barcode.search.submit' },
        scanAnother: 'Scan another',
        ready: 'Ready to scan',
        normalizedHint: 'Leading and trailing scanner whitespace is ignored.',
        cameraOpen: 'Scan with camera',
        cameraTitle: 'Scan barcode with camera',
        cameraDescription:
          'Point the camera at the barcode and hold the phone steady. The scan runs automatically.',
        modeGroupLabel: 'Barcode type',
        modeStoreLabels: 'Store labels',
        modeStoreLabelsDescription:
          'For barcodes the store creates or prints internally. This is the most stable option.',
        modeRetailProducts: 'Retail products',
        modeRetailProductsDescription:
          'For manufacturer barcodes already printed on packaged products, such as EAN or UPC.',
        modeCompactCodes: 'Compact codes',
        modeCompactCodesDescription:
          'For short codes on small products. Requires steadier reads to reduce mistakes.',
        cameraVideoLabel: 'Live camera preview for barcode scanning',
        cameraIdle: 'Camera is ready.',
        cameraStatusStarting: 'Starting camera...',
        cameraStatusScanning: 'Looking for a barcode... Center it in the frame and hold steady.',
        cameraConsensusProgress: '{{count}} of {{required}} matching reads. Hold steady.',
        cameraDetected: 'Barcode detected.',
        cameraClose: 'Close scanner',
        cameraTryAgain: 'Try again',
        cameraSecureContextRequired:
          'Camera scanning on phones requires HTTPS. Open the secure local testing URL or type the barcode.',
        cameraUnsupported: 'Camera scanning is not available in this browser.',
        cameraPermissionDenied:
          'Camera access was blocked. Allow camera access or type the barcode.',
        cameraDecodeFailed: 'Could not start the camera scanner. Type the barcode or try again.',
      },
      result: {
        // Compatibility aliases for F-045 tests and older call sites.
        known: { title: 'admin.barcode.known.title' },
        unknown: { title: 'admin.barcode.unknown.title' },
        deletedRecoverable: { title: 'admin.barcode.deletedRecoverable.title' },
        knownTitle: 'Variant found',
        unknownTitle: 'Unknown barcode',
        deletedTitle: 'Deleted barcode match',
        invalidTitle: 'Invalid barcode',
        product: 'Product',
        variant: 'Variant',
        category: 'Category',
        price: 'Price',
        stock: 'Stock',
        status: {
          active: 'Active',
          disabled: 'Disabled',
          outOfStock: 'Out of stock',
          unpublished: 'Unpublished',
        },
        editProduct: 'Edit product',
        updateStock: 'Update stock',
        createProduct: 'Create product with this barcode',
        reuseDeleted: 'Reuse this barcode',
      },
      stock: {
        mode: {
          addReceived: 'Add received',
          setExact: 'Set exact',
        },
        quantityLabel: 'Received quantity',
        exactLabel: 'Exact stock',
        success: 'Stock updated',
        errors: {
          positiveAdd: 'Enter a quantity greater than zero.',
          nonNegative: 'Stock cannot be negative.',
          wholeNumber: 'Enter a whole number.',
          maxQuantity: 'Quantity must be {{max}} or less.',
        },
      },
      errors: {
        lookupFailed: 'Could not look up this barcode.',
        permissionDenied: 'You do not have permission to use this barcode.',
        networkFailed: 'Connection failed. Try again.',
        unknown: 'Something went wrong while looking up the barcode.',
      },
    },
    offerCampaigns: {
      title: 'Offer campaigns',
      subtitle: 'Group discounted products into a timed campaign.',
      addNew: 'New campaign',
      adsTab: 'Ads',
      campaignsTab: 'Campaigns',
      emptyTitle: 'No offer campaigns yet',
      emptyDescription: 'Create a campaign from existing sale items.',
      visibilityNotice:
        'Storefront shows up to 3 live campaigns after categories, ordered by the soonest end time.',
      publicLink: 'Offer link',
      copyLink: 'Copy link',
      editCampaign: 'Edit {{title}}',
      dateRange: '{{start}} to {{end}}',
      toasts: {
        created: 'Campaign created.',
        updated: 'Campaign saved.',
        deleted: 'Campaign deleted.',
        linkCopied: 'Link copied.',
      },
      form: {
        pageTitleCreate: 'New offer campaign',
        pageTitleEdit: 'Edit offer campaign',
        back: 'Back to campaigns',
        titleLabel: 'Campaign title',
        titlePlaceholder: 'Example: Weekend offers',
        descriptionLabel: 'Short description',
        descriptionPlaceholder: 'Briefly describe the offer',
        startsAtLabel: 'Start time',
        endsAtLabel: 'End time',
        activeLabel: 'Campaign is active',
        sellablesLabel: 'Sale items',
        submitCreate: 'Create campaign',
        submitEdit: 'Save campaign',
      },
      picker: {
        searchLabel: 'Search sale items',
        searchPlaceholder: 'Product or option name',
        loading: 'Loading sale items...',
        empty: 'No eligible sale items.',
        selected: '{{count}} selected',
        defaultVariant: 'Default option',
        optionWithCategory: '{{category}}: {{variant}}',
        ineligible: 'No longer eligible',
      },
      status: {
        scheduled: 'Scheduled',
        live: 'Live',
        ended: 'Ended',
        inactive: 'Inactive',
      },
      delete: {
        title: 'Delete campaign?',
        description: 'Customers will no longer see this campaign.',
        confirm: 'Delete campaign',
      },
      errors: {
        titleRequired: 'Enter a campaign title.',
        titleTooLong: 'Title is too long.',
        descriptionTooLong: 'Description is too long.',
        titleInvalid: 'Hidden direction controls are not allowed.',
        invalidWindow: 'End time must be after start time.',
        emptySelection: 'Select at least one sale item.',
        ineligibleSellable: 'Some selected items are no longer eligible for the campaign.',
        itemLimitExceeded: 'Select up to 48 items per campaign.',
        duplicateSelection: 'The same item cannot be selected more than once.',
        permissionDenied: 'You do not have permission to manage this campaign.',
        notFound: 'Campaign not found.',
      },
    },
    promotionKit: {
      tabs: { kit: 'Promotion Kit' },
      title: 'Promotion Kit',
      subtitle: 'Prepare a link, message, QR poster, and story image for your store.',
      target: {
        label: 'What do you want to promote?',
        search: 'Search targets',
        loadMore: 'Load more',
        groups: {
          storeHome: 'Store home',
          product: 'Products',
          category: 'Categories',
          liveOfferCampaign: 'Live offers',
          salesPage: 'Sales page',
          reviewHighlight: 'Highlighted reviews',
        },
        unavailable: { targetUnavailable: 'This target is no longer available to share.' },
      },
      locale: { label: 'Message language' },
      actions: {
        copyUrl: 'Copy link',
        copyMessage: 'Copy message',
        openWhatsapp: 'Open WhatsApp',
        downloadPoster: 'Download poster',
        printPoster: 'Print poster',
        downloadStory: 'Download story',
      },
      preview: {
        whatsappTitle: 'WhatsApp message',
        urlTitle: 'Public link',
        posterTitle: 'QR poster',
        storyTitle: 'Story image',
      },
      analytics: {
        title: 'Link results',
        empty: 'No results for this link yet.',
        linkVisits: 'Attributed route visits',
        productViews: 'Product views',
        contactClicks: 'Contact clicks',
        addToCartClicks: 'Add-to-cart clicks',
        orderStartClicks: 'Order starts',
        countOverflowed: 'The count reached the technical maximum.',
      },
      empty: {
        noPublicTargets: 'Publish your store or add a public product to start.',
        noLiveOffers: 'There are no live offers right now.',
        noReviews: 'There are no visible text reviews to share.',
      },
      errors: {
        permissionDenied: 'You do not have permission to manage this store promotion kit.',
        targetUnavailable: 'This target is not available to share now.',
        linkUnavailable: 'Could not prepare the share link.',
        assetFailed: 'Could not prepare the image. Try again.',
        clipboardFailed: 'Could not copy. Copy the text manually.',
      },
      toasts: {
        urlCopied: 'Link copied',
        messageCopied: 'Message copied',
        posterDownloaded: 'Poster downloaded',
        storyDownloaded: 'Story downloaded',
      },
    },
    welcome: 'Welcome to Dukanti',
    home: {
      hero: 'Welcome to Dukanti',
      subhero: 'Start your online store today',
      signUpCta: 'Sign up',
      signInCta: 'Sign in',
    },
    nav: {
      subscribers: 'Subscribers',
      ariaLabel: 'Primary navigation',
      dashboard: 'Dashboard',
      settings: 'Settings',
      products: 'Products',
      categories: 'Categories',
      promotions: 'Marketing',
      billing: 'Billing',
      orders: 'Orders',
      prepareOrder: 'Prepare order',
      reviews: 'Reviews',
      couriers: 'Couriers',
      stock: 'Stock',
      storefront: 'Storefront',
      addProduct: 'Add product',
      addProductAria: 'Add new product',
      ordersShortcut: 'New orders',
      storeWorkspace: 'Store workspace',
      storeFallback: 'My store',
      accountFallback: 'Merchant account',
      storeSettings: 'Store settings',
      signOut: 'Sign out',
      signOutDialog: {
        title: 'Sign out?',
        body: 'You will need to sign in again before managing your store.',
        cancel: 'Stay signed in',
        confirm: 'Sign out',
      },
      openSidebar: 'Open menu',
      closeSidebar: 'Close menu',
      toggleSidebar: 'Toggle sidebar',
      sidebarTitle: 'Sidebar',
      sidebarDescription: 'Displays the admin navigation sidebar.',
      skipToContent: 'Skip to main content',
      languageSwitcher: 'Language',
      unsavedChanges: {
        title: 'You have unsaved changes',
        body: 'Leave anyway? Your edits will be lost.',
        stay: 'Stay',
        leave: 'Leave',
      },
    },
    billing: {
      title: 'Billing',
      subtitle: 'Choose the Dukanti plan for this store.',
      permissionDeniedBody:
        'Billing changes are limited to the store owner. Ask the owner to open this page or update the subscription.',
      currentPlan: {
        title: 'Current plan',
        none: 'No active subscription',
        pending: 'Waiting for PayPal confirmation',
      },
      interval: { monthly: 'Monthly', annual: 'Annual' },
      plan: {
        starter: { name: 'Starter' },
        growth: { name: 'Growth' },
        pro: { name: 'Pro' },
        priceMonthly: '{{price}} / month',
        priceAnnual: '{{price}} / year',
        activeProducts: '{{count}} active products',
        mediaStorage: '{{size}} media storage',
        aiImageEdits: '{{count}} AI image edits',
        promotionTools: { basic: 'Basic store-link sharing', full: 'Full Promotion Kit' },
        analytics: { basic: 'Basic analytics', advanced: 'Advanced analytics' },
        support: { standard: 'Standard support', priority: 'Priority support' },
      },
      actions: {
        subscribe: 'Subscribe',
        upgrade: 'Upgrade',
        downgrade: 'Downgrade',
        intervalChange: 'Change interval',
        cancel: 'Cancel subscription',
        retryPayment: 'Retry payment',
        updatePayment: 'Update payment',
        confirmRevision: 'Confirm change',
        continueApproval: 'Continue approval',
      },
      paypal: {
        loading: 'Loading PayPal approval...',
        closed:
          'The approval window was closed. No subscription was activated; choose PayPal or card again when ready.',
        pendingNotice:
          'PayPal approval starts here, but Dukanti activates access only after server confirmation.',
        selectedLabel: 'Selected plan',
        selectedTitle: 'Complete {{plan}} approval',
        selectedSummary: '{{plan}} plan at {{price}}',
        paymentMethods: 'Choose how to approve this subscription',
      },
      pilot: {
        subtitle:
          'Dukanti is running this store on a free launch pilot. No payment method is needed.',
        activeTitle: 'Pilot access is active',
        activeBody:
          'This store has Pro-level launch access until {{date}}. Payment plans are paused during the pilot.',
        paidActiveTitle: 'Platform access is active',
        paidActiveBody:
          'This store still has verified subscription access. Payment changes are paused during the launch pilot.',
        expiredTitle: 'Pilot access ended',
        expiredBody:
          'Store actions and the public storefront are paused until Dukanti extends access or enables billing.',
        windowTitle: '30-day pilot window',
        windowBody: 'Current pilot access ends on {{date}}.',
        storefrontTitle: 'Storefront engine',
        storefrontBody:
          'You can keep building, previewing, and publishing the storefront while pilot access is active.',
        storefrontCta: 'Open storefront builder',
        overageTitle: 'Usage needs attention',
      },
      usage: {
        activeProducts: 'Active products',
        mediaStorage: 'Media storage',
        aiEdits: 'AI image edits',
        noLimit: 'No paid capacity is currently available.',
      },
      diagnostics: {
        title: 'Billing diagnostics',
        environment: 'PayPal environment',
        lastSync: 'Last PayPal sync',
        empty: 'Not available',
        notStarted: 'No subscription yet',
      },
      toast: {
        activated: 'Subscription activated.',
        reconsentReady: 'PayPal approval is ready. Complete the approval to change plans.',
        cancelled: 'Cancellation was sent. Access remains through the paid-through window.',
        retryStarted: 'Payment retry started.',
      },
      status: {
        missing: 'Subscription required',
        configurationError: 'Billing unavailable',
        approvalPending: 'Approval pending',
        active: 'Active',
        grace: 'Past due',
        cancelledPendingPeriodEnd: 'Cancelled, access until period end',
        cancelled: 'Cancelled',
        limited: 'Limited',
        suspended: 'Suspended',
        expired: 'Expired',
      },
      warning: {
        pastDue: 'Payment failed. Access continues until {{deadline}}.',
        limited: 'Paid actions are blocked until billing is resolved.',
        overProductLimit: 'This store is over the active product limit.',
        overStorageLimit: 'This store is over the media storage limit.',
        overAiImageEdits: 'This store used all AI image edits for the period.',
      },
      dialog: {
        cancel: {
          title: 'Cancel subscription?',
          description:
            'Paid access stays available until {{date}}. Existing store data is not deleted.',
        },
        changePlan: {
          title: 'Change plan',
          planSummary: '{{plan}} - {{price}}',
          overLimitDescription:
            'Some current usage is above the target plan and new paid actions may be blocked after the change.',
          approvalDescription: 'PayPal approval is ready. Open PayPal to approve this plan change.',
          openPayPalApproval: 'Open PayPal approval',
        },
      },
      errors: {
        permissionDenied: 'Only the store owner can manage billing.',
        paypalUnavailable: 'PayPal is unavailable right now.',
        configurationMissing: 'Billing is not configured for this environment.',
        activationPending: 'PayPal approval is pending. Access is not active yet.',
        planMismatch: 'The PayPal plan did not match the Dukanti plan.',
        planAlreadyActive: 'This plan is already active.',
        reconsentRequired: 'PayPal approval is required to finish this change.',
        webhookUnverified: 'PayPal verification failed.',
        paymentPaused: 'Payments are paused while Dukanti runs the launch pilot.',
        retry: 'Billing action failed. Try again.',
      },
    },
    entitlements: {
      upgradePrompt: {
        title: 'Upgrade required',
        description: 'Upgrade the store plan to use this feature.',
      },
      promotionKitBlocked: 'Upgrade to Growth or Pro to use these promotion tools.',
      productLimitBlocked: 'The active product limit has been reached.',
      storageLimitBlocked: 'The media storage limit has been reached.',
      aiQuotaBlocked: 'The AI image edit quota has been used.',
      reasons: {
        missingSubscription: 'Choose a Dukanti plan before using paid features.',
        configurationError: 'Billing is not configured for this environment.',
        activationPending: 'PayPal approval is pending. Access is not active yet.',
        paymentRequired: 'Paid access is required for this action.',
        suspended: 'This subscription is suspended. Resolve billing before continuing.',
        expired: 'This subscription expired. Renew billing before continuing.',
        cancelled: 'This subscription was cancelled. Choose a plan to resume paid access.',
        productLimitReached: 'The active product limit has been reached.',
        storageLimitReached: 'The media storage limit has been reached.',
        aiQuotaExhausted: 'The AI image edit quota has been used.',
        planUpgradeRequired: 'Upgrade the store plan to continue.',
        permissionDenied: 'You do not have permission to use this paid feature.',
        ownerRequired: 'Only the store owner can manage billing.',
        paidBasisMissing: 'PayPal payment evidence is not available yet. Try again shortly.',
        environmentMismatch: 'PayPal sandbox/live settings do not match this billing database.',
      },
    },
    reviews: {
      title: 'Product reviews',
      emptyTitle: 'No reviews yet',
      emptyDescription: 'Verified customer reviews will appear here after orders are delivered.',
      loadMore: 'Load more',
      columns: {
        product: 'Product',
        rating: 'Rating',
        review: 'Customer review',
        visibility: 'Visibility',
        response: 'Store response',
        date: 'Date',
        actions: 'Actions',
      },
      filters: {
        product: 'Product',
        allProducts: 'All products',
        productSearch: 'Search reviewed products',
        previousProducts: 'Previous products',
        moreProducts: 'More reviewed products',
        visibility: 'Visibility',
      },
      visibility: {
        all: 'All',
        visible: 'Visible',
        hidden: 'Hidden',
        reasonRequired: 'Choose a hide reason',
        noteTooLong: 'Note must be 500 characters or fewer',
      },
      actions: {
        respond: 'Respond',
        editResponse: 'Edit response',
        removeResponse: 'Remove response',
        hide: 'Hide',
        unhide: 'Unhide',
        disabledForStaff: 'Only owners and admins can do this',
      },
      response: {
        title: 'Store response',
        placeholder: 'Write a short response',
        save: 'Save response',
        required: 'Write a short response',
        tooLong: 'Response must be 1000 characters or fewer',
      },
      hideReasons: {
        abusive: 'Abusive content',
        privateInfo: 'Contains private information',
        offTopic: 'Off topic',
        spam: 'Spam or repetitive',
        other: 'Other reason',
      },
      moderation: {
        hideTitle: 'Hide review',
        unhideTitle: 'Unhide review',
        reasonLabel: 'Hide reason',
        noteLabel: 'Internal note',
        notePlaceholder: 'Optional, visible to admins only',
        hiddenMeta: 'Hidden: {{reason}}',
      },
      errors: {
        permissionDenied: 'You do not have permission to modify reviews',
        loadFailed: 'Could not load reviews',
      },
    },
    createStore: {
      title: 'Create your store',
      subtitle: 'Enter your store details to get started',
      nameLabel: 'Store name',
      namePlaceholder: "e.g., Ahmed's Grocery",
      descriptionLabel: 'Store description',
      descriptionPlaceholder: 'Briefly describe your store and products',
      slugLabel: 'Store URL',
      slugPlaceholder: 'my-store',
      slugHint: 'Your store URL will be: {{slug}}.{{domain}}',
      slugAvailable: 'This URL is available',
      slugChecking: 'Checking\u2026',
      logoLabel: 'Store logo (optional)',
      logoHint: 'JPEG, PNG, or WEBP, max 2MB',
      logoRemove: 'Remove logo',
      logoUpload: 'Upload logo',
      submit: 'Create store',
      submitting: 'Creating store\u2026',
      welcomeBanner: 'Welcome! Check your email to verify your account.',
      errors: {
        nameRequired: 'Store name is required',
        nameTooLong: 'Store name must be 100 characters or less',
        descriptionRequired: 'Store description is required',
        descriptionTooLong: 'Description must be 500 characters or less',
        slugRequired: 'Store URL is required',
        slugTooShort: 'URL must be at least 3 characters',
        slugTooLong: 'URL must be 60 characters or less',
        slugInvalidFormat: 'URL must contain only lowercase letters, numbers, and hyphens',
        slugTaken: 'This URL is already taken',
        slugReserved: 'This URL is reserved',
        approvalRequired: 'Your merchant account must be approved before creating a store.',
        logoTooLarge: 'Image must be 2MB or less',
        logoInvalidType: 'File must be JPEG, PNG, or WEBP',
        logoUploadFailed: 'Logo upload failed. Try again.',
        networkFailed: 'Connection failed. Check your network.',
        unknown: 'Something went wrong. Try again.',
      },
    },
    signIn: {
      title: 'Admin sign in',
      subtitle: 'Enter your email and we will send a verification code.',
      emailLabel: 'Email',
      emailPlaceholder: 'merchant@example.com',
      passwordLabel: 'Password',
      codeLabel: 'Verification code',
      codeHint: 'Enter the 6-digit code.',
      requestCode: 'Send code',
      requestingCode: 'Sending...',
      sentMessage: 'If this email has admin access, it will receive a verification code.',
      verifyCode: 'Verify code',
      verifyingCode: 'Verifying...',
      changeEmail: 'Change email',
      resendCode: 'Send a new code',
      invitationOnly: 'Admin access is invitation-only.',
      showPassword: 'Show password',
      hidePassword: 'Hide password',
      submit: 'Send code',
      submitting: 'Sending...',
      noAccount: 'Admin access is invitation-only.',
      signUpLink: 'Back to sign in',
      errors: {
        emailRequired: 'Email is required',
        emailInvalid: 'Email is not valid',
        codeRequired: 'Verification code is required',
        codeInvalid: 'Enter a 6-digit code',
        invalidCode: 'The code is incorrect or expired',
        rateLimited: 'Too many attempts. Try again in a moment.',
        passwordRequired: 'Password is required',
        invalidCredentials: 'Invalid email or password',
        networkFailed: 'Connection failed. Check your network.',
        serviceUnavailable: 'The sign-in service is not responding. Try again in a moment.',
        unknown: 'Something went wrong. Try again.',
      },
    },
    publishStore: {
      ctaLabel: 'Publish store',
      ctaLabelSubmitting: 'Publishing\u2026',
      unpublishCtaLabel: 'Unpublish',
      unpublishCtaLabelSubmitting: 'Unpublishing\u2026',
      badge: {
        draft: 'Draft',
        published: 'Published',
      },
      draftCard: {
        title: 'Ready to launch your store?',
        description: 'Publish your store so customers can find it and browse your products.',
      },
      modal: {
        title: 'Publish your store?',
        description: 'Your store will be live at:',
        confirm: 'Confirm publish',
      },
      unpublishModal: {
        title: 'Take store offline?',
        description: "Customers will see a 'Store not available' page. You can re-publish anytime.",
        confirm: 'Unpublish',
      },
      publishedCard: {
        title: 'Your store is live',
        urlLabel: 'Your public URL',
        copy: 'Copy URL',
        copyAriaLabel: 'Copy public URL to clipboard',
        viewStore: 'View your store',
      },
      toasts: {
        published: 'Your store is live',
        unpublished: 'Store taken offline',
        urlCopied: 'Link copied',
      },
      errors: {
        noStockedProducts: 'Add at least one in-stock product before publishing.',
        noStockedProductsCta: 'Add a product',
        missingContactMethod: 'Add a contact method before publishing your store.',
        missingContactMethodCta: 'Go to store settings',
        missingLogo: 'Upload a store logo before publishing your store.',
        missingLogoCta: 'Upload logo in settings',
        subscriptionRequired: 'Choose and activate a Dukanti plan before publishing your store.',
        activeProductLimitReached:
          'Your current plan cannot publish this many products. Unpublish extra products or upgrade your plan.',
        // DEPRECATED: use common.errors.permissionDenied
        permissionDenied: 'You do not have permission to do that.',
        // DEPRECATED: use common.errors.storeNotFound
        storeNotFound: 'This store no longer exists. Refresh the page.',
        // DEPRECATED: use common.errors.networkFailed
        networkFailed: 'Connection failed. Check your network and try again.',
        copyFailed: 'Could not copy link. Select and copy manually.',
        // DEPRECATED: use common.errors.unknown
        unknown: 'Something went wrong. Try again.',
      },
    },
    storeSettings: {
      fulfillment: {
        saveFailed: 'Save failed. Your draft is retained.',
        saved: 'Fulfillment options saved',
        reload: 'Reload settings',
        conflict: 'Other changes were saved. Your draft is retained; reload settings to review.',
        noMethodsWarning: 'Customers cannot confirm orders until you configure a method.',
        pickupLimit: 'One pickup place is supported currently.',
        removeDescription: 'This will preserve previously accepted order details.',
        removePickup: 'Remove pickup place',
        addPickup: 'Add pickup place',
        pickupDetailsInvalid: 'Enter an address or instructions of 1–1000 characters.',
        pickupNameInvalid: 'Enter a name of 1–160 characters.',
        pickupDetails: 'Address or pickup instructions',
        pickupName: 'Pickup place name',
        feeInvalid: 'Enter an amount from ₪0 to ₪1000 with at most two decimal places.',
        fixedFeeHelp:
          'One fee for all destinations. Leave blank to disable delivery, or enter 0 for free delivery.',
        title: 'Delivery and pickup',
      },
      socialProfiles: {
        reload: 'Reload settings',
        forbidden: 'These settings are available only to store owners and admins.',
        title: 'Social profiles',
        help: 'These settings apply to your live store immediately. Template text and subscriptions require Publish.',
        provider: 'Platform',
        url: 'Profile URL',
        add: 'Add profile',
        remove: 'Remove profile',
        saved: 'Social profiles saved.',
        invalid: 'Enter a valid Instagram or Facebook profile URL.',
        stale: 'These settings changed in another session. Reload them and try again.',
        loadError: 'Social profiles could not be loaded.',
        saveError: 'Social profiles could not be saved. Try again.',
        empty: 'No social profiles added.',
        max: 'You can add up to four profiles.',
        instagram: 'Instagram',
        facebook: 'Facebook',
      },
      newsletterCollection: {
        title: 'Newsletter collection',
        description:
          'Collect consented email subscriptions through a supported published template. This does not send messages.',
        label: 'Allow newsletter subscriptions',
        enabledHint: 'Shown when your live template supports the newsletter.',
        disabledHint: 'Newsletter collection is off. Existing subscriptions are kept.',
        saving: 'Saving…',
        savedToast: 'Newsletter setting saved.',
        manageSubscribers: 'Manage subscribers',
        permissionHint: 'Only store owners and admins can change collection.',
        error: 'We could not save this setting. Try again.',
        subscriberTitle: 'Newsletter subscribers',
        export: 'Export CSV',
        suppress: 'Suppress subscription',
        empty: 'No subscriptions yet.',
        loadError: 'We could not load subscriptions.',
        suppressionError: 'We could not suppress this subscription.',
      },
      title: 'Store settings',
      subtitle: 'Update the store details and contact methods your customers see',
      fixBanner: 'Add a WhatsApp or phone number so you can publish your store.',
      whatsappLabel: 'WhatsApp number',
      phoneLabel: 'Phone number',
      countryLabel: 'Country',
      callingCodeLabel: 'Calling code',
      localPhoneLabel: 'Local phone number',
      whatsappLocalLabel: 'Local WhatsApp number',
      phoneLocalLabel: 'Local phone number',
      phonePlaceholder: '+972501234567',
      phoneHint: 'Include country code (e.g., +972 for Israel)',
      localPhonePlaceholderIL: '0501234567',
      internationalPhonePlaceholder: '+972501234567',
      phoneHintCountryFirst: 'Choose the country, then enter the number as used locally.',
      submit: 'Save',
      submitting: 'Saving…',
      details: {
        title: 'Store details',
        description: 'Update the name, description, and logo customers see.',
        nameLabel: 'Store name',
        namePlaceholder: "e.g., Ahmed's Grocery",
        descriptionLabel: 'Store description',
        descriptionPlaceholder: 'Briefly describe your store and products',
        descriptionCount: '{{count}}/{{max}}',
        localityLabel: 'Store locality',
        localityPlaceholder: 'Choose locality',
        localityLoading: 'Loading localities…',
        localityHint: 'The locality where your store is located.',
        slugLabel: 'Store URL',
        slugLockedWarning:
          'The store URL cannot be changed after creation so shared links keep working.',
        logoLabel: 'Store logo',
        logoHint: 'JPEG, PNG, or WEBP, up to 2MB',
        logoUpload: 'Upload logo',
        logoReplace: 'Replace logo',
        logoRemove: 'Remove logo',
        logoUndoRemove: 'Undo remove',
        submit: 'Save details',
        submitting: 'Saving details…',
      },
      manualApproval: {
        title: 'Manual order approval',
        description: 'Require a merchant review before new orders can be shipped or assigned.',
        checkboxLabel: 'Review new orders before fulfillment',
        enabledHint: 'New orders will wait for approval. Existing orders are unchanged.',
        disabledHint: 'New orders can move directly into fulfillment.',
        saving: 'Saving setting…',
        savedToast: 'Manual approval setting saved',
      },
      toasts: {
        saved: 'Saved',
        detailsSaved: 'Store details saved',
      },
      errors: {
        nameRequired: 'Store name is required',
        nameTooLong: 'Store name must be 100 characters or less',
        descriptionRequired: 'Store description is required',
        descriptionTooLong: 'Description must be 500 characters or less',
        localityRequired: 'Store locality is required',
        logoTooLarge: 'Image must be 2MB or less',
        logoInvalidType: 'File must be JPEG, PNG, or WEBP',
        logoUploadFailed: 'Logo upload failed. Try again.',
        whatsappInvalid: 'Enter a valid international number (e.g., +972501234567)',
        phoneInvalid: 'Enter a valid international number (e.g., +972501234567)',
        whatsappInvalidForCountry: 'WhatsApp number is not valid for the selected country',
        phoneInvalidForCountry: 'Phone number is not valid for the selected country',
        whatsappInternationalInvalid:
          'Enter a valid international WhatsApp number like +972501234567',
        phoneInternationalInvalid: 'Enter a valid international phone number like +972501234567',
        whatsappLocalTooLong: 'Local WhatsApp number is too long',
        phoneLocalTooLong: 'Local phone number is too long',
        whatsappInternationalTooLong: 'International WhatsApp number is too long',
        phoneInternationalTooLong: 'International phone number is too long',
        atLeastOneRequired: 'Provide at least one contact method',
        networkFailed: 'Connection failed. Check your network and try again.',
        unknown: 'Something went wrong. Try again.',
      },
    },
    // F-003 Phase 4a scaffolding: English copy is the source for all three
    // locales during the foundation pass. stitch-react fills in real
    // Arabic / Hebrew strings during Phase 4c (see F4d-4).
    products: {
      title: 'Products',
      subtitle: 'Track publication and availability for the products in this window.',
      addNew: 'Add product',
      limitNote: 'Showing the first 50 products',
      summary: {
        all: 'All products',
        published: 'Published',
        draft: 'Drafts',
        unavailable: 'Unavailable',
      },
      filters: {
        label: 'Filter products',
        all: 'All',
        published: 'Published',
        draft: 'Drafts',
        unavailable: 'Unavailable',
      },
      table: {
        columns: {
          product: 'Product',
          category: 'Category',
          price: 'Price',
          inventory: 'Inventory',
          publication: 'Publication',
          availability: 'Availability',
          actions: 'Actions',
        },
        inventory: {
          available: '{{available}} of {{inventory}} available',
        },
        variantSummary: {
          count: '{{count}} variants',
          active: '{{count}} active',
          fromPrice: 'From price',
          sale: 'Variant sale',
          disabled: 'Disabled variants',
          outOfStock: 'Out-of-stock variants',
        },
        availability: {
          available: 'Available',
          outOfStock: 'Out of stock',
        },
      },
      noResults: {
        title: 'No products in this filter',
        subtitle: 'Change the filter to view another slice of the first 50 products.',
      },
      empty: {
        title: 'Add your first product',
        subtitle: 'Your store is ready. Add a product to start selling.',
        cta: 'Add product',
      },
      badge: {
        draft: 'Draft',
        published: 'Published',
        outOfStock: 'Out of stock',
        onSale: 'On sale',
        discountPercent: '{{percent}}% off',
      },
      card: {
        inventoryLabel: '{{count}} in stock',
        editAction: 'Edit',
        deleteAction: 'Delete',
        actionsMenu: 'Product actions',
      },
      form: {
        title: {
          new: 'New product',
          edit: 'Edit product',
        },
        nameLabel: 'Product name',
        namePlaceholder: 'e.g., Extra virgin olive oil',
        categoryLabel: 'Category',
        categoryPlaceholder: 'Choose a category',
        categoryHint: 'Every product belongs to one active category.',
        categoryLoading: 'Loading categories...',
        noCategoriesTitle: 'Create a category first',
        noCategoriesDescription: 'Products need an active category before they can be saved.',
        noCategoriesCta: 'Manage categories',
        descriptionLabel: 'Description',
        descriptionPlaceholder: 'Briefly describe the product...',
        priceLabel: 'Price',
        priceHint: 'ILS — up to 2 decimal places',
        saleSectionTitle: 'Sale',
        saleEnabledLabel: 'Enable a sale price for this product',
        salePriceLabel: 'Sale price',
        salePriceHint: 'Must be lower than the regular price',
        currencyLabel: 'Currency',
        inventoryLabel: 'Inventory',
        inventoryHint: 'Enter 0 if the product is out of stock',
        barcodeLabel: 'Product barcode',
        barcodePlaceholder: 'e.g., ABC-12345',
        barcodeHint: 'Optional. Letters, numbers, and safe barcode symbols only.',
        barcodeCameraAriaLabel: 'Scan product barcode with camera',
        imagesLabel: 'Product images',
        imagesHint: 'JPEG, PNG, or WEBP — up to 5 images, 4 MB each',
        imageAdd: 'Add image',
        imageRemove: 'Remove image',
        imageMoveUp: 'Move image up',
        imageMoveDown: 'Move image down',
        imageDragHandle: 'Drag to reorder',
        imageRetry: 'Retry upload',
        imageUploading: 'Uploading image\u2026',
        imageSlotAlt: 'Product image {{index}}',
        imagePosition: 'Position {{current}}',
        modeSectionTitle: 'Selling mode',
        mode: {
          simple: {
            label: 'Simple product',
            hint: 'One price and one inventory count.',
          },
          options: {
            label: 'Product with options',
            hint: 'Sizes, colors, packages, or any combination.',
          },
        },
        variantsTitle: 'Options and variants',
        variantsHint: 'Add option values to generate the sellable variant rows automatically.',
        templates: {
          title: 'Start from a template',
          active: 'Selected template: {{template}}',
          selected: 'Selected',
          sizes: {
            label: 'Sizes',
            hint: 'Small, medium, large.',
            optionName: 'Size',
            valueSmall: 'Small',
            valueMedium: 'Medium',
            valueLarge: 'Large',
          },
          colors: {
            label: 'Colors',
            hint: 'Common color choices.',
            optionName: 'Color',
            valueBlack: 'Black',
            valueWhite: 'White',
            valueGreen: 'Green',
          },
          sizeColor: {
            label: 'Size and color',
            hint: 'Generate every size/color combination.',
          },
          packageQuantity: {
            label: 'Package quantity',
            hint: 'Single item or multipacks.',
            optionName: 'Package',
            valueSingle: 'Single',
            valuePack6: 'Pack of 6',
            valuePack12: 'Pack of 12',
          },
          supplement: {
            label: 'Supplement',
            hint: 'Strength and capsule count.',
            strengthOptionName: 'Strength',
            packageOptionName: 'Capsules',
            value500mg: '500mg',
            value1000mg: '1000mg',
            value30Capsules: '30 capsules',
            value60Capsules: '60 capsules',
          },
          volumeWeight: {
            label: 'Volume or weight',
            hint: 'Bottle, drink, or food sizes.',
            optionName: 'Amount',
            value250ml: '250ml',
            value500ml: '500ml',
            value1kg: '1kg',
          },
          custom: {
            label: 'Custom',
            hint: 'Build your own option values.',
            optionName: 'Option',
            valueOne: 'Value',
          },
        },
        addOption: 'Add option',
        removeOption: 'Remove option',
        optionGroupLabel: 'Option group {{number}}',
        optionNameLabel: 'Option name',
        optionNamePlaceholder: 'Size, color, package...',
        optionValuePlaceholder: 'Option value',
        addOptionValue: 'Add value',
        removeOptionValue: 'Remove value',
        variantColumn: 'Variant',
        variantPriceColumn: 'Price',
        variantSaleColumn: 'Sale',
        variantInventoryColumn: 'Inventory',
        variantBarcodeColumn: 'Barcode',
        variantActiveColumn: 'Active',
        variantImageColumn: 'Image',
        variantDefaultColumn: 'Default',
        variantImageNone: 'Default image',
        variantImageUnavailable: 'Upload product images before assigning one.',
        variantSaleToggle: 'Sale',
        variantEmpty: 'Add at least one named option value to generate variants.',
        variantUnnamed: 'Variant {{number}}',
        variantPriceAriaLabel: 'Price for {{variant}}',
        variantSaleToggleAriaLabel: 'Enable sale for {{variant}}',
        variantSalePriceAriaLabel: 'Sale price for {{variant}}',
        variantInventoryAriaLabel: 'Inventory for {{variant}}',
        variantBarcodeAriaLabel: 'Barcode for {{variant}}',
        variantBarcodeCameraAriaLabel: 'Scan barcode for {{variant}} with camera',
        variantActiveAriaLabel: 'Activate {{variant}}',
        variantImageAriaLabel: 'Image for {{variant}}',
        variantDefaultAriaLabel: 'Make {{variant}} the default variant',
        submitCreate: 'Save product',
        submitEdit: 'Save changes',
        submitting: 'Saving\u2026',
        cancel: 'Cancel',
        back: 'Back to products',
      },
      delete: {
        title: 'Delete this product?',
        description:
          'Are you sure you want to delete <name>{{name}}</name>? This action cannot be undone.',
        confirm: 'Delete',
        confirming: 'Deleting…',
        cancel: 'Cancel',
      },
      barcodeConfirm: {
        replaceConfirm: { title: 'admin.products.barcode.replaceConfirm.title' },
        clearConfirm: { title: 'admin.products.barcode.clearConfirm.title' },
        replaceTitle: 'Replace barcode?',
        replaceDescription: 'The old barcode will stop opening this variant after you save.',
        clearTitle: 'Clear barcode?',
        clearDescription: 'Staff will no longer find this variant by barcode after you save.',
        deletedTitle: 'Reuse deleted barcode?',
        deletedDescription:
          'This barcode belonged to a deleted product or variant. Confirm reuse to save it here.',
        oldBarcode: 'Old barcode',
        newBarcode: 'New barcode',
        confirm: 'Confirm',
        cancel: 'Cancel',
      },
      detail: {
        notFoundTitle: 'Product not found',
        notFoundDescription: 'It may have been deleted, or the link is incorrect.',
        notFoundCta: 'Back to products',
        backToList: 'Products',
        editAction: 'Edit',
        deleteAction: 'Delete',
        statusLabel: 'Status',
        priceLabel: 'Price',
        categoryLabel: 'Category',
        categoryMissing: 'No category assigned',
        inventoryInStock: '{{count}} in stock',
        inventoryLow: 'Only {{count}} left',
        inventoryOutOfStock: 'Out of stock',
        descriptionLabel: 'Description',
        galleryLabel: 'Product image gallery',
        thumbnailLabel: 'Image {{index}}',
        noImagesLabel: 'No images for this product',
        loadingLabel: 'Loading product\u2026',
      },
      errors: {
        nameRequired: 'Product name is required',
        nameTooLong: 'Product name must be 200 characters or less',
        nameInvalid: 'Product name contains invalid characters',
        descriptionInvalid: 'Description contains invalid characters',
        priceRequired: 'Price is required',
        priceInvalid: 'Price must be a positive number with at most 2 decimal places',
        salePriceInvalid: 'Sale price must be positive and lower than the regular price',
        inventoryInvalid: 'Inventory must be a non-negative whole number',
        categoryRequired: 'Choose a category for this product',
        imageTooLarge: 'Image is too large (max 2 MB). Pick a smaller file.',
        imageInvalidType: 'File must be JPEG, PNG, or WEBP',
        tooManyImages: 'A product can have at most 5 images',
        storageQuotaExceeded:
          'Store storage quota exceeded (50 MB limit). Delete some images to free up space.',
        imageUploadFailed: 'Image upload failed. Try again.',
        barcodeInvalid: 'Barcode contains unsupported characters.',
        barcodeTooLong: 'Barcode must be 80 characters or fewer.',
        barcodeDuplicate: 'This barcode is already used by another variant.',
        barcodeDeletedConflict: 'Confirm reuse of a barcode from a deleted variant.',
        barcodeChangedElsewhere: 'Barcode changed elsewhere. Refresh and try again.',
        barcodeReplaceConfirmationRequired: 'Confirm barcode replacement before saving.',
        barcodeClearConfirmationRequired: 'Confirm barcode clearing before saving.',
        activeOrderedOrders: 'Resolve incoming orders for this product before changing it.',
        // DEPRECATED: use common.errors.networkFailed
        networkFailed: 'Connection failed. Check your network.',
        notFound: 'Product not found',
        // DEPRECATED: use common.errors.permissionDenied
        permissionDenied: 'You do not have permission to do that',
        // DEPRECATED: use common.errors.unknown
        unknown: 'Something went wrong. Try again.',
      },
      variants: {
        errors: {
          optionNameRequired: 'Option name is required',
          optionValueRequired: 'Option value is required',
          duplicateOptionValue: 'Option value is duplicated',
          duplicateCombination: 'Variant combination is duplicated',
          tooManyOptions: 'You can add up to 3 options',
          tooManyValues: 'There are too many option values',
          tooManyVariants: 'You can create up to 100 variants',
          noActiveVariant: 'Product needs at least one active variant',
          imageMismatch: 'Variant image must belong to this product',
          cannotDeleteOptionValue: 'Cannot delete a value used by existing variants',
        },
      },
      toasts: {
        created: 'Product created',
        updated: 'Product updated',
        deleted: 'Product deleted',
        imagesPartialFailure: 'Product saved, but some images failed to upload',
      },
      // F-028 Per-Product Publish Toggle — values
      publish: {
        buttons: {
          publish: 'Publish',
          unpublish: 'Hide',
          publishing: 'Publishing\u2026',
          unpublishing: 'Hiding\u2026',
        },
        publishModal: {
          title: 'Publish this product?',
          description: 'This product will appear on your store immediately.',
          urlLabel: 'Customers will see it at:',
          confirm: 'Publish product',
        },
        unpublishModal: {
          title: 'Hide this product?',
          description:
            'Customers will no longer see this product on your store. You can publish it again anytime.',
          urlLabel: 'This URL will stop working:',
          confirm: 'Hide product',
          stay: 'Keep visible',
        },
        disabled: {
          storeNotPublished: 'Publish your store first.',
          storeNotPublishedCta: 'Go to dashboard',
          insufficientInventory: 'Add inventory to publish.',
          insufficientInventoryCta: 'Edit product',
          categoryRequired: 'Choose a category before publishing.',
          categoryRequiredCta: 'Edit product',
          categoryRequiredCreateCta: 'Create a category',
        },
        errors: {
          storeNotPublished: 'Publish your store first.',
          insufficientInventory: 'This product has no inventory. Add stock before publishing.',
          categoryRequired: 'Choose an active category before publishing this product.',
          permissionDenied: "You don't have permission to publish this product.",
          notFound: 'This product no longer exists.',
          networkFailed: 'Network error. Please try again.',
          unknown: 'Something went wrong. Please try again.',
        },
        toasts: {
          published: 'Product published.',
          unpublished: 'Product hidden.',
        },
        card: {
          menuPublish: 'Publish',
          menuUnpublish: 'Hide',
        },
      },
    },
    categories: {
      title: 'Categories',
      subtitle: 'Organize your catalog into the categories customers will see on products.',
      addNew: 'Add category',
      loading: 'Loading categories...',
      empty: {
        title: 'Create your first category',
        subtitle: 'Categories keep product publishing strict and prepare your storefront catalog.',
        cta: 'Add category',
      },
      list: {
        order: 'Position {{order}}',
      },
      actions: {
        edit: 'Edit {{name}}',
        delete: 'Delete {{name}}',
        moveUp: 'Move {{name}} up',
        moveDown: 'Move {{name}} down',
      },
      form: {
        title: {
          create: 'Add category',
          edit: 'Edit category',
        },
        description: {
          create: 'Name the category and optionally add a public image and description.',
          edit: 'Update the category name, image, or public description.',
        },
        nameLabel: 'Category name',
        namePlaceholder: 'e.g., Olive oil',
        publicDescription: {
          legend: 'Public descriptions',
          help: 'Write short optional plain text shown to shoppers when category descriptions are enabled.',
          arLabel: 'Arabic description',
          heLabel: 'Hebrew description',
          enLabel: 'English description',
          placeholder: 'e.g., Fresh picks from the store',
        },
        mediaUpload: 'Upload image',
        mediaReplace: 'Replace image',
        mediaRemove: 'Remove image',
        mediaHint: 'JPEG, PNG, WEBP, or GIF, max 2 MB',
        mediaPreviewAlt: 'Category image preview',
        submitCreate: 'Create category',
        submitEdit: 'Save changes',
      },
      delete: {
        title: 'Delete this category?',
        description: 'Delete {{name}} from your store categories.',
        confirm: 'Delete category',
        assignedProductsRecovery: 'Reassign products in this category before deleting it.',
      },
      toasts: {
        created: 'Category created',
        updated: 'Category updated',
        deleted: 'Category deleted',
        reordered: 'Category order updated',
      },
      errors: {
        nameRequired: 'Category name is required',
        nameTooLong: 'Category name must be 80 characters or less',
        nameInvalid: 'Category name contains invalid characters',
        descriptionTooLong: 'Category description must be 240 characters or less per language',
        descriptionInvalid:
          'Category description must be plain text without links, formatting, or hidden characters',
        nameDuplicate: 'A live category with this name already exists',
        imageInvalidType: 'File must be JPEG, PNG, WEBP, or GIF',
        imageTooLarge: 'Image is too large (max 2 MB). Pick a smaller file.',
        imageUploadFailed: 'Category image upload failed. Try again.',
        storageQuotaExceeded:
          'Store storage quota exceeded (50 MB limit). Delete some images to free up space.',
        assignedProducts: 'This category is still assigned to products.',
        permissionDenied: 'You do not have permission to manage this category.',
        notFound: 'This category no longer exists.',
        reorderInvalid: 'Category order changed. Refresh and try again.',
      },
    },
    promotionalAds: {
      title: 'Storefront ads',
      subtitle: 'Add promotional images that appear at the top of the store page.',
      addNew: 'Add ad',
      legacyNotice:
        'Your store now uses a design template, so these ads no longer appear on your storefront. To change banners and images, open <storefrontLink>Storefront</storefrontLink> and turn on <editMode>Edit mode</editMode>. These ads are kept here for reference.',
      readOnlyStaffNotice: 'Only store owners and admins can change ads.',
      accessLoadError: "We couldn't load your ads settings. Check your connection and try again.",
      marketingTabsLabel: 'Marketing sections',
      empty: {
        title: 'No ads yet',
        subtitle: 'Start with one promotional image for the top of your storefront.',
      },
      list: {
        untitled: 'Untitled ad',
      },
      form: {
        title: {
          create: 'Add ad',
          edit: 'Edit ad',
        },
        mediaLabel: 'Ad image',
        mediaHint: 'WEBP, JPEG, PNG, or GIF, max 4 MB. A ratio near 3:1 works best.',
        titleLabel: 'Title',
        titlePlaceholder: 'Example: Weekend sale',
        altLabel: 'Alt text',
        altPlaceholder: 'Describe what appears in the image',
        back: 'Back to ads',
        pageTitleCreate: 'Create promotional ad',
        pageTitleEdit: 'Edit promotional ad',
        destinationLabel: 'External link',
        destinationPlaceholder: 'https://example.com',
        activeLabel: 'Show ad on storefront',
        submitCreate: 'Create ad',
        submitEdit: 'Save changes',
      },
      destination: {
        title: 'Destination',
        description: 'Choose what opens when shoppers tap this ad.',
        typeLabel: 'Destination type',
        none: 'No link',
        noneDescription: 'Show the image without navigation.',
        sales: 'Sales page',
        salesDescription: "Open this store's sale products.",
        category: 'Category',
        categoryDescription: 'Send shoppers to a public category.',
        product: 'Product',
        productDescription: 'Send shoppers to a product page.',
        external: 'Instagram or Facebook',
        externalDescription: 'Open a trusted social page or post.',
        externalLabel: 'Instagram or Facebook URL',
        externalPlaceholder: 'https://www.instagram.com/store-name',
        categorySearchLabel: 'Search categories',
        categorySearchPlaceholder: 'Category name',
        productSearchLabel: 'Search products',
        productSearchPlaceholder: 'Product name',
        productCategoryFilterLabel: 'Filter products by category',
        productCategoryFilterButton: 'Filter: {{name}}',
        productCategoryFilterAll: 'All products',
        productCategoryFilterSearchLabel: 'Search product categories',
        productCategoryFilterSearchPlaceholder: 'Search categories',
        categoryEmpty: 'No matching categories',
        productEmpty: 'No matching products',
        categoryUnavailable: 'This category is unavailable on the storefront.',
        productUnavailable: 'This product is unavailable on the storefront.',
        selectedCategory: 'Selected category: {{name}}',
        selectedProduct: 'Selected product: {{name}}',
        clearSelection: 'Clear selection',
        summary: {
          noLink: 'No link',
          sales: 'Sales page',
          category: 'Category: {{value}}',
          product: 'Product: {{value}}',
          external: 'External: {{value}}',
          unavailable: 'Unavailable destination',
        },
      },
      pickers: {
        loadingCategories: 'Loading categories...',
        loadingProducts: 'Loading products...',
        retryCategories: 'Retry loading categories',
        retryProducts: 'Retry loading products',
        productStatus: {
          published: 'Published',
          draft: 'Draft',
        },
        availability: {
          inStock: 'In stock',
          outOfStock: 'Out of stock',
        },
      },
      actions: {
        edit: 'Edit {{title}}',
        delete: 'Delete {{title}}',
        moveUp: 'Move {{title}} up',
        moveDown: 'Move {{title}} down',
      },
      status: {
        active: 'Active',
        inactive: 'Inactive',
      },
      delete: {
        title: 'Delete this ad?',
        description: 'This ad will no longer appear on the storefront after deletion.',
        confirm: 'Delete ad',
      },
      toasts: {
        created: 'Ad created',
        updated: 'Ad updated',
        deleted: 'Ad deleted',
        reordered: 'Ad order updated',
      },
      errors: {
        mediaRequired: 'Upload an ad image',
        mediaInvalidType: 'File must be WEBP, JPEG, PNG, or GIF only',
        mediaTooLarge: 'Image is larger than 4 MB',
        mediaDimensionsInvalid: 'Image dimensions do not fit a storefront ad',
        titleTooLong: 'Title must be 80 characters or less',
        titleInvalid: 'Title contains unsupported control characters',
        altTooLong: 'Alt text must be 160 characters or less',
        altInvalid: 'Alt text contains unsupported control characters',
        destinationInvalid: 'Use a secure HTTPS URL',
        destinationTypeRequired: 'Choose a destination type',
        categoryRequired: 'Choose a category',
        productRequired: 'Choose a product',
        externalHttpsRequired:
          'Enter a valid Instagram or Facebook HTTPS URL without spaces or credentials',
        destinationTargetUnavailable: 'Choose a destination that is available on the storefront',
        tooManyAds: 'You can keep up to 10 ads',
        tooManyActiveAds: 'Only 5 ads can be active',
        storageQuotaExceeded: 'Store storage quota exceeded. Delete some images to free space.',
        permissionDenied: 'You do not have permission to manage this ad',
        notFound: 'This ad no longer exists',
        reorderInvalid: 'Ad order changed. Refresh and try again.',
      },
    },
    /** Epic #228 P5: the shared Admin DestinationEditor (aliases reuse promotionalAds / websiteTemplates copy). */
    destination: {
      pickCategory: 'Choose a category from the list.',
      pickProduct: 'Choose a product from the list.',
      pickCollection: 'Choose a collection.',
      finishToSave: 'Finish your choice to save',
      incompleteTitle: "This link isn't finished",
      incomplete: {
        category:
          'Choose a category or pick another destination. If you leave now, the link stays as it was.',
        product:
          'Choose a product or pick another destination. If you leave now, the link stays as it was.',
        collection:
          'Choose a collection or pick another destination. If you leave now, the link stays as it was.',
        external:
          'Enter an Instagram or Facebook link or pick another destination. If you leave now, the link stays as it was.',
      },
      leaveUnchanged: 'Leave without changing the link',
      externalRefused:
        "This link can't be used. Paste an Instagram or Facebook page or post address.",
      unavailableNext: 'Choose another item below, or pick a different destination.',
      labelMissing: 'Add a button label so shoppers can tap this link.',
      title: 'Destination',
      typeLabel: 'Destination type',
      none: 'No link',
      noneDescription: 'Nothing opens when shoppers tap it.',
      sales: 'Sales page',
      salesDescription: "Open this store's sale products.",
      category: 'Category',
      categoryDescription: 'Send shoppers to a public category.',
      product: 'Product',
      productDescription: 'Send shoppers to a product page.',
      external: 'Instagram or Facebook',
      externalDescription: 'Open a trusted social page or post.',
      storePage: 'Store page',
      storePageDescription: 'Open another page of your store.',
      collection: 'Collection',
      collectionDescription: 'Send shoppers to a product collection.',
      contact: 'Contact',
      contactDescription: 'Let shoppers message or call you.',
      more: 'More destinations',
      fewer: 'Fewer destinations',
      externalLabel: 'Instagram or Facebook URL',
      externalPlaceholder: 'https://www.instagram.com/store-name',
      externalHint: 'Only instagram.com and facebook.com links work.',
      externalRequired: 'Enter an Instagram or Facebook link',
      externalHttpsRequired: 'Link must start with https://',
      externalHostUnsupported: 'Only Instagram and Facebook links are supported',
      externalTooLong: 'Link is too long (256 characters max)',
      externalInvalid: "This link has characters that aren't allowed",
      invalid: 'Choose where this link goes.',
      unavailable: "This item is no longer available. Shoppers won't be sent anywhere.",
      unavailableBadge: 'Unavailable',
      selectedCategory: 'Selected category: {{name}}',
      selectedProduct: 'Selected product: {{name}}',
      selectedUnknown: 'Selected item',
      chooseCategory: 'Choose category',
      chooseProduct: 'Choose product',
      chooseCollection: 'Choose collection',
      categorySearchLabel: 'Search categories',
      categorySearchPlaceholder: 'Category name',
      productSearchLabel: 'Search products',
      productSearchPlaceholder: 'Product name',
      productCategoryFilterButton: 'Filter: {{name}}',
      productCategoryFilterAll: 'All products',
      productCategoryFilterSearchLabel: 'Search product categories',
      productCategoryFilterSearchPlaceholder: 'Search categories',
      categoryEmpty: 'No matching categories',
      productEmpty: 'No matching products',
      noCategories: 'No categories yet. Add one in Categories, or choose another destination.',
      noProducts: 'No products yet. Add one in Products, or choose another destination.',
      noCollections: 'No collections to choose yet.',
      loadingCategories: 'Loading categories...',
      loadingProducts: 'Loading products...',
      retryCategories: 'Retry loading categories',
      retryProducts: 'Retry loading products',
      inStock: 'In stock',
      outOfStock: 'Out of stock',
      storePageLabel: 'Page',
      contactLabel: 'Contact method',
      page: {
        home: 'Home',
        catalog: 'Products list',
        shop: 'Shop all',
        storeInfo: 'Store info',
      },
      method: {
        whatsapp: 'WhatsApp',
        phone: 'Phone',
      },
      summary: {
        noLink: 'No link',
        sales: 'Sales page',
        category: 'Category: {{value}}',
        product: 'Product: {{value}}',
        collection: 'Collection: {{value}}',
        external: 'External: {{value}}',
        unavailable: 'Unavailable destination',
        storePage: {
          home: 'Home',
          catalog: 'Products list',
          shop: 'Shop all',
          storeInfo: 'Store info',
        },
        contact: {
          whatsapp: 'WhatsApp',
          phone: 'Phone',
          email: 'Email',
        },
      },
    },
    couriers: {
      title: 'Couriers',
      count: '{{count}} couriers',
      create: 'Add courier',
      edit: 'Edit courier',
      active: 'Active',
      inactive: 'Inactive',
      empty: {
        title: 'No couriers yet',
        description: 'Add trusted couriers so orders can be assigned without copying details.',
      },
      nameLabel: 'Courier name',
      phoneLabel: 'Phone number',
      internalNotesLabel: 'Internal notes',
      actions: {
        create: 'Add courier',
        edit: 'Edit courier',
        activate: 'Activate courier',
        deactivate: 'Deactivate courier',
        delete: 'Delete courier',
      },
      columns: {
        name: 'Name',
        phone: 'Phone',
        status: 'Status',
        createdAt: 'Created',
        actions: 'Actions',
      },
      status: {
        active: 'Active',
        inactive: 'Inactive',
      },
      form: {
        title: {
          create: 'Add courier',
          edit: 'Edit courier',
        },
        description: 'Enter courier details used for assigning orders and sending delivery links.',
        nameLabel: 'Courier name',
        phoneLabel: 'Phone number',
        notesLabel: 'Internal notes',
        activeLabel: 'Courier is active',
        save: 'Save courier',
      },
      delete: {
        title: 'Delete courier?',
        description: 'This disables {{name}} and keeps previous delivery history for audit.',
      },
      toasts: {
        created: 'Courier created',
        updated: 'Courier updated',
        deleted: 'Courier disabled',
      },
      verification: {
        title: 'Courier verification',
        pending: 'Pending verification',
        approved: 'Verified',
        sendOtp: 'Send verification code',
        resendOtp: 'Resend code',
        enterOtp: 'Enter verification code',
        verify: 'Verify',
        sent: 'Code sent to {{phone}}',
        cooldown: 'You can resend after {{time}}',
        localDebugOtp: 'Local development code',
        localDebugOtpHelp: 'Local SMS is not sent to a real phone. Use this code to verify.',
      },
      portal: {
        copyLink: 'Copy portal link',
        shareLink: 'Share portal link',
        sendWhatsApp: 'Send on WhatsApp',
        revoke: 'Revoke link',
        regenerate: 'Regenerate link',
        generate: 'Generate link',
        generated: 'Courier portal link generated',
        unavailablePending: 'Available only for verified couriers',
        revoked: 'Courier portal link revoked',
        regenerated: 'Courier portal link regenerated',
        copied: 'Courier portal link copied',
        whatsappMessage:
          'Hi {{courierName}}, this is your delivery portal link for {{storeName}}: {{portalUrl}}\n\nPlease keep this link with you and do not forward it to anyone else.',
        safetyNote: 'Share this link only with this courier.',
        recovery: {
          title: 'Portal link is ready',
          description:
            'Your browser blocked automatic copying for {{courierName}}. The link is available here.',
          linkLabel: 'Portal link',
        },
        errors: {
          linkNotFound: 'No active link exists for this courier.',
          unverifiedCourier: 'Verify the courier before creating a link.',
          inactiveCourier: 'Activate the courier before creating a link.',
          networkFailed:
            'Could not prepare the courier portal link. Check your connection and try again.',
          copyFailed: 'Could not copy the courier portal link. Try again.',
        },
      },
      errors: {
        nameRequired: 'Courier name is required.',
        phoneInvalid: 'Enter a valid phone number with country code.',
        notesTooLong: 'Internal notes must be 500 characters or less.',
        phoneDuplicate: 'This phone number is already used by another courier.',
        permissionDenied: 'You do not have permission to manage couriers.',
        networkFailed: 'Could not update couriers. Check your connection and try again.',
        otpInvalid: 'The code is incorrect.',
        otpExpired: 'The code expired. Send a new code.',
        otpCooldown: 'Wait before resending the code.',
        tooManyAttempts: 'Too many attempts. Send a new code.',
        alreadyVerified: 'This courier is already verified.',
        smsUnavailable: 'Could not send the message. Try again.',
      },
    },
    orders: {
      fulfillment: {
        pickupOnly: 'This action is available for pickup orders only.',
        deliveryOnly: 'This action is available for delivery orders only.',
      },
      title: 'Orders',
      searchPlaceholder: 'Search by customer name, phone, or order ID',
      sort: {
        totalDesc: 'Total: high to low',
        totalAsc: 'Total: low to high',
        label: 'Sort',
        visibleLabel: 'Sort:',
        createdAtDesc: 'Newest first',
        createdAtAsc: 'Oldest first',
        subtotalDesc: 'Subtotal: high to low',
        subtotalAsc: 'Subtotal: low to high',
      },
      filter: {
        all: 'All',
        ordered: 'Ordered',
        shipped: 'Shipped',
        delivered: 'Delivered',
        cancelled: 'Cancelled',
        rejected: 'Rejected',
        awaitingApproval: 'Awaiting approval',
        groupLabel: 'Filter by status',
      },
      columns: {
        total: 'Total',
        customer: 'Customer',
        date: 'Date',
        items: 'Items',
        subtotal: 'Subtotal',
        status: 'Status',
        action: 'Action',
      },
      empty: {
        all: {
          title: 'No orders yet',
          subtitle: 'When customers place orders, they will appear here.',
        },
        ordered: 'No new orders',
        shipped: 'No shipped orders',
        delivered: 'No delivered orders',
        cancelled: 'No cancelled orders',
        rejected: 'No rejected orders',
        awaitingApproval: {
          title: 'No orders awaiting approval',
          subtitle: 'Pending review orders will appear here.',
        },
      },
      loadMore: 'Load more',
      loadingMore: 'Loading...',
      itemCount: '{{count}} items',
      viewOrder: 'View',
      viewOrderAria: 'Open order {{id}} for {{customer}}',
      urgentLabel: 'Urgent',
      badge: {
        ariaLabel: '{{count}} new orders',
        ariaLabelCapped: '99+ new orders',
      },
      prepare: {
        title: 'Prepare order',
        subtitle: 'Create an order from current store inventory without opening the storefront.',
        openCta: 'Prepare order',
        backToOrders: 'Back to orders',
        steps: {
          items: 'Items',
          delivery: 'Delivery',
          review: 'Review',
        },
        picker: {
          title: 'Choose products',
          searchLabel: 'Search inventory',
          searchPlaceholder: 'Product, option, or barcode',
          categoryLabel: 'Category',
          categoryAll: 'All categories',
          categoryLoading: 'Loading categories...',
          loading: 'Searching inventory...',
          empty: 'No matching inventory items.',
          error: 'Could not load inventory. Try again.',
          capped: 'Showing first {{count}} products. Search to narrow the list.',
          add: 'Add',
          selected: 'Selected',
          draft: 'Unpublished',
          barcode: 'Barcode',
          stock: '{{count}} in stock',
          outOfStock: 'Out of stock',
          inactive: 'Inactive',
          unavailable: 'Unavailable',
        },
        lines: {
          title: 'Selected items',
          empty: 'Add at least one item to prepare an order.',
          product: 'Product',
          quantity: 'Quantity',
          unitPrice: 'Unit price',
          lineTotal: 'Line total',
          remove: 'Remove item',
          subtotal: 'Subtotal',
          quantityWhole: 'Enter a whole quantity greater than zero.',
          quantityStock: 'Only {{count}} in stock.',
        },
        delivery: {
          title: 'Delivery details',
          customerName: 'Customer name',
          customerNameOptional: 'Customer name (optional)',
          customerNamePlaceholder: 'Recipient name',
          phoneCountry: 'Country',
          phoneNumber: 'Phone number',
          phonePlaceholder: '050 123 4567',
          locality: 'City or village',
          localityPlaceholder: 'Search locality',
          localityEmpty: 'No localities found.',
          addressLine1: 'Address or delivery description',
          addressLine1Optional: 'Address or delivery description (optional)',
          addressLine1Placeholder: 'Street, house, landmark, or delivery instructions',
          notes: 'Order notes',
          notesPlaceholder: 'Optional notes for the store or courier',
        },
        review: {
          title: 'Review order',
          deliverySummary: 'Delivery summary',
          noPaymentNotice: 'No online payment is collected for this prepared order.',
          create: 'Create order',
          creating: 'Creating order...',
          editItems: 'Edit items',
          editDelivery: 'Edit delivery',
          requirementsTitle: 'Complete these before creating the order:',
        },
        success: {
          created: 'Prepared order created.',
        },
        errors: {
          noLines: 'Add at least one item.',
          quantityInvalid: 'Enter whole quantities greater than zero and within stock.',
          insufficientStock: 'Some selected items no longer have enough stock.',
          deletedProduct: 'Some selected products are no longer available.',
          inactiveVariant: 'Some selected variants are inactive.',
          variantUnavailable: 'Some selected items are no longer available.',
          customerNameRequired: 'Enter the customer name.',
          phoneRequired: 'Enter the customer phone number.',
          phoneInvalid: 'Enter a valid phone number with country code.',
          localityRequired: 'Choose a city or village.',
          localityInvalid: 'Choose an active city or village.',
          addressRequired: 'Enter an address or delivery description.',
          addressTooLong: 'Address or delivery description is too long.',
          unauthorized: 'You do not have permission to create this order.',
          networkFailed: 'Connection failed. Try again.',
          unknown: 'Could not create the prepared order.',
        },
      },
      shippingLabel: {
        open: 'Print shipping label',
        title: 'Shipping label',
        backToOrder: 'Back to order',
        print: 'Print',
        printAria: 'Print the shipping label for this order',
        previewHeading: 'Label preview',
        loading: 'Loading label...',
        notFoundTitle: 'Could not open label',
        notFoundDescription: 'The order may not exist or you may not have access to it.',
        manualApprovalBlockedTitle: 'Approval required first',
        manualApprovalBlockedDescription: 'Approve this order before printing a shipping label.',
        retry: 'Retry',
        fields: {
          order: 'Order',
          package: 'Package',
          packageTotal: 'Package total',
          batch: 'Delivery batch',
          recipient: 'Recipient',
          phone: 'Phone',
          destination: 'Destination',
          address: 'Address',
          deliveryInformation: 'Delivery information',
          preciseLocation: 'Precise location',
          sender: 'Sender',
          senderPhone: 'Sender phone',
          notes: 'Delivery notes',
          printedAt: 'Printed',
        },
        noCourierAssignment: 'No courier assigned to this order',
        singleOrderNotice: 'This label is for this order only.',
      },
      courierAssignment: {
        title: 'Courier assignment',
        assign: 'Assign courier',
        bulkAssign: 'Assign courier',
        unassign: 'Unassign courier',
        bulkUnassign: 'Unassign courier',
        unassigned: 'Courier unassigned.',
        assignedTo: 'Assigned to {{name}}',
        packageTag: 'Package {{tag}}',
        selectedCount: '{{count}} selected',
        noAutoShipNotice: 'Assigning a courier does not mark the order as shipped.',
        manualApprovalRequiredNotice: 'Approve this order before assigning a courier.',
        currentlyAssigned: 'Already assigned to {{name}}. Choose another courier to change it.',
        mixedExistingAssignments:
          'Some selected orders already have a courier. Assigning will replace their current courier.',
        notEligible: 'This order is not eligible for courier assignment.',
        unassignRequiresAssignedSelection:
          'Select only orders already assigned to a courier to unassign.',
        chooseCourier: 'Choose a courier',
        noActiveCouriers: 'This store has no active couriers.',
        noVerifiedCouriers: 'This store has no verified active couriers.',
        copyLink: 'Copy link',
        batchLinkTitle: 'Courier batch link',
        batchLinkTitleWithCount: 'Courier batch link · {{count}} packages',
        batchLinkDescription: 'This link includes all orders assigned in this courier batch.',
        linkCopied: 'Delivery link copied.',
        linkGenerated: 'Delivery link created.',
        sendWhatsapp: 'Send WhatsApp',
        resendWhatsApp: 'Send WhatsApp',
        resend: 'Resend link',
        regenerate: 'Regenerate link',
        regenerateLink: 'Regenerate link',
        revoke: 'Revoke link',
        revokeLink: 'Revoke link',
        revoked: 'Delivery link revoked.',
        whatsappMessage:
          'Hello {{courierName}}, {{storeName}} sent you a delivery list: {{manifestUrl}}',
        errors: {
          invalidStatus: 'Only ordered or shipped orders can be assigned.',
          manualApprovalRequired: 'Approve pending orders before courier assignment.',
          inactiveCourier: 'This courier is inactive.',
          unverifiedCourier: 'Verify the courier before assigning orders.',
          ordersUnavailable: 'Some selected orders are unavailable. Refresh and try again.',
          tooManyOrders: 'You can assign up to 50 orders at once.',
          linkExpired: 'No active delivery link was found. Regenerate the link.',
          assignmentConflict: 'Some assignments changed. Refresh and try again.',
          assignmentNotFound: 'Some selected orders are not assigned to a courier.',
          assignmentHasProof: 'Orders with delivery proof cannot be unassigned.',
        },
      },
      manualApproval: {
        pendingBadge: 'Awaiting approval',
        pendingCallout: 'Approve this order before shipping, rejecting, assigning, or printing.',
        pendingPickupCallout: 'Approve this order before marking it collected.',
        approve: 'Approve order',
        approving: 'Approving…',
        decline: 'Decline order',
        declining: 'Declining…',
        approveModalTitle: 'Approve order?',
        approvePrompt: 'This unlocks fulfillment actions for this order.',
        declineModalTitle: 'Decline order?',
        declinePrompt: 'This rejects the order, restores inventory, and notifies the customer.',
        approveSuccess: 'Order approved.',
        declineSuccess: 'Order declined.',
        badges: {
          pending: 'Awaiting approval',
          approved: 'Approved',
          declined: 'Declined',
        },
      },
      courierProof: {
        awaitingProof: 'Awaiting courier proof',
        awaitingConfirmation: 'Awaiting internal confirmation',
        confirmed: 'Internally confirmed',
        viewProof: 'View proof',
        confirm: 'Confirm delivery',
        errors: {
          invalidLink: 'This courier proof link is not valid.',
          notFound: 'This proof is no longer available.',
          alreadyConfirmed: 'This proof was already confirmed.',
          signedUrlFailed: 'Could not open the proof photo.',
        },
      },
      detail: {
        backToOrders: 'Back to orders',
        orderId: 'Order ID',
        orderIdLabel: 'Order ID:',
        placedOn: 'Placed on',
        placedOnLabel: 'Placed on:',
        customer: 'Customer',
        callButton: 'Call',
        whatsappButton: 'WhatsApp',
        contactWhatsappMessage: "Hello, I'd like to discuss order {{orderId}}",
        shippingAddress: 'Shipping address',
        deliveryInformation: 'Delivery information',
        copyAddress: 'Copy address',
        addressCopied: 'Copied!',
        preciseLocation: 'Precise delivery location',
        noPreciseLocation: 'No precise location shared',
        deliveryLocationShareTitle: 'Delivery location',
        deliveryLocationShareMessage: 'Customer delivery location:',
        shareLocationWhatsapp: 'Share via WhatsApp',
        openGoogleMaps: 'Open Maps',
        openWaze: 'Open Waze',
        copyLocationLink: 'Copy location link',
        locationCopied: 'Location link copied!',
        copyFailed: 'Copy failed',
        notes: 'Customer notes',
        lineItems: 'Items',
        viewProduct: 'View product',
        subtotal: 'Subtotal',
        notFound: 'Order not found',
        notFoundDesc: "This order does not exist or you don't have permission to view it",
        loading: 'Loading order...',
        errorRetry: 'Retry',
      },
      actions: {
        collectFailed: 'Could not confirm collection. Try again.',
        collectedSuccess: 'Order marked collected',
        collectDescription: 'Has the customer collected this order from the pickup place?',
        collectTitle: 'Confirm order collection',
        collecting: 'Marking collected',
        markCollected: 'Mark collected',
        ship: 'Mark as shipped',
        deliver: 'Mark as delivered',
        reject: 'Reject order',
        cancel: 'Cancel order',
        shipping: 'Shipping...',
        delivering: 'Delivering...',
        rejecting: 'Rejecting...',
        cancelling: 'Cancelling...',
        shipPrompt: "Mark as shipped? Do this when you've physically handed over the goods.",
        deliverPrompt: 'Mark as delivered? This closes the order.',
        rejectPrompt:
          'Reject this order? This will restore the ordered quantities to inventory and close the order.',
        cancelPrompt:
          'Cancel this order? This will restore the ordered quantities to inventory and notify the customer.',
        shipModalTitle: 'Mark as Shipped',
        deliverModalTitle: 'Mark as Delivered',
        rejectModalTitle: 'Reject Order',
        cancelModalTitle: 'Cancel Order',
        modalConfirm: 'Yes, proceed',
        modalCancel: 'No, go back',
        successToasts: {
          shipped: 'Order marked as shipped',
          delivered: 'Order marked as delivered',
          rejected: 'Order rejected',
          cancelled: 'Order cancelled',
        },
      },
    },
    // F-148 reviewed stock receiving (design §12). Merchant content (supplier names,
    // references, notes, product names) renders raw and never goes through these keys.
    receiving: {
      title: 'Receipts',
      subtitle: 'Record deliveries, review counts, then post stock in one step.',
      entryButton: 'Receive a delivery',
      notEnabled:
        'Receiving is not available for your account. Ask the store owner for stock access.',
      backToList: 'Back to receipts',
      paused: {
        banner:
          'Receiving is paused for this store. Receipts, downloads and corrections stay available.',
        draftsReadOnly:
          'Drafts are read-only while receiving is paused. You can still discard them.',
      },
      tabs: {
        drafts: 'Drafts',
        receipts: 'Receipts',
      },
      list: {
        empty: 'No open drafts. Start by receiving a delivery.',
        emptyReceipts: 'No posted receipts yet.',
        lines: '{{count}} lines',
        acceptedUnits: '{{count}} accepted units',
        updated: 'Updated {{date}}',
        loadMore: 'Load more',
        open: 'Open',
        draftLabel: 'Draft',
        receiptLabel: 'Receipt',
      },
      new: {
        title: 'New delivery',
        supplier: 'Supplier',
        supplierPlaceholder: 'Search or type a supplier name',
        supplierNew: 'Create supplier "{{name}}"',
        supplierExistingMatch: 'Existing supplier',
        supplierRequired: 'Choose or create a supplier.',
        reference: 'Shipment reference',
        referenceOptional: 'Optional',
        referenceHelp: "Leave blank and we'll create a system reference.",
        referenceTooLong: 'The reference must be 80 characters or fewer.',
        referenceControlCharacter: 'The reference contains an unsupported control character.',
        linkedReceipt: 'Linked receipt',
        note: 'Note',
        create: 'Create draft',
        creating: 'Creating…',
      },
      reference: {
        systemBadge: 'System reference',
        systemHelp: 'Generated by Dukanti because no shipment reference was entered.',
        merchantLabel: 'Shipment reference',
      },
      draft: {
        title: 'Delivery draft',
        lines: 'Lines',
        noLines: 'No lines yet. Scan a barcode, add a row or import a CSV.',
        addRow: 'Add row',
        scan: 'Scan',
        importCsv: 'Import CSV',
        downloadTemplate: 'Download template',
        save: 'Save',
        review: 'Review',
        discard: 'Discard draft',
        discardConfirm: 'Discard this draft? Nothing has been added to stock.',
        discarded: 'Draft discarded.',
        versionConflict: 'Changed in another window. Reload to see the latest version.',
        reload: 'Reload',
        errorSummary: 'Fix these before review',
        errorSummaryItem: 'Line {{line}}: {{message}}',
        combinePreview:
          'Combined: expected {{expected}}, accepted {{accepted}}, damaged {{damaged}}, missing {{missing}}',
        notOpen: 'This draft is no longer open.',
        openReceipt: 'Open receipt',
      },
      save: {
        saved: 'Saved',
        saving: 'Saving…',
        unsaved: 'Unsaved changes',
        failed: 'Save failed',
        retry: 'Retry save',
        fixFields: 'Fix the highlighted fields to save.',
      },
      line: {
        lineNo: 'Line {{line}}',
        variant: 'Variant',
        variantRef: 'Variant ID',
        barcode: 'Barcode',
        supplierCode: 'Supplier code',
        supplierItem: 'Supplier item',
        unitKind: 'Counting',
        unit: 'Units',
        pack: 'Packs',
        packs: 'Packs',
        unitsPerPack: 'Units per pack',
        packMath: '{{packs}} packs × {{unitsPerPack}} = {{total}} units',
        confirmPack: 'Confirm the units-per-pack conversion',
        expected: 'Expected',
        accepted: 'Accepted',
        damaged: 'Damaged',
        missing: 'Missing',
        note: 'Line note',
        remove: 'Remove line',
        selectVariant: 'Select variant',
        changeVariant: 'Change variant',
        inactiveBadge: 'Inactive',
        unresolvedScan: 'Scanned barcode not matched yet',
        noVariant: 'No variant selected',
        clearVariant: 'Clear selection',
        quantities: 'Quantities',
        identifiers: 'Identifiers',
      },
      picker: {
        title: 'Select a variant',
        search: 'Search by product name, option or barcode',
        noResults: 'No matching variants.',
        loadMore: 'Load more',
        exactBarcode: 'Exact barcode match',
        results: 'Search results',
      },
      conflict: {
        title: 'Identifiers point to different variants',
        description:
          'The barcode and the variant ID on this row resolve to different variants. Choose the one that was delivered.',
        byBarcode: 'By barcode',
        byRef: 'By variant ID',
        use: 'Use this variant',
      },
      duplicates: {
        title: 'Duplicate variant rows',
        description:
          '{{product}} — {{variant}} appears on lines {{lines}}. Combine them before review.',
        combine: 'Combine rows',
      },
      csv: {
        title: 'Import CSV',
        choose: 'Choose a CSV file',
        file: 'File',
        templateHelp: 'Use the template: UTF-8 CSV, comma separated, up to 500 rows.',
        import: 'Import rows',
        importing: 'Importing…',
        imported: '{{count}} rows imported.',
        duplicateWarning: 'This content matches an existing draft or receipt: {{references}}.',
        acknowledge: 'I checked and this is a different delivery.',
        utf8Guidance:
          'This file is not CSV UTF-8. In Excel choose "CSV UTF-8 (Comma delimited)" and upload again.',
        fileError: 'File error',
        readFailed: 'Could not read the file. Choose it again or try another file.',
        rowErrors: 'Row errors (first 50)',
        row: 'Row',
        column: 'Column',
        problem: 'Problem',
        errors: {
          empty_file: 'The file is empty.',
          too_large: 'The file is larger than 2 MiB.',
          unsupported_format:
            'Only CSV files are supported. Excel and PDF files cannot be imported.',
          utf16_encoding: 'The file is UTF-16. Save it as CSV UTF-8 and upload again.',
          encoding_not_utf8:
            'This file is not CSV UTF-8. In Excel choose "CSV UTF-8 (Comma delimited)" and upload again.',
          invalid_character:
            'The file contains a damaged character (�), probably from an earlier conversion. Re-export as CSV UTF-8.',
          semicolon_delimiter: 'The file uses semicolons. Export it comma separated.',
          missing_headers: 'Required template columns are missing from the header row.',
          unknown_header: 'The header row contains a column that is not in the template.',
          duplicate_header: 'The header row repeats a column.',
          too_many_rows: 'The file has more than 500 rows.',
          control_character: 'The cell contains an unsupported control character.',
          bad_quote: 'The row has an unterminated or misplaced quote.',
          too_many_fields: 'The row has more fields than the header.',
          not_integer: 'Enter a whole number without signs, decimals or separators.',
          out_of_range: 'The number is outside the allowed range.',
          invalid_barcode: 'The barcode is not valid.',
          invalid_uuid: 'The variant ID is not a valid Dukanti ID.',
          too_long: 'The text is too long.',
          pack_incomplete: 'Pack rows need both packs and units per pack.',
          pack_total_mismatch: 'Expected units must equal packs × units per pack.',
          partial_disposition:
            'Fill in accepted, damaged and missing together, or leave all three blank.',
          invalid_unit_kind: 'unit_kind must be "unit" or "pack".',
        },
      },
      review: {
        title: 'Review and post',
        description:
          'Only accepted units are added to stock. Damaged and missing units are recorded on the receipt.',
        lines: 'Stock changes',
        inventoryNow: 'Now',
        adds: '+{{count}}',
        after: 'After',
        totals: 'Totals',
        confirm: 'Post receipt',
        confirmTitle: 'Post this receipt?',
        confirmDescription:
          'Stock for {{lines}} variants increases by {{accepted}} accepted units. This cannot be undone; later fixes use corrections.',
        posting: 'Posting…',
        checking: 'Checking result…',
        notReady: 'Fix the blocking issues in the draft before posting.',
        backToDraft: 'Back to draft',
        posted: 'Receipt posted.',
        alreadyPosted: 'This draft was already posted. Opening the receipt.',
        similarRecent:
          'A receipt with the same lines was posted for this supplier on {{date}}. Make sure this is a different delivery.',
        openSimilar: 'Open the similar receipt',
      },
      receipt: {
        title: 'Receipt',
        postedBy: 'Posted by',
        postedAt: 'Posted at',
        supplier: 'Supplier',
        reference: 'Reference',
        linked: 'Linked to receipt',
        netAccepted: 'Net accepted',
        corrections: 'Corrections',
        noCorrections: 'No corrections.',
        download: 'Download discrepancy CSV',
        noEditNotice:
          'Posted receipts cannot be edited or deleted. Use a correction to reduce an accepted quantity.',
        lines: 'Lines',
        newLinked: 'Receive a follow-up delivery',
        inventoryChange: '{{before}} → {{after}}',
        correctedBy:
          '{{units}} units reclassified as {{reclassify}} by {{actor}} on {{date}}: {{reason}}',
      },
      export: {
        lineNo: 'Line',
        product: 'Product',
        variant: 'Variant',
        barcode: 'Barcode',
        supplierCode: 'Supplier code',
        supplierItemName: 'Supplier item',
        expected: 'Expected',
        accepted: 'Accepted',
        damaged: 'Damaged',
        missing: 'Missing',
        corrected: 'Corrected',
        netAccepted: 'Net accepted',
        note: 'Note',
      },
      correction: {
        action: 'Correct',
        title: 'Correct accepted quantity',
        description:
          'Reduces the accepted quantity of this line and removes the units from stock. The original receipt stays unchanged.',
        units: 'Units to remove',
        available: 'In stock now: {{count}}',
        remaining: 'Correctable on this line: {{count}}',
        reason: 'Reason',
        reasons: {
          accepted_count_error: 'Accepted count was wrong',
          damage_found_after_posting: 'Damage found after posting',
          wrong_variant_received: 'Wrong variant was received',
          other: 'Other',
        },
        reclassifyLabel: 'Record the removed units as',
        reclassify: {
          missing: 'Missing',
          damaged: 'Damaged',
        },
        note: 'Note',
        noteRequired: 'A note is required for "Other".',
        unitsInvalid: 'Enter a whole number between 1 and {{max}}.',
        increaseHint: 'To add units or record a different variant, receive a new linked delivery.',
        confirm: 'Apply correction',
        applying: 'Applying…',
        applied: 'Correction applied.',
        pausedAllowed: 'Corrections stay available while receiving is paused.',
      },
      barcodeHint: {
        notRecorded: 'Quick changes here are not recorded as delivery receipts.',
        openReceiving: 'Receive a delivery instead',
      },
      scan: {
        label: 'Scan to add',
        liveRegion: 'Scan results',
        added: '{{product}} — {{variant}}: expected {{expected}}, accepted {{accepted}}',
        incremented: '{{product}} — {{variant}}: expected {{expected}}, accepted {{accepted}}',
        packLine: 'This item is received in packs on line {{line}}. Adjust the pack count there.',
        duplicateLines: 'This variant is on lines {{lines}}. Combine the rows first.',
        unresolvedUnknown: 'Unknown barcode added as line {{line}}. Select a variant to continue.',
        unresolvedIneligible:
          'This barcode belongs to a deleted variant. Added as line {{line}} for review.',
        alreadyUnresolved: 'This barcode is already waiting on line {{line}}.',
        invalid: 'That is not a valid barcode.',
        lineLimit: 'A draft can hold at most {{max}} lines.',
        quantityLimit: 'Line {{line}} is at the maximum quantity.',
        lookupFailed: 'Lookup failed. Check your connection and scan again.',
      },
      issues: {
        VARIANT_REQUIRED: 'Select a variant for this line.',
        IDENTIFIER_UNKNOWN: 'No variant matches this identifier. Select one manually.',
        IDENTIFIER_AMBIGUOUS: 'More than one variant matches this identifier. Select one manually.',
        IDENTIFIER_CONFLICT:
          'The barcode and variant ID point to different variants. Resolve the conflict.',
        VARIANT_INELIGIBLE: 'This variant was deleted or is no longer available for receiving.',
        PACK_CONFIRMATION_REQUIRED: 'Confirm the units-per-pack conversion.',
        PACK_TOTAL_MISMATCH: 'Expected units must equal packs × units per pack ({{product}}).',
        QUANTITY_MISSING: 'Enter a quantity.',
        QUANTITY_OUT_OF_RANGE: 'Enter a whole number in the allowed range.',
        QUANTITY_SUM_MISMATCH:
          'Accepted + damaged + missing must equal expected ({{expected}}). Calculated accepted: {{calculated}}.',
        DUPLICATE_VARIANT: 'This variant appears on more than one line. Combine the rows.',
        INVENTORY_LIMIT_EXCEEDED: 'Adding this quantity would exceed the stock limit of 999,999.',
        NO_LINES: 'Add at least one line.',
        TOO_MANY_LINES: 'A draft can hold at most {{max}} lines (currently {{count}}).',
        REFERENCE_ALREADY_RECEIVED: 'A receipt with this supplier and reference already exists.',
        DUPLICATE_CONTENT_UNACKNOWLEDGED:
          'Acknowledge the duplicate content warning before posting.',
        RECEIVING_DISABLED: 'Receiving is paused for this store.',
        SIMILAR_RECENT_RECEIPT: 'A similar receipt was posted recently for this supplier.',
      },
      errors: {
        F148_NOT_FOUND: 'This draft or receipt was not found.',
        F148_DISABLED: 'Receiving is paused for this store.',
        F148_VALIDATION: 'Some values are not valid. Check the highlighted fields.',
        F148_DRAFT_EXISTS: 'An open draft with this supplier and reference already exists.',
        F148_DRAFT_VERSION_CONFLICT: 'Changed in another window. Reload to see the latest version.',
        F148_DRAFT_NOT_OPEN: 'This draft was already posted or discarded.',
        F148_REVIEW_STALE: 'The draft changed since you reviewed it. Review it again.',
        F148_DRAFT_INVALID: 'The draft has blocking issues. Fix them and review again.',
        F148_REFERENCE_ALREADY_RECEIVED:
          'A receipt with this supplier and reference already exists.',
        F148_IDEMPOTENCY_KEY_REUSED:
          'This request was already used with different values. Reload and try again.',
        F148_VARIANT_INELIGIBLE:
          'A variant in this draft was deleted or is no longer available. Nothing was posted.',
        F148_INVENTORY_LIMIT:
          'Posting would exceed the stock limit of 999,999 for a variant. Nothing was posted.',
        F148_STOCK_BUSY: 'Stock is busy. Your draft is safe; try again.',
        F148_CORRECTION_EXCEEDS_RECEIPT:
          'The correction exceeds the correctable quantity on this line.',
        F148_CORRECTION_EXCEEDS_AVAILABLE: 'Not enough units in stock for this correction.',
        F148_UNKNOWN: 'Something went wrong. Your draft is safe; try again.',
      },
    },
    stock: {
      worklist: {
        scopeLabel: 'Variant scope',
        scopeActive: 'Active variants',
        scopeAll: 'All variants',
        scopeSummary: 'Totals for {{scope}}',
        scopeHelp: 'Counts cover the entire scope, independent of search and stock filter.',
        variants: 'Variants',
        outCount: 'Out of stock',
        lowCount: 'Low stock',
        available: 'Available to sell',
        availableHelp: 'Recorded units available to sell, not a physical count.',
        lowRule: 'Low stock: 1–5 units',
        defaultVariant: 'Default variant',
        inactive: 'Inactive',
        unpublished: 'Unpublished product',
        barcode: 'Barcode',
        search: 'Search by name, options or barcode',
        searchTooLong: 'Use at most 100 characters.',
        results: 'Matching variants: {{total}} · Loaded: {{loaded}}',
        viewProduct: 'View product',
        viewProductLabel: 'View product: {{product}}, {{variant}}',
        backToStock: 'Back to stock',
        loadMore: 'Load more',
        loadingMore: 'Loading more',
        end: 'End of these results',
        clear: 'Clear search and filters',
        lastChecked: 'Last successful check: {{time}}',
        paused:
          'Automatic refresh pauses while reviewing multiple pages. Refresh for current data.',
        changed: 'Stock may have changed. Refresh to review current results.',
        restarted: 'The list refreshed from the beginning.',
        loadFailed: 'Could not load stock. Try again.',
        refreshFailed: 'Could not refresh. Showing last loaded data.',
        pageFailed: 'Could not load more. Your loaded results remain.',
        accessDenied: 'Stock access for this store is unavailable.',
        invalidQuery: 'This search could not be used. Clear it or refresh the list.',
        noVariants: 'No variants to show in this store.',
        noActive: 'No active variants. You can view all variants.',
        noMatches: 'No variants match this search and filters.',
        showAll: 'Show all variants',
        openProducts: 'View products',
        viewStockLabel: 'Review stock: {{product}}, {{variant}}',
      },
      title: 'Stock Dashboard',
      refresh: 'Refresh',
      refreshing: 'Refreshing...',
      loading: 'Loading stock...',
      summary: {
        heading: 'Stock Overview',
        totalSkus: 'Total SKUs',
        totalInventory: 'Total Inventory',
        lowStockCount: 'Low Stock',
        outOfStockCount: 'Out of Stock',
      },
      columns: {
        product: 'Product',
        inventory: 'Inventory',
        available: 'Available',
        status: 'Status',
        actions: 'Actions',
      },
      status: {
        healthy: 'In stock',
        lowStock: 'Low stock',
        outOfStock: 'Out of stock',
      },
      filter: {
        all: 'All',
        lowStock: 'Low stock',
        outOfStock: 'Out of stock',
        searchPlaceholder: 'Search products...',
      },
      empty: {
        title: 'No products',
        subtitle: 'Add products to your store to start tracking stock.',
        noResults: 'No products match the current filter.',
      },
      updateInventory: {
        trigger: 'Update inventory',
        triggerForProduct: 'Update inventory for {{name}}',
        modalTitle: 'Update inventory for {{name}}',
        currentInventory: 'Current inventory: {{count}}',
        explanation: 'This changes the total inventory available for sale.',
        newInventoryLabel: 'New inventory',
        availablePreview: 'Available after update: {{count}}',
        confirm: 'Update',
        confirming: 'Updating...',
        cancel: 'Cancel',
        errors: {
          negative: 'Inventory cannot be negative.',
          networkFailed: 'Request failed. Try again.',
          unknown: 'Something went wrong. Try again.',
        },
        toasts: {
          updated: 'Inventory updated',
        },
      },
      errors: {
        networkFailed: 'Could not load stock data. Check your connection and try again.',
        unknown: 'An error occurred loading stock.',
      },
    },
  },
  storefront: {
    beauty: {
      shopAll: 'Shop all',
      menu: 'Open store menu',
      closeMenu: 'Close store menu',
      search: 'Search this store',
      searchPlaceholder: 'Search products',
      closeSearch: 'Close search',
      account: 'My account',
      cart: 'Shopping cart',
      productCount: '{{count}} products',
      loadedCount: 'Showing {{loaded}} of {{total}}',
      loadMore: 'Load more',
      sort: 'Sort products',
      clearSearch: 'Clear search',
      noResults: 'No products match your search.',
      selectShade: 'Select shade',
      selectOptions: 'Select options',
      shadeCount: '{{count}} shades',
      shadeCount_one: '1 shade',
      moreDetails: 'More details',
      details: 'Product details',
      shippingReturns: 'Shipping & returns',
      relatedHeading: 'More from this category',
      reviews: 'Reviews',
      ratingSummary: 'Rated {{average}} out of 5 from {{count}} reviews',
      previous: 'Previous',
      next: 'Next',
      community: 'Our community',
      browseProduct: 'View {{name}}',
      shop: 'Shop',
      contact: 'Contact us',
      policies: 'Store policies',
      currency: 'Prices in Israeli shekels',
      newsletterInert: 'Preview only; subscriptions cannot be submitted here.',
      catalogLoadError: 'Products could not be loaded. Try again.',
      factsLoadError: 'Additional product details could not be loaded.',
      chooseOptions: 'Choose all options before adding to cart.',
      backToShop: 'Back to shopping',
      newsletterHeading: 'Stay in touch',
      newsletterBody: 'Subscribe for store updates.',
      galleryIndex: 'Image {{index}} of {{total}}',
      searchSubmit: 'Search',
      footerGroupToggle: 'Toggle {{group}}',
      communityProductUnavailable: 'Product unavailable',
    },
    website: {
      luxury: {
        categories: 'Explore categories',
        discoverCategory: 'Explore collection',
        bestSellers: 'Best Sellers',
        sale: 'Sale',
        newArrivals: 'New Arrivals',
        moreFromCategory: 'More from this category',
        backToCategory: 'Back to collection',
        productDetails: 'Product details',
        merchandisingError: 'Products could not be loaded. Try again.',
      },
      fashion: {
        demoWhiteShirt: 'White button shirt',
        demoBlackBag: 'Black shoulder bag',
        demoCollection: 'The essentials',
        viewCollection: 'View {{category}} collection',
        campaignLoadError: 'We could not load this collection.',
        collectionUnavailableTitle: 'This collection is unavailable right now',
        collectionUnavailableDescription: 'Return to the store to browse available products.',
        retry: 'Try again',
        previousProductImage: 'Previous image of {{name}}',
        nextProductImage: 'Next image of {{name}}',
        productImagePosition: 'Image {{index}} of {{count}} for {{name}}',
        sizeGuideOpen: 'View size guide',
        sizeGuideClose: 'Close size guide',
        newsletterCompany: 'Company',
        sizeGuideTitle: 'Size guide',
        description: 'Description',
        detailsCare: 'Details and care',
        deliveryReturns: 'Delivery and returns',
        generatedArtwork:
          'Original synthetic fashion illustrations generated for Dukanti templates.',
      },
      credits: {
        licenseScope: 'Original photographs and our adaptations use the licenses linked below.',
        title: 'Template photo credits',
        intro:
          'Photography used in Dukanti website templates and their previews. Merchants may replace these images with their own.',
        source: 'Original photograph',
        license: 'License',
        changes:
          'Changes: cropped, resized and compressed to WebP; also used in template preview screenshots.',
      },
      updating: {
        reload: 'Reload this store',
        reloadFailed: 'This store still needs newer code. Try again later.',
        title: 'This store is being updated',
        body: 'Please check back shortly.',
        browse: 'Browse Dukanti',
      },
      skipToContent: 'Skip to content',
      nav: {
        label: 'Store navigation',
        home: 'Home',
        shop: 'Shop',
        about: 'About',
        cart: 'Cart',
        account: 'Account',
        search: 'Search',
        openMenu: 'Open menu',
        closeMenu: 'Close menu',
      },
      hero: {
        previous: 'Previous slide',
        next: 'Next slide',
        slideStatus: 'Slide {{current}} of {{total}}',
      },
      media: {
        pause: 'Pause',
        play: 'Play',
      },
      categories: {
        heading: 'Shop by category',
      },
      products: {
        newArrivals: 'New arrivals',
        bestSellers: 'Best sellers',
        trending: 'Trending now',
        related: 'You may also like',
        viewAll: 'View all',
      },
      about: {
        title: 'About the store',
        delivery: 'Delivery',
        returns: 'Returns',
        contact: 'Contact',
      },
      product: {
        careTitle: 'Care',
        sizeGuideTitle: 'Size guide',
      },
      confirmation: {
        title: 'Order received',
        continue: 'Continue shopping',
      },
      footer: {
        powered: 'Powered by Dukanti',
      },
      canvas: {
        checkoutDisabled:
          'This is your real checkout page. Placing orders is turned off while you edit.',
        sampleProduct: 'Sample product',
        sampleCategory: 'Sample category',
        embeddedBlocked: "This page can't be shown here.",
        orderConfirmationNotice: 'After a customer orders, they see their order confirmation here.',
        seeOrderConfirmation: 'See the order confirmation page',
        notInEditor: "This page isn't part of your storefront editor.",
        backToStore: 'Back to the store home',
      },
    },
    storefrontEngine: {
      fallback: {
        sectionUnavailable: 'This section cannot be shown right now.',
      },
      liveCanvas: {
        empty: {
          title: 'Waiting for builder',
          body: 'This route renders only after the Admin Builder starts a trusted canvas session.',
        },
        loading: 'Waiting for builder',
        ready: 'Canvas ready',
        renderFailed: 'The canvas could not render.',
        invalidPayload: 'The canvas payload was invalid.',
        unavailable: 'Live canvas is unavailable.',
        controlsDisabled: 'Canvas controls are temporarily unavailable.',
        selectionLabel: 'Select storefront section',
        sections: {
          hero: 'Hero section',
          header: 'Store header',
          footer: 'Store footer',
          mobileNav: 'Mobile navigation',
          announcementBar: 'Announcement bar',
          benefits: 'Benefits',
          faq: 'FAQ',
          aboutStore: 'About store',
          testimonials: 'Testimonials',
          newsletterCta: 'Newsletter CTA',
          trustBadge: 'Trust badge',
          promotionalAds: 'Store ads',
          categories: 'Categories',
          liveOffers: 'Live offers',
          products: 'Products',
          trustSummary: 'Trust summary',
          editorialBanner: 'Editorial banner',
        },
        selection: {
          chromeLabel: 'Selected',
          aria: {
            section: 'Selectable section: {{section}}',
            selectedSection: 'Selected canvas section: {{section}}',
          },
        },
        edit: {
          shortLabel: 'Edit',
          editSection: 'Edit {{section}}',
          lockedShortLabel: 'Locked',
          lockedSection: '{{section}} is protected in the builder canvas',
        },
        textFields: {
          editTextField: 'Edit {{field}} in {{section}}',
          labels: {
            heroHeadline: 'Hero headline',
            heroEyebrow: 'Hero eyebrow',
            heroDescription: 'Hero description',
            heroPrimaryActionLabel: 'Hero primary action',
            contentHeading: 'Section heading',
            contentBody: 'Section body',
            contentBenefitTitle: 'Benefit title',
            contentBenefitBody: 'Benefit body',
            contentFaqQuestion: 'FAQ question',
            contentFaqAnswer: 'FAQ answer',
            contentAboutParagraph: 'About paragraph',
            contentTestimonialQuote: 'Testimonial quote',
            contentTestimonialName: 'Testimonial name',
            contentTestimonialContext: 'Testimonial context',
            contentNewsletterButton: 'Newsletter button',
            contentEditorialBannerButton: 'Banner button',
          },
        },
        richContentFields: {
          editRichContentField: 'Edit rich content for {{field}} in {{section}}',
          labels: {
            contentBenefitsBody: 'Benefits body',
            contentBenefitBody: 'Benefit body',
            contentFaqBody: 'FAQ intro',
            contentFaqAnswer: 'FAQ answer',
            contentAboutParagraph: 'About paragraph',
            contentTestimonialsBody: 'Testimonials intro',
            contentTestimonialQuote: 'Testimonial quote',
            contentNewsletterBody: 'Newsletter CTA body',
            contentEditorialBannerBody: 'Banner body',
          },
        },
        mediaFields: {
          replaceMediaField: 'Replace {{field}} in {{section}}',
          labels: {
            heroDesktop: 'Hero desktop image',
            heroMobile: 'Hero mobile image',
            trustBadgeImage: 'Trust badge image',
            editorialBannerDesktop: 'Banner desktop image',
            editorialBannerMobile: 'Banner mobile image',
          },
        },
        add: {
          shortLabel: 'Add',
          addAfterSection: 'Add after {{section}}',
        },
        lifecycle: {
          duplicate: 'Duplicate {{section}}',
          hide: 'Hide {{section}}',
          show: 'Show {{section}}',
          delete: 'Delete {{section}}',
          hiddenPlaceholder: '{{section}} is hidden in the draft.',
        },
        visibility: {
          deviceHiddenPlaceholder: '{{section}} is hidden for this preview size.',
        },
        heroSchedule: {
          always: 'Always active',
          active: 'Live now',
          upcoming: 'Scheduled',
          expired: 'Expired',
        },
        reorder: {
          handle: 'Drag to reorder {{section}}',
          moveUp: 'Move {{section}} before the previous section',
          moveDown: 'Move {{section}} after the next section',
          dropBefore: 'Drop before {{section}}',
          dropAfter: 'Drop after {{section}}',
          movedBefore: '{{section}} moved before {{target}}.',
          movedAfter: '{{section}} moved after {{target}}.',
        },
        inertAction: 'Shopper actions are disabled in the builder canvas.',
        aria: {
          route: 'Storefront builder canvas',
          selectedSection: 'Selected canvas section',
          selectedControls: 'Selected canvas controls for {{section}}',
        },
      },
    },
    preview: {
      banner: 'Preview mode',
      expired: 'This preview link expired.',
      notAvailable: 'Preview is not available.',
      noindexLabel: 'Hidden from search engines',
      unsupportedTitle: 'Preview page not available',
      unsupportedDescription:
        'This destination is not part of the safe preview yet. Continue from the preview home.',
      backToPreview: 'Back to preview home',
    },
    welcome: 'Welcome to Dukanti',
    shopNow: 'Shop now',
    offerCampaigns: {
      homeRailTitle: 'Live campaigns',
      liveBadge: 'Live offer',
      endsIn: 'Ends in {{time}}',
      endedTitle: 'This offer has ended',
      endedDescription: 'Check the store for other offers.',
      emptyTitle: 'No available products in this offer',
      emptyDescription: 'The products may be sold out or no longer be on sale.',
      variantLabel: 'Option: {{variant}}',
      homeCta: 'View offer',
      shareLabel: 'Share offer',
      meta: {
        title: '{{campaignTitle}} — {{storeName}}',
        description: 'A live limited-time offer from {{storeName}}.',
      },
    },
    courierManifest: {
      title: 'Delivery list',
      loading: 'Loading delivery list...',
      retry: 'Retry',
      error: {
        title: 'Could not load this delivery list',
        description:
          'Check your connection and try again. If it keeps failing, ask the store for a new link.',
      },
      batchCode: 'Batch {{displayCode}}',
      courierGreeting: 'For {{name}}',
      expiresAt: 'Expires {{expiresAt}}',
      orders: 'Delivery stops',
      orderCount: '{{count}} orders',
      orderLabel: 'Stop {{displayId}}',
      address: 'Address',
      customerPhone: 'Customer phone',
      googleMaps: 'Google Maps',
      waze: 'Waze',
      packageCount: '{{count}} package',
      packageBatchCount: '{{count}} delivery stops',
      notes: 'Customer notes',
      openMap: 'Open map',
      storeContact: 'Call store: {{phone}}',
      storeContactUnavailable: 'Store contact is unavailable',
      packageTag: 'Package {{tag}}',
      proof: {
        submit: 'Submit delivery proof',
        replace: 'Replace proof',
        submitted: 'Proof submitted',
        choosePhoto: 'Choose photo',
        chooseFromLibrary: 'Choose from library',
        takePhoto: 'Take photo',
        fileSizeKilobytes: '{{count}} KB',
        fileSizeMegabytes: '{{count}} MB',
        errors: {
          invalidType: 'Use a JPG, PNG, or WebP photo.',
          tooLarge: 'Photo must be 5 MB or smaller.',
          notShipped: 'This stop cannot be delivered from here.',
          alreadyConfirmed: 'The store already confirmed this delivery.',
          uploadFailed: 'Could not upload the proof photo. Try again.',
        },
      },
      invalid: {
        title: 'This delivery link is not available',
        body: 'The link may be expired, revoked, or incomplete. Ask the store for a new link.',
        description:
          'The link may be expired, revoked, or incomplete. Ask the store for a new link.',
      },
    },
    courierPortal: {
      title: 'Courier portal',
      invalid: {
        title: 'This courier portal link is not available',
        description: 'The link may be revoked or invalid. Ask the store for a new link.',
      },
      empty: {
        title: 'No packages ready right now',
        description: 'Shipped orders assigned to this courier will appear here.',
      },
      localities: {
        title: 'Localities',
        count: '{{count}} package',
      },
      locality: {
        back: 'Back to localities',
      },
      language: {
        label: 'Language',
        ar: 'Arabic',
        he: 'Hebrew',
        en: 'English',
      },
      device: {
        forgetAction: 'Forget this device',
        title: 'Remove this device?',
        description:
          'Use this if this is not your phone. This device will no longer open the portal unless the store sends the full link again.',
        confirm: 'Remove this device',
      },
      packageCount: '{{count}} package',
      customer: 'Customer',
      proof: {
        required: 'Delivery proof is required before marking this package delivered.',
        submitted: 'Delivery proof has been submitted and is waiting for store confirmation.',
        confirmed: 'Delivery proof has been confirmed by the store.',
      },
    },
    storeChrome: {
      announcementBar: {
        label: 'Store announcement',
      },
      labels: {
        storeHome: 'Store home',
        storeCategories: 'Categories',
        storeSales: 'Sales',
        bestSellers: 'Best sellers',
        trendingNow: 'Trending now',
        newArrivals: 'New arrivals',
        category: 'Category',
        cart: 'Cart',
        account: 'Account',
        orders: 'Orders',
        whatsapp: 'WhatsApp',
        phone: 'Phone',
        instagram: 'Instagram',
        facebook: 'Facebook',
      },
      footer: {
        quickLinks: 'Store links',
        socialLinks: 'Social',
      },
    },
    home: {
      nav: {
        label: 'Home navigation',
        home: 'Home',
        stores: 'Stores',
        sales: 'Sales',
        orders: 'Orders',
        account: 'Account',
      },
      storeNav: {
        home: 'Store',
        sales: 'Sales',
      },
      rootLanding: {
        eyebrow: 'Dukanti',
        title: 'Your local store link is ready when you are',
        subtitle:
          'Use the store link you received, or manage your customer profile and orders from here.',
        profile: 'Profile',
        openStore: 'Open your store',
      },
      hero: {
        badge: 'Shop local',
        title: 'Discover local stores',
        subtitle: 'Browse stores from your village and order directly from their owners.',
        browseStores: 'Browse stores',
        openYourStore: 'Open your store',
        fromProduct: 'From a published product',
        fromStore: 'From a published store',
      },
      products: {
        title: 'Products from local stores',
        empty: 'No published products yet.',
        error: 'Could not load products. Try again.',
        retry: 'Retry',
        viewProductLabel: 'View {{productName}}',
      },
      sales: {
        title: 'Sales and discounts',
        subtitle: 'Discounted products from local stores',
        empty: 'No sales available right now.',
        error: 'Could not load sales. Try again.',
        retry: 'Retry',
      },
      publishedStores: {
        title: 'Local stores',
        empty: 'No published stores yet.',
        error: 'Could not load stores. Try again.',
        retry: 'Retry',
      },
      featuredStores: {
        title: 'Featured stores',
        empty: 'No stores yet. Be the first to open yours!',
        error: 'Could not load stores. Try again.',
        retry: 'Retry',
        viewStore: 'Visit store',
        viewStoreLabel: 'Visit store — {{storeName}}',
        storeLogoAlt: '{{storeName}} logo',
      },
      howItWorks: {
        title: 'How it works',
        step1Title: 'Browse stores',
        step1Description: 'Discover local stores from your village and browse their products.',
        step2Title: 'Contact the seller',
        step2Description: 'Reach out directly to the store owner via WhatsApp or phone.',
        step3Title: 'Order and collect',
        step3Description: 'Complete your order and follow its details from your account.',
      },
      meta: {
        title: 'Dukanti',
        description: 'Customer profile and store links for Dukanti shoppers.',
      },
    },
    sale: {
      onSale: 'Sale',
      discountPercent: '{{percent}}% off',
      originalPrice: 'Original price {{price}}',
    },
    contactSeller: {
      prefilledMessage: "Hi! I'm interested in {{productName}}. Link: {{url}}",
      unavailable: 'Contact not available',
      modal: {
        title: 'How would you like to get in touch?',
        whatsapp: 'Open WhatsApp',
        phone: 'Call',
        close: 'Close',
      },
    },
    storeNotAvailable: {
      title: 'Store not available',
      description: 'This store is currently offline or does not exist.',
      backHome: 'Back to Dukanti',
    },
    storePlaceholder: {
      liveBadge: 'Live',
      comingSoon: 'Full store experience coming soon',
    },
    storeSales: {
      title: 'Sale items',
      subtitle: 'Discounted products from this store only.',
      emptyTitle: 'No sale items right now',
      emptyDescription: 'Check this store again later for new discounts.',
      error: 'Could not load sale items. Try again.',
      meta: {
        title: '{{storeName}} sale items',
        description: 'Browse discounted products from {{storeName}}.',
      },
    },
    storeCollections: {
      bestSellers: {
        title: 'Best Sellers',
      },
      trendingNow: {
        title: 'Trending Now',
      },
      newArrivals: {
        title: 'New Arrivals',
      },
      subtitle: 'Selected products from {{storeName}}',
      emptyTitle: 'No products in this collection yet',
      emptyDescription: 'Check back later as this store receives more activity.',
      meta: {
        title: '{{collectionName}} from {{storeName}}',
      },
    },
    customProductCollections: {
      subtitle: 'Selected products from {{storeName}}',
      emptyTitle: 'No products in this collection right now',
      emptyDescription: 'Check back later as this store updates its collection.',
      meta: {
        title: '{{collectionName}} from {{storeName}}',
        description: 'Browse {{collectionName}} from {{storeName}}.',
      },
    },
    storeCategoriesPage: {
      title: 'All categories',
      subtitle: 'Browse every live category from {{storeName}}',
      emptyTitle: 'No categories yet',
      emptyDescription: 'This store has not published categories with products yet.',
      error: 'Could not load categories. Try again.',
      meta: {
        title: '{{storeName}} categories',
        description: 'Browse all categories from {{storeName}}.',
      },
    },
    storeHeader: {
      homeAriaLabel: 'Back to {{storeName}}',
      storeLogoName: '{{storeName}} store',
      cartAriaLabel: '{{storeName}} cart',
    },
    // F-005 Phase 4a stub strings — final English copy lands in Phase 4c.
    storeHome: {
      header: {
        wordmark: 'dukanti',
        wordmarkAriaLabel: 'Dukanti — home',
      },
      languageSwitcher: 'Language',
      hero: {
        storeLogoAlt: '{{storeName}} logo',
      },
      conversionHero: {
        primaryCta: 'Shop now',
        categoriesCta: 'Browse categories',
        featuredProduct: 'From the store',
        visualFallback: 'A look at {{storeName}}',
        descriptionFallback: 'Selected products from {{storeName}} in one place.',
      },
      contact: {
        title: 'Direct contact with the store',
        description:
          'Choose WhatsApp or a phone call using the contact details published by the merchant.',
        whatsapp: 'WhatsApp',
        phone: 'Call',
        whatsappAriaLabel: 'Contact {{storeName}} on WhatsApp',
        phoneAriaLabel: 'Call {{storeName}}',
      },
      products: {
        title: 'Products',
      },
      sections: {
        hero: 'Storefront',
        contact: 'Direct contact',
        bestSellers: 'Best Sellers',
        trendingNow: 'Trending Now',
        newArrivals: 'New Arrivals',
        specialOffers: 'Special Offers',
        selectedProducts: 'Selected products',
        customCollection: 'Selected collection',
        sales: 'Sales',
        latestProducts: 'Latest products',
        availableNow: 'Available now',
        unavailable: 'Currently unavailable',
        allProducts: 'All products',
        viewAll: 'View all',
        productsUnavailableHint: 'These published products are not available to order right now.',
        emptyManualProducts: 'No selected products can be shown right now.',
        emptyCustomCollection: 'No products from this collection can be shown right now.',
        emptyAutomaticProducts: 'No eligible products can be shown right now.',
        railError: 'Could not load this section.',
        networkError: 'Could not load this section. Check your connection and try again.',
        unknownError: 'Could not load this section right now.',
      },
      newsletter: {
        contactTypeLabel: 'Contact method',
        email: 'Email',
        phone: 'Phone',
        emailLabel: 'Email address',
        phoneLabel: 'Israeli phone number',
        emailPlaceholder: 'name@example.com',
        phonePlaceholder: '050 123 4567',
        consentLabel: 'I agree to receive updates from this store by the contact method I provide.',
        submit: 'Join updates',
        pending: 'Saving...',
        successTitle: 'Thank you',
        successBody: 'Your request was received.',
        invalidEmail: 'Enter a valid email address.',
        invalidPhone: 'Enter a valid Israeli phone number.',
        consentRequired: 'Confirm consent to continue.',
        genericError: 'Could not save right now. Try again later.',
      },
      trust: {
        title: 'Customer trust',
        summary: '{{average}} out of 5 from {{count}} verified reviews',
        badge: '{{average}} · {{count}} verified reviews',
      },
      categories: {
        title: 'Shop by category',
        viewAll: 'View all',
        viewCategory: 'View {{categoryName}}',
        productCount: '{{count}} products',
        loadError: 'Could not load categories.',
        imageAlt: '{{categoryName}} category image',
      },
      ads: {
        slideAriaLabel: 'Ad {{index}} of {{total}}',
        openAd: 'Open ad',
        destinationCue: 'View details',
        imageAlt: 'Ad from {{storeName}}',
        previous: 'Previous ad',
        next: 'Next ad',
        goToSlide: 'Show ad {{index}}',
      },
      productImageAlt: '{{productName}} image',
      viewProduct: 'View product',
      loadMore: 'Load more',
      loadingMore: 'Loading\u2026',
      productCard: {
        availability: {
          outOfStock: 'Out of stock',
          onlyNLeft: 'Only {{count}} left',
        },
      },
      empty: {
        title: 'No products yet',
        description: "This store hasn't added any products yet — check back later.",
      },
      errors: {
        loadMoreFailed: "Couldn't load more. Try again.",
        networkFailed: 'Connection failed. Check your network.',
        unknown: 'Something went wrong. Try again.',
        loadFirstFailed: "Couldn't load products right now.",
        loadFirstFailedHint: 'Check your connection and try again.',
      },
      liveRegion: {
        loaded: 'Showing {{count}} loaded products',
        loadedFiltered: 'Showing {{visibleCount}} of {{loadedCount}} loaded products',
      },
      loadedFilter: {
        label: 'Filter loaded products',
        placeholder: 'Product name in loaded products',
        summary: 'Showing {{visibleCount}} of {{loadedCount}} loaded products',
        clear: 'Clear filter',
        hideUnavailable: 'Hide out-of-stock loaded products',
        emptyTitle: 'No loaded products match that name',
        emptyDescription: 'Clear the filter or load another page into the shown products.',
      },
      loading: {
        srLabel: 'Loading store',
        ariaLabel: 'Loading store',
      },
      seo: {
        title: '{{storeName}}',
        fallbackDescription: 'Shop {{storeName}}',
      },
    },
    productCard: {
      fromPrice: 'From {{price}}',
      saleFromPrice: 'Sale from {{price}}',
    },
    categoryCatalog: {
      backToStore: 'Back to store',
      productCount: '{{count}} products',
      search: {
        label: 'Search within {{categoryName}}',
        placeholder: 'Search products in this category',
        clear: 'Clear search',
      },
      sort: {
        label: 'Sort products',
        default: 'Default',
        priceAsc: 'Price: low to high',
        priceDesc: 'Price: high to low',
        newest: 'Newest',
        nameAsc: 'Name A-Z',
      },
      loading: 'Loading category products...',
      empty: {
        title: 'No matching products',
        description: "Try another search or clear it to see this category's products.",
      },
      emptySearch: {
        title: 'No products match {{search}}',
        description: "Try another search or clear it to see this category's products.",
        clear: 'Clear search',
      },
      errors: {
        loadFailed: 'Could not load category products.',
        loadMoreFailed: 'Could not load more category products.',
        networkFailed: 'Could not connect. Try again.',
        unknown: 'Something went wrong.',
      },
      liveRegion: {
        loaded: 'Showing {{visibleCount}} of {{totalCount}} products',
      },
      meta: {
        title: '{{categoryName}} — {{storeName}}',
        description: 'Shop {{categoryName}} products from {{storeName}}.',
      },
    },
    productPlaceholder: {
      title: 'Product page coming soon',
      description:
        'We are building product pages next. Head back to the store to browse what is available.',
      backToStore: 'Back to store',
    },
    productDetail: {
      backToStore: 'Back to {{storeName}}',
      categoryLabel: 'Category',
      descriptionLabel: 'Description',
      contactSeller: 'Contact seller',
      outOfStock: 'Out of stock',
      availability: {
        inStock: 'In stock',
        lowStock: 'Only {{count}} left',
        outOfStock: 'Out of stock',
      },
      variants: {
        chooseOption: 'Choose {{option}}',
        available: 'Available',
        disabled: 'Not available for sale',
        unavailable: 'Unavailable',
        outOfStock: 'Out of stock',
        lowStock: 'Only {{count}} left',
        selectRequired: 'Choose an option before adding to cart',
      },
      quantity: {
        label: 'Quantity',
        decreaseAriaLabel: 'Decrease quantity',
        increaseAriaLabel: 'Increase quantity',
        helperMustBeBetween: 'Enter a whole number between 1 and {{max}}',
        helperExceedsAvailable: 'Only {{available}} available',
        helperOnlyNLeft: 'Only {{count}} left',
        outOfStockReplacement: 'Out of stock',
        maxInCartReplacement: 'Maximum already in your cart',
      },
      gallery: {
        previousImage: 'Previous image',
        nextImage: 'Next image',
        goToImage: 'Go to image {{index}}',
        paginationLabel: 'Image pagination',
        thumbnailLabel: 'Image {{index}} thumbnail',
        noImages: 'No images for this product',
        zoomImage: 'Zoom image {{index}}',
        zoomOverlayLabel: 'Full-size image viewer',
        zoomClose: 'Close',
        zoomCounter: '{{current}} / {{total}}',
        zoomResetZoom: 'Reset zoom',
      },
      seo: {
        title: '{{productName}} — {{storeName}}',
        fallbackDescription: 'Shop {{productName}} from {{storeName}}',
      },
      loading: {
        srLabel: 'Loading product',
      },
      share: {
        button: 'Share',
        copyButton: 'Copy link',
        shareAriaLabel: 'Share {{productName}}',
        copyAriaLabel: 'Copy link to {{productName}}',
        nativeText: '{{productName}} from {{storeName}}',
        toast: {
          sharedTitle: 'Product shared',
          copiedTitle: 'Link copied',
          failedTitle: 'Couldn\u2019t share',
          failedBody: 'We couldn\u2019t share or copy this product link. Try again.',
        },
      },
      reviews: {
        title: 'Customer reviews',
        summary: '{{average}} · {{count}} reviews',
        retry: 'Reload reviews',
        yourReviewTitle: 'Your review',
        editYourReview: 'Edit review',
        editTitle: 'Edit your review',
        saveEdit: 'Save review',
        editSuccess: 'Your review was updated',
        hiddenOwnReview:
          'This review is hidden from shoppers for now. Your changes stay attached to the same verified purchase.',
      },
    },
    cart: {
      addToCart: 'Add to cart',
      adding: 'Adding\u2026',
      outOfStockButton: 'Out of stock',
      maxInCartButton: 'Maximum in cart',
      persistenceError:
        'We could not save this item in your cart on this browser. Check storage or private browsing settings, then try again.',
      successToast: {
        title: 'Added to cart',
        body: '{{productName}} \u00d7 {{quantity}}',
        viewCart: 'View cart',
      },
      errorToast: {
        title: 'Couldn\u2019t add to cart',
      },
      line: {
        variantSummary: '{{summary}}',
      },
      primedBanner: {
        title: 'Picking up where you left off \u2014 add to {{storeName}}',
        dismiss: 'Dismiss',
      },
      postAddPanel: {
        title: '{{productName}} was added to your cart',
        body: 'Your cart now has {{quantity}}.',
        viewCart: 'View cart',
        continueShopping: 'Continue shopping',
        dismiss: 'Dismiss',
      },
      // F-015: cart header icon + badge + dropdown.
      // English CLDR plural rules use only _one (count=1) and _other
      // (everything else). We seed _zero, _two, _few, _many with _other
      // to satisfy the i18n parity test (set-equality with ar.ts / he.ts).
      header: {
        // iconLabel (6 forms)
        iconLabel_zero: 'Cart \u2014 empty',
        iconLabel_one: 'Cart \u2014 1 item',
        iconLabel_two: 'Cart \u2014 {{count}} items', // seeded = _other
        iconLabel_few: 'Cart \u2014 {{count}} items', // seeded = _other
        iconLabel_many: 'Cart \u2014 {{count}} items', // seeded = _other
        iconLabel_other: 'Cart \u2014 {{count}} items',

        // static strings
        dropdownTitle: 'Your cart',
        dropdownAriaLabel: 'Cart preview',
        subtotal: 'Subtotal',
        viewCart: 'View cart',
        emptyTitle: 'Your cart is empty',
        browseProducts: 'Browse products',
        // Security W4: pure translated string — no interpolation allowed.
        loadError: 'Couldn\u2019t load your cart',

        // itemCountSuffix (6 forms)
        itemCountSuffix_zero: 'No items',
        itemCountSuffix_one: '1 item',
        itemCountSuffix_two: '{{count}} items',
        itemCountSuffix_few: '{{count}} items',
        itemCountSuffix_many: '{{count}} items',
        itemCountSuffix_other: '{{count}} items',

        // moreItems (6 forms)
        moreItems_zero: '+0 more',
        moreItems_one: '+1 more',
        moreItems_two: '+{{count}} more',
        moreItems_few: '+{{count}} more',
        moreItems_many: '+{{count}} more',
        moreItems_other: '+{{count}} more',

        // itemsUnavailable (6 forms)
        itemsUnavailable_zero: 'No items unavailable',
        itemsUnavailable_one: '1 item no longer available',
        itemsUnavailable_two: '{{count}} items no longer available',
        itemsUnavailable_few: '{{count}} items no longer available',
        itemsUnavailable_many: '{{count}} items no longer available',
        itemsUnavailable_other: '{{count}} items no longer available',
      },
      // F-016: full cart page strings.
      page: {
        title: 'Your cart',
        subtitle: 'Review your items before checkout',
        breadcrumbBack: 'Back to store',
        empty: {
          title: 'Your cart is empty',
          body: 'You haven\u2019t added anything to your cart yet.',
          browseProducts: 'Browse products',
        },
        line: {
          removeAriaLabel: 'Remove {{productName}} from cart',
          unitPrice: '{{price}} each',
          lineSubtotal: 'Line subtotal',
          priceUpdated: 'Price updated',
          priceWas: 'Was: {{oldPrice}}',
          priceNow: 'Now: {{newPrice}}',
          unavailable: 'Unavailable',
          unknownProduct: 'Product no longer available',
          imageAlt: '{{productName}} image',
        },
        unavailableBanner: {
          title: 'Some items are no longer available',
          body: '{{count}} items in your cart can no longer be purchased. Remove them to continue to order confirmation.',
        },
        handoffRecovery: {
          retrying: 'Moving your cart after sign-in...',
          partial:
            'We could not move every cart item. Review your cart, then try confirming the order again.',
          savedIntentTitle: 'Some saved items need review',
          savedIntentBody:
            'These items are still saved on this browser and have not moved into your account cart. Remove anything you no longer want, then try confirming the order again.',
          retrySavedItems: 'Try moving saved items again',
        },
        summary: {
          title: 'Order summary',
          subtotal: 'Subtotal',
          itemCount_zero: 'No items',
          itemCount_one: '1 item',
          itemCount_two: '{{count}} items', // seeded = _other (English CLDR has only _one + _other)
          itemCount_few: '{{count}} items', // seeded = _other
          itemCount_many: '{{count}} items', // seeded = _other
          itemCount_other: '{{count}} items',
          stockCheckNote:
            'Items stay in your cart without holding stock. Availability is confirmed when you submit the order.',
          proceedToCheckout: 'Continue to confirm order',
          checkoutDisabledHint: 'Remove unavailable items to continue',
          checkoutDisabledHandoffHint: 'Review or remove saved cart items before continuing',
          signinRequiredDialog: {
            title: 'Verify your phone to confirm the order',
            body: 'We will send one code, then bring you back to confirm the order. Your cart stays saved here.',
            continue: 'Continue with phone',
            cancel: 'Stay in cart',
          },
        },
        removeDialog: {
          title: 'Remove {{productName}}?',
          body: '{{productName}} will be removed from your cart.',
          confirm: 'Remove',
          cancel: 'Cancel',
        },
        errors: {
          refetchFailed: 'Couldn\u2019t load your cart. Please try again.',
          updateFailed: 'Couldn\u2019t update your cart',
          removeFailed: 'Couldn\u2019t remove the item from your cart',
        },
        toast: {
          removed: 'Removed {{productName}} from your cart',
        },
        skeleton: {
          loadingAriaLabel: 'Loading your cart',
        },
        retryButton: 'Try again',
      },
    },
    // F-017: real checkout flow.
    checkout: {
      review: {
        cartChanged: 'Your cart items changed. Review the updated cart, then confirm again.',
        cartRefreshFailed: 'Could not refresh your cart. Try again before confirming the order.',
        verificationFailed: 'Could not verify your cart items. Try again before confirming.',
      },
      page: {
        title: 'Checkout',
        backToCart: 'Back to cart',
        shippingSection: 'Shipping address',
        changeAddress: 'Change address',
        useDifferentAddress: 'Use a different address',
        manageAddresses: 'Manage saved addresses',
        saveForNextTime: 'Save this address for next time',
        localityPlaceholder: 'Choose city or village',
        localityLoading: 'Loading cities...',
        localityError: 'Could not load cities. Try again.',
        notesSection: 'Notes for seller',
        submitCta: 'Submit order',
        deliveryPriceNoticeTitle: 'Delivery price will be confirmed separately',
        deliveryPriceNotice:
          'Delivery price is not included yet. The store will contact you to confirm the delivery cost before sending your order.',
      },
      deliveryMethod: {
        title: 'Delivery method',
        location: 'Location',
        address: 'Address',
      },
      deliveryInfo: {
        title: 'Delivery information',
        description: 'Tell the store where to deliver. Precise location is optional.',
        localityLabel: 'City or village',
        postalCodeLabel: 'Postal code',
        recipientLabel: 'Recipient name',
        backupPhoneLabel: 'Backup phone',
        errors: {
          localityRequired: 'Choose a city or village.',
          postalCodeRequired: 'Enter a postal code.',
          postalCodeInvalid: 'Enter a valid Israeli postal code.',
          recipientRequired: 'Enter the recipient name.',
          recipientTooLong: 'Recipient name is too long.',
          backupPhoneInvalid: 'Enter a valid backup phone number.',
        },
      },
      deliveryLocation: {
        title: 'Precise delivery location',
        description: 'Share your current location so the merchant can deliver to the exact point.',
        localityLabel: 'City or village',
        localityRequired: 'Choose a city or village before sharing your location.',
        homeWarning:
          'Use this option only when you are at home or at the location where you usually receive deliveries.',
        cta: 'Share my precise location',
        update: 'Update precise location',
        locating: 'Finding location...',
        shared: 'Precise location added for delivery',
        clear: 'Remove location',
        denied: 'Location permission was denied. You can still complete checkout without GPS.',
        deniedWithExisting:
          'Location permission was denied. Your previously added precise location is still attached to this order.',
        unavailable:
          'Precise location is unavailable on this device. You can still complete checkout without GPS.',
        unavailableWithExisting:
          'Precise location is unavailable on this device. Your previously added location is still attached to this order.',
      },
      locationModal: {
        title: 'Share precise location?',
        body: 'Your browser will ask for permission. We will add these coordinates only to this order.',
        confirmCta: 'Share location',
        cancelCta: 'Cancel',
      },
      modal: {
        title: 'Confirm order',
        body: 'You are about to submit an order to {{storeName}}.',
        itemCount_one: '1 item',
        itemCount_other: '{{count}} items',
        subtotal: 'Subtotal',
        confirmCta: 'Confirm',
        cancelCta: 'Cancel',
      },
    },
  },
  // F-017: order domain namespace.
  order: {
    submit: 'Submit order',
    confirm: 'Confirm order',
    cancel: 'Cancel order',
    placed: 'Order placed',
    tracking: 'Track order',
    shippingAddress: 'Shipping address',
    deliveryInformation: 'Delivery information',
    preciseLocation: 'Precise delivery location',
    orderSummary: 'Order summary',
    stockCheckNotice:
      'Stock is reserved when you submit the order. The seller will contact you to arrange delivery.',
    notes: {
      placeholder: 'Add notes for the seller (optional)',
      maxLengthHint: '{{current}}/{{max}}',
    },
    status: {
      ordered: 'Ordered',
      shipped: 'Shipped',
      delivered: 'Delivered',
      cancelled: 'Cancelled',
      rejected: 'Rejected',
    },
    // F-018: order confirmation page keys
    headline: {
      placed: 'Order placed!',
      ordered: 'Order received!',
      shipped: 'Order shipped!',
      delivered: 'Order delivered!',
      cancelled: 'Order cancelled',
      rejected: 'Order rejected',
    },
    whatHappensNext: 'What happens next?',
    whatHappensNextBody:
      'The merchant will review your order and contact you via WhatsApp or phone to confirm delivery and arrange payment. You can track the status of this order here any time.',
    contactMerchant: 'Contact merchant',
    contactMerchantWhatsappMessage:
      "Hello {{storeName}}, I'd like to ask about my order {{orderId}}",
    contactMerchantWhatsappAriaLabel: 'Message {{storeName}} on WhatsApp about this order',
    contactMerchantPhoneAriaLabel: 'Call {{storeName}} about this order',
    id: 'Order #',
    placedDate: 'Placed on',
    cancelButton: 'Cancel order',
    cancelQuestion: 'Changed your mind?',
    cancelConfirmTitle: 'Cancel this order?',
    cancelConfirmBody: 'This will cancel the order and restore the items to the store inventory.',
    cancelConfirmCta: 'Yes, cancel order',
    cancelSuccess: 'Your order has been cancelled',
    timeline: {
      title: 'Order status',
      completedOn: 'Completed on {{- date}}',
    },
    viewAllOrders: 'View all my orders',
    backToStore: 'Back to store',
    customerNotes: 'Your notes to the merchant',
    loadingDetails: 'Loading order details',
    reviewPrompt: {
      title: 'Reviews for this order',
      submit: 'Submit review',
      alreadyReviewed: 'Already reviewed',
      ratingRequired: 'Choose a rating from 1 to 5',
      textTooLong: 'Review text must be 1000 characters or fewer',
      whitespaceOnly: 'Write clear text or leave the field empty',
      success: 'Your review was submitted',
      openDialog: 'Write a review',
      ratingOnlyHint: 'You can submit a star rating only or add a short note.',
      reviewedHint: 'Your submitted reviews stay attached to the delivered products.',
      yourReview: 'Your review',
      editReview: 'Edit review',
      hiddenReview: 'Hidden from shoppers for now. Your edit keeps the verified purchase link.',
      merchantResponse: 'Merchant response',
      textLabel: 'Review text',
      textPlaceholder: 'Write what you thought about the product',
      dialogTitle: 'Review {{productName}}',
      close: 'Close',
    },
    errors: {
      productUnavailable: 'Some products are no longer available: {{names}}',
      variantUnavailable: 'One option is no longer available. Review your cart.',
      cartEmpty: 'Your cart is empty. Add products before checking out.',
      storeUnavailable: 'This store is not accepting orders at this time.',
      networkFailed: 'Connection failed. Check your network and try again.',
      unknown: 'Something went wrong. Please try again.',
      // F-018: cancel-specific error keys
      cancelFailed: 'Could not cancel order. Try again.',
      cancelAlreadyProcessed: 'This order can no longer be cancelled.',
      orderNotFound: 'Order not found.',
      // F-023: merchant transition error keys
      invalidTransition: 'This action cannot be performed in the current order status.',
      transitionUnauthorized: "You do not have permission to change this order's status.",
      shipFailed: 'Inventory update failed during shipment. Try again.',
      deliverFailed: 'Failed to mark the order as delivered. Try again.',
      courierProofRequired:
        'The courier must submit a delivery proof photo from the delivery link.',
      manualApprovalRequired: 'Approve this order before fulfillment actions.',
      rejectFailed: 'Failed to reject the order. Try again.',
    },
    history: {
      title: 'My Orders',
      totalOrders: '{{count}} orders',
      activeOrders: '{{count}} active',
      filterLabel: 'Filter by status',
      filterAll: 'All',
      itemCount: '{{count}} items',
      viewOrder: 'View',
      loadMore: 'Load more',
      loadingMore: 'Loading more...',
      loadingOrders: 'Loading orders',
      backToAccount: 'Account',
      emptyAllCta: 'Browse stores',
      emptyAllSubtitle: 'Browse stores and start shopping',
      emptyFilteredHint: 'Try a different filter or check back later',
      empty: {
        all: "You haven't placed any orders yet",
        ordered: 'No orders awaiting shipment',
        shipped: 'No shipped orders',
        delivered: 'No delivered orders',
        cancelled: 'No cancelled orders',
        rejected: 'No rejected orders',
      },
    },
    // F-020: status-change toast key
    statusChangedToast: 'Your order is now {{status}}',
  },
  promotionKit: {
    whatsapp: {
      storeHome: 'Visit {{storeName}}: {{url}}',
      product: 'See {{targetName}} from {{storeName}}: {{url}}',
      category: 'Browse {{targetName}} from {{storeName}}: {{url}}',
      liveOfferCampaign: 'Live offer from {{storeName}}: {{targetName}} {{url}}',
      salesPage: '{{storeName}} sales are waiting: {{url}}',
      reviewHighlight: 'Customers love {{targetName}} from {{storeName}}. See more: {{url}}',
    },
    poster: { cta: 'Scan and order from the store' },
    story: { cta: 'Order from the store' },
  },
  errors: {
    notFound: 'Page not found',
    networkFailed: 'Network request failed',
    title: 'Something went wrong',
    description: 'We are sorry for the inconvenience. You can try again.',
    retry: 'Try again',
  },
};
